/**
 * Unloaded compatibility reference for the pre-split storefront runtime.
 * The live theme loads main-core.js and conditionally loads main-product.js.
 * Keep behavior fixes mirrored here until this compatibility copy is retired.
 */
const SESSION_COUNTDOWN_STORAGE_PREFIX="exa:session-countdown:v1",
sessionCountdownEngine=(()=>{
  const registrations=new Map;
  let ticker=null,
  nextRegistrationId=0;
  function readStoredNumber(key)
  {
    try{
      const value=Number(window.sessionStorage.getItem(key));
      return Number.isSafeInteger(value)&&value>0?value:null
    }
    catch{
      return null
    }
  }
  function writeStoredNumber(key, value)
  {
    try{
      window.sessionStorage.setItem(key, String(value))
    }
    catch{
    }
  }
  const sessionStartedAtKey=SESSION_COUNTDOWN_STORAGE_PREFIX+":started-at";
  let sessionStartedAt=readStoredNumber(sessionStartedAtKey);
  if(sessionStartedAt===null)
  {
    sessionStartedAt=Date.now(),
    writeStoredNumber(sessionStartedAtKey, sessionStartedAt)
  }
  function getDeadline(key, durationSeconds)
  {
    const storageKey=SESSION_COUNTDOWN_STORAGE_PREFIX+":deadline:"+encodeURIComponent(key);
    let deadline=readStoredNumber(storageKey);
    if(deadline===null)
    {
      const duration=Number(durationSeconds);
      const maximumDurationSeconds=Math.max(0, Math.floor((Number.MAX_SAFE_INTEGER-sessionStartedAt)/1000)),
      requestedDurationSeconds=Number.isFinite(duration)?duration>0?Math.max(1, Math.floor(duration)):0:duration>0?maximumDurationSeconds:0,
      safeDurationSeconds=Math.min(Number.isSafeInteger(requestedDurationSeconds)?requestedDurationSeconds:maximumDurationSeconds, maximumDurationSeconds);
      deadline=sessionStartedAt+safeDurationSeconds*1000,
      writeStoredNumber(storageKey, deadline)
    }
    return deadline
  }
  function stopTickerIfIdle()
  {
    if(registrations.size===0&&ticker!==null)
    {
      clearInterval(ticker),
      ticker=null
    }
  }
  function updateRegistration(registration, now)
  {
    const remainingSeconds=Math.max(0, Math.ceil((registration.deadline-now)/1000));
    if(remainingSeconds!==registration.remainingSeconds)
    {
      registration.remainingSeconds=remainingSeconds,
      registration.onTick(remainingSeconds, registration.deadline)
    }
    if(now>=registration.deadline)
    {
      registrations.delete(registration.id),
      registration.onExpire(registration.deadline)
    }
  }
  function tick()
  {
    const now=Date.now();
    Array.from(registrations.values()).forEach(registration=>updateRegistration(registration, now)),
    stopTickerIfIdle()
  }
  return{
    register(key, durationSeconds, onTick, onExpire)
    {
      const registration={
        id:++nextRegistrationId,
        deadline:getDeadline(key, durationSeconds),
        remainingSeconds:null,
        onTick:typeof onTick==="function"?onTick:()=>{},
        onExpire:typeof onExpire==="function"?onExpire:()=>{}
      };
      registrations.set(registration.id, registration),
      updateRegistration(registration, Date.now());
      if(registrations.size>0&&ticker===null)ticker=setInterval(tick, 1000);
      return registrations.has(registration.id)?registration.id:null
    },
    unregister(registrationId)
    {
      registrations.delete(registrationId),
      stopTickerIfIdle()
    }
  }
})();
window.sessionCountdownEngine=sessionCountdownEngine;
class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    const duration=Number(this.dataset.duration),
    maximumSafeDurationSeconds=Math.max(0, Math.floor((Number.MAX_SAFE_INTEGER-Date.now())/1000)),
    requestedDurationSeconds=Number.isFinite(duration)?duration>0?Math.max(1, Math.floor(duration)):0:duration>0?maximumSafeDurationSeconds:0;
    this.duration=Math.min(Number.isSafeInteger(requestedDurationSeconds)?requestedDurationSeconds:maximumSafeDurationSeconds, maximumSafeDurationSeconds),
    this.countdownKey=this.dataset.countdownKey||"cart:"+this.duration,
    this.registrationId=null,
    this.isPlaying=!1,
    this.initTimer(),
    this.updateTimer(this.duration)
  }
  connectedCallback() { this.playTimer() }
  disconnectedCallback() { this.pauseTimer() }
  initTimer() {
    this.innerHTML="",
    this.minutesSpan=document.createElement("span");
    const separator=document.createTextNode(":");
    this.secondsSpan=document.createElement("span"),
    this.append(this.minutesSpan, separator, this.secondsSpan)
  }
  updateTimer(remainingSeconds=this.duration) {
    const remaining=Math.max(0, Math.floor(remainingSeconds));
    this.minutesSpan.textContent=this.formatNumber(Math.floor(remaining/60)),
    this.secondsSpan.textContent=this.formatNumber(remaining%60)
  }
  playTimer() {
    if(this.isPlaying)return;
    this.isPlaying=!0,
    this.registrationId=null;
    const engine=window.sessionCountdownEngine;
    if(!engine||typeof engine.register!=="function") {
      this.isPlaying=!1;
      return
    }
    const registrationId=engine.register(this.countdownKey, this.duration, remaining=>this.updateTimer(remaining), ()=>this.expire());
    if(this.isPlaying&&registrationId!==null)this.registrationId=registrationId;
    else if(registrationId!==null)engine.unregister(registrationId)
  }
  pauseTimer() {
    const engine=window.sessionCountdownEngine;
    this.registrationId!==null&&engine&&typeof engine.unregister==="function"&&engine.unregister(this.registrationId),
    this.registrationId=null,
    this.isPlaying=!1
  }
  expire() {
    this.updateTimer(0),
    this.pauseTimer();
    const timerBlock=this.closest(".cart-timer")||this;
    timerBlock.style.display="none"
  }
  formatNumber(t) { return 1===t.toString().length?"0"+t:t }
}
customElements.define("countdown-timer", CountdownTimer);
class DateCountdownTimer extends HTMLElement {
  connectedCallback() {
    if(this.update=this.update.bind(this), this.wrapper=this.closest(".countdown-wrapper"), !this.wrapper)return;
    this.mode=this.wrapper.dataset.countdownMode||"fixed_date",
    this.behavior=this.wrapper.dataset.endBehavior,
    this.repeatHours=parseInt(this.wrapper.dataset.repeatHours)||24,
    this.labels={days:this.wrapper.dataset.labelDays,hours:this.wrapper.dataset.labelHours,minutes:this.wrapper.dataset.labelMinutes,seconds:this.wrapper.dataset.labelSeconds},
    this.units={days:"true"===this.wrapper.dataset.showDays,hours:"true"===this.wrapper.dataset.showHours,minutes:"true"===this.wrapper.dataset.showMinutes,seconds:"true"===this.wrapper.dataset.showSeconds},
    this.timerStyle=this.dataset.style;
    if(this.mode==="rolling") {
      this.startRolling();
      return
    }
    const endTime=(this.wrapper.dataset.endTime||"").trim(),
    timezone=this.wrapper.dataset.timezone,
    utcOffset=parseInt(this.wrapper.dataset.utcOffset)||0;
    if(this.endTime=this.parseEndTime(endTime, timezone, utcOffset), !(this.endTime instanceof Date)||isNaN(this.endTime.getTime())) {
      this.hideCountdown();
      return
    }
    this.timer=setInterval(this.update, 1e3),
    this.update()
  }
  startRolling() {
    const configuredMinutes=Number(this.wrapper.dataset.rollingDurationMinutes),
    durationMinutes=Number.isFinite(configuredMinutes)&&configuredMinutes>0?configuredMinutes:10,
    maximumSafeDurationSeconds=Math.max(1, Math.floor((Number.MAX_SAFE_INTEGER-Date.now())/1000)),
    requestedDurationSeconds=durationMinutes*60,
    durationSeconds=Math.min(Number.isFinite(requestedDurationSeconds)?Math.max(1, Math.ceil(requestedDurationSeconds)):maximumSafeDurationSeconds, maximumSafeDurationSeconds),
    countdownKey=this.wrapper.dataset.countdownKey||"date:"+durationSeconds,
    engine=window.sessionCountdownEngine;
    this.rollingActive=!0,
    this.rollingRegistrationId=null;
    if(!engine||typeof engine.register!=="function") {
      this.rollingActive=!1;
      return
    }
    const registrationId=engine.register(countdownKey, durationSeconds, remaining=>this.renderTime(remaining), ()=>this.expireRolling());
    if(this.rollingActive&&registrationId!==null)this.rollingRegistrationId=registrationId;
    else if(registrationId!==null)engine.unregister(registrationId)
  }
  stopRolling() {
    const engine=window.sessionCountdownEngine;
    this.rollingRegistrationId!==null&&engine&&typeof engine.unregister==="function"&&engine.unregister(this.rollingRegistrationId),
    this.rollingRegistrationId=null,
    this.rollingActive=!1
  }
  expireRolling() {
    this.renderTime(0),
    this.stopRolling(),
    this.hideCountdown(),
    this.hideOwnedAnnouncementSlide()
  }
  hideCountdown() {
    this.wrapper.style.display="none"
  }
  hideOwnedAnnouncementSlide() {
    if(this.wrapper.dataset.countdownAnnouncementSlide!=="true")return;
    const announcementItem=this.wrapper.parentElement,
    slideContainer=announcementItem&&announcementItem.parentElement,
    slide=slideContainer&&slideContainer.parentElement;
    if(announcementItem&&announcementItem.classList.contains("announcement-bar__item")&&announcementItem.children.length===1&&announcementItem.firstElementChild===this.wrapper&&slideContainer&&slideContainer.classList.contains("splide__slide__container")&&slideContainer.children.length===1&&slideContainer.firstElementChild===announcementItem&&slide&&slide.classList.contains("splide__slide")&&slide.children.length===1&&slide.firstElementChild===slideContainer)slide.style.display="none"
  }
  parseEndTime(t, e, i) { let s=t.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/); if(!s) { let n=new Date(t.replace(/-/g, "/")); return n } let r=parseInt(s[1], 10),o=parseInt(s[2], 10)-1,a=parseInt(s[3], 10),l=parseInt(s[4], 10),d=parseInt(s[5], 10),c=parseInt(s[6]||"0", 10); if("fixed"!==e)return new Date(r, o, a, l, d, c); { let h=Date.UTC(r, o, a, l, d, c)-36e5*i; return new Date(h) } }
  update() { let t=new Date,e=Math.floor((this.endTime-t)/1e3); if(!isFinite(e))return; if(e<=0) { if("hide"===this.behavior) { this.hideCountdown(),clearInterval(this.timer); return } if("restart"===this.behavior) { let i=new Date(this.endTime),s=36e5*this.repeatHours; for(;i<=t;)i=new Date(i.getTime()+s); this.endTime=i,e=Math.floor((this.endTime-t)/1e3) } else if("next_day"===this.behavior) { let n=new Date(this.endTime); for(;n<=t;)n.setDate(n.getDate()+1); this.endTime=n,e=Math.floor((this.endTime-t)/1e3) } } this.renderTime(e) }
  renderTime(remainingSeconds) { let r=Math.floor(remainingSeconds/86400),o=Math.floor(remainingSeconds%86400/3600),a=Math.floor(remainingSeconds%3600/60),l=remainingSeconds%60,d={days:r,hours:o,minutes:a,seconds:l}; Object.keys(this.units).forEach(t=>{if(!this.units[t])return;let e=this.querySelector(`[aria-label="${"days"===t?"Day":"hours"===t?"Hour":"minutes"===t?"Minute":"Second"}"]`);if(!e)return;let i=e.querySelector(".countdown-timer__number");if(!i)return;let s=String(d[t]).padStart(2,"0");"4"===this.timerStyle?(i.innerHTML="",s.split("").forEach(t=>{let e=document.createElement("span");e.className="countdown-timer__digit",e.textContent=t,i.appendChild(e)})):i.textContent=s,i.setAttribute("aria-label",`${d[t]} ${this.labels[t]}`)}) }
  disconnectedCallback() { clearInterval(this.timer),this.timer=null; if(this.mode==="rolling")this.stopRolling() }
}
customElements.define("date-countdown-timer", DateCountdownTimer);
const ON_CHANGE_DEBOUNCE_TIMER=300,
currentDate=new Date,
PUB_SUB_EVENTS={
  cartUpdate:"cart-update",
  quantityUpdate:"quantity-update",
  optionValueSelectionChange:"option-value-selection-change",
  variantChange:"variant-change",
  cartError:"cart-error"
};
let subscribers={
};
function subscribe(id_0001, id_0002)
{
  return subscribers[id_0001]===void 0&&(subscribers[id_0001]=[]),
  subscribers[id_0001]=[...subscribers[id_0001],
  id_0002],
  function()
  {
    subscribers[id_0001]=subscribers[id_0001]["filter"](id_0003=>id_0003!==id_0002)
  }
}
function publish(id_0004, id_0005)
{
  subscribers[id_0004]&&subscribers[id_0004].forEach(id_0006=>{
    id_0006(id_0005)
  })
}
function parseSectionElement(sectionHTML, selector)
{
  const element=new DOMParser().parseFromString(sectionHTML, "text/html").querySelector(selector);
  if(!element)throw new Error("Cart section is missing "+selector);
  return element
}
class CartUpdateCoordinator
{
  constructor()
  {
    this.abortController=null,
    this.refreshPromise=null
  }
  abortActiveRequest()
  {
    this.abortController&&this.abortController.abort()
  }
  isAbortError(error)
  {
    return error&&error.name==="AbortError"
  }
  async request(url, options, parser, operation)
  {
    this.abortActiveRequest();
    const controller=new AbortController;
    this.abortController=controller;
    try{
      const response=await fetch(url, {
        ...options,
        signal:controller.signal
      }),
      result=await parser(response);
      if(!response.ok)
      {
        const message=result&&typeof result==="object"&&(result.description||result.message);
        throw new Error(message||operation+" failed ("+response.status+")")
      }
      return result
    }
    finally{
      this.abortController===controller&&(this.abortController=null)
    }
  }
  fetchJSON(url, options={}, operation="Cart update")
  {
    return this.request(url, options, async response=>{
      const responseText=await response.text();
      if(!responseText)return{};
      try{
        return JSON.parse(responseText)
      }
      catch{
        if(!response.ok)return{};
        throw new Error("Invalid "+operation.toLowerCase()+" response ("+response.status+")")
      }
    }, operation)
  }
  fetchText(url, options={}, operation="Cart refresh")
  {
    return this.request(url, options, response=>response.text(), operation)
  }
  refreshMainCartItems()
  {
    if(this.refreshPromise)return this.refreshPromise;
    this.refreshPromise=this.fetchText("/cart?section_id=main-cart-items", {}, "Cart refresh").then(sectionHTML=>{
      const freshCartItems=parseSectionElement(sectionHTML, "cart-items");
      document.querySelectorAll("cart-items").forEach(cartItems=>{
        cartItems.innerHTML=freshCartItems.innerHTML
      })
    }).catch(error=>{
      this.isAbortError(error)||console.error(error)
    }).finally(()=>{
      this.refreshPromise=null
    });
    return this.refreshPromise
  }
}
const cartUpdateCoordinator=new CartUpdateCoordinator;
function getVisibleCartItemCount(id_0007=document)
{
  let id_0008=0;
  return id_0007&&id_0007.querySelectorAll&&id_0007.querySelectorAll(".cart-item").forEach(id_0009=>{
    if(id_0009.classList.contains("cart-item--hidden"))return;
    id_0008+=Number(id_0009.dataset.quantity||1)||1
  }),
  id_0008
}
function syncCartDrawerVisibleEmptyState(id_0010=document.querySelector("cart-drawer"))
{
  if(!id_0010)return 0;
  const id_0011=getVisibleCartItemCount(id_0010),
  id_0012=id_0011<=0,
  id_0013=id_0010.querySelector("cart-drawer-items"),
  id_0014=document.getElementById("main-cart-footer");
  id_0010.classList.toggle("is-empty", id_0012),
  id_0013&&id_0013.classList.toggle("is-empty", id_0012),
  id_0014&&id_0014.classList.toggle("is-empty", id_0012);
  return id_0011
}
function hiddenUpsellDebugEnabled()
{
  try{
    return localStorage.getItem("hiddenUpsellDebug")==="true"
  }
  catch{
    return !1
  }
}
function hiddenUpsellDebugLog(id_0014a, id_0014b={})
{
  if(!hiddenUpsellDebugEnabled())return;
  const id_0014c={
    step:id_0014a, timestamp:new Date().toISOString(), ...id_0014b
  };
  console.log("[hidden-upsell]", id_0014c);
  try{
    const id_0014d=JSON.parse(localStorage.getItem("hiddenUpsellDebugLog")||"[]");
    id_0014d.push(id_0014c),
    localStorage.setItem("hiddenUpsellDebugLog", JSON.stringify(id_0014d.slice(-50)))
  }
  catch{}
}
async function getHiddenUpsellCartSnapshot()
{
  const id_0014e=buildAjaxCartUrl(routes.cart_url, "/cart"),
  id_0014f=await fetch(id_0014e, {
    headers:{
      Accept:"application/json"
    }
  });
  if(!id_0014f.ok)throw new Error("Cart snapshot failed ("+id_0014f.status+")");
  return id_0014f.json()
}
function hiddenUpsellDelay(id_0014g)
{
  return new Promise(id_0014h=>setTimeout(id_0014h, id_0014g))
}
function getMissingHiddenCartUpsellItems(id_0014i, id_0014j)
{
  const id_0014k=new Set;
  id_0014j&&id_0014j.items&&id_0014j.items.forEach(id_0014l=>{
    const id_0014m=String(id_0014l.variant_id||id_0014l.id||"");
    id_0014m&&id_0014k.add(id_0014m)
  });
  return id_0014i.filter(id_0014n=>!id_0014k.has(String(id_0014n.id)))
}
async function confirmHiddenCartUpsells(id_0014o)
{
  let id_0014p=null,
  id_0014q=id_0014o;
  for(let id_0014r=0; id_0014r<5; id_0014r++)
  {
    id_0014p=await getHiddenUpsellCartSnapshot(),
    id_0014q=getMissingHiddenCartUpsellItems(id_0014o, id_0014p),
    hiddenUpsellDebugLog("confirm-cart", {
      attempt:id_0014r+1,
      missingItems:id_0014q,
      itemCount:id_0014p.item_count
    });
    if(!id_0014q.length)return{
      ok:!0, cart:id_0014p, missingItems:[]
    };
    await hiddenUpsellDelay(150*(id_0014r+1))
  }
  return{
    ok:!1, cart:id_0014p, missingItems:id_0014q
  }
}
function getHiddenCartUpsellItems(id_0018a=document)
{
  const id_0015=new Set;
  document.querySelectorAll(".cart-item[data-variant-id]").forEach(id_0016=>{
    id_0016.dataset.variantId&&id_0015.add(String(id_0016.dataset.variantId))
  });
  id_0018a!==document&&id_0018a.querySelectorAll&&id_0018a.querySelectorAll(".cart-item[data-variant-id]").forEach(id_0016=>{
    id_0016.dataset.variantId&&id_0015.add(String(id_0016.dataset.variantId))
  });
  const id_0017=[];
  id_0018a&&id_0018a.querySelectorAll&&id_0018a.querySelectorAll("[data-hidden-upsell-item][data-variant-id]").forEach(id_0018=>{
    const id_0019=String(id_0018.dataset.variantId||"");
    if(!id_0019||id_0015.has(id_0019))return;
    id_0015.add(id_0019);
    const id_0020={
      id:id_0019,
      quantity:1,
      properties:{
        _cart_hidden:"true"
      }
    };
    id_0018.dataset.sellingPlanId&&(id_0020.selling_plan=id_0018.dataset.sellingPlanId),
    id_0017.push(id_0020)
  });
  hiddenUpsellDebugLog("metadata-read", {
    source:id_0018a===document?"document":"fresh-section",
    existingVariantIds:[...id_0015],
    candidateItems:id_0017
  });
  return id_0017
}
async function getFreshHiddenCartUpsellItems()
{
  const id_0018a=routes.cart_url||"/cart",
  id_0018b=id_0018a+(id_0018a.includes("?")?"&":"?")+"sections=cart-drawer&sections_url="+encodeURIComponent(window.location.pathname),
  id_0018c=await fetch(id_0018b, {
    headers:{
      Accept:"application/json"
    }
  });
  if(!id_0018c.ok)throw new Error("Hidden upsell refresh failed ("+id_0018c.status+")");
  const id_0018d=await id_0018c.json(),
  id_0018e=id_0018d["cart-drawer"];
  if(!id_0018e)return[];
  const id_0018f=new DOMParser().parseFromString(id_0018e, "text/html");
  hiddenUpsellDebugLog("fresh-section-read", {
    hasCartDrawerSection:!!id_0018e,
    hiddenMetadataCount:id_0018f.querySelectorAll("[data-hidden-upsell-item][data-variant-id]").length
  });
  return getHiddenCartUpsellItems(id_0018f)
}
async function addHiddenCartUpsells(id_0021)
{
  if(!id_0021.length)return;
  const id_0022=buildAjaxCartUrl(routes.cart_add_url, "/cart/add"),
  id_0021a={
    ...fetchConfig(), credentials:"same-origin", body:JSON.stringify({
      items:id_0021
    })
  },
  id_0023=await fetch(id_0022, id_0021a);
  hiddenUpsellDebugLog("batch-add-response", {
    status:id_0023.status,
    ok:id_0023.ok,
    items:id_0021
  });
  if(id_0023.ok)return;
  let id_0024={},
  id_0024a="";
  try{
    id_0024a=await id_0023.text(),
    id_0024=id_0024a?JSON.parse(id_0024a):{}
  }
  catch{}
  hiddenUpsellDebugLog("batch-add-error-body", {
    status:id_0023.status,
    body:id_0024a,
    parsed:id_0024
  });
  const id_0025a=new Error(id_0024.description||id_0024.message||"Hidden upsell add failed ("+id_0023.status+")");
  let id_0025b=0;
  for(const id_0025c of id_0021)
  {
    try{
      const id_0025d=await fetch(id_0022, {
        ...fetchConfig(), credentials:"same-origin", body:JSON.stringify({
          items:[id_0025c]
        })
      });
      hiddenUpsellDebugLog("single-add-response", {
        status:id_0025d.status,
        ok:id_0025d.ok,
        item:id_0025c
      });
      if(id_0025d.ok)id_0025b+=1;
      else{
        let id_0025f="";
        try{id_0025f=await id_0025d.text()}catch{}
        hiddenUpsellDebugLog("single-add-error-body", {
          status:id_0025d.status,
          item:id_0025c,
          body:id_0025f
        })
      }
    }
    catch(id_0025e){
      console.error(id_0025e)
    }
  }
  if(id_0025b===0)throw id_0025a
}
window.handleHiddenCartUpsellsCheckout=function(id_0025, id_0026)
{
  id_0025&&id_0025.preventDefault&&id_0025.preventDefault(),
  id_0026&&(id_0026.disabled=!0),
  (async()=>{
    hiddenUpsellDebugLog("checkout-click", {
      path:window.location.pathname,
      domMetadataCount:document.querySelectorAll("[data-hidden-upsell-item][data-variant-id]").length
    });
    let id_0027=getHiddenCartUpsellItems();
    if(!id_0027.length)id_0027=await getFreshHiddenCartUpsellItems();
    hiddenUpsellDebugLog("items-before-add", {
      items:id_0027
    });
    if(id_0027.length)
    {
      await addHiddenCartUpsells(id_0027);
      let id_0027a=await confirmHiddenCartUpsells(id_0027);
      if(!id_0027a.ok&&id_0027a.missingItems.length)
      {
        hiddenUpsellDebugLog("retry-missing-items", {
          items:id_0027a.missingItems
        }),
        await addHiddenCartUpsells(id_0027a.missingItems),
        id_0027a=await confirmHiddenCartUpsells(id_0027)
      }
      hiddenUpsellDebugLog("cart-after-add", {
        confirmed:id_0027a.ok,
        itemCount:id_0027a.cart&&id_0027a.cart.item_count,
        items:id_0027a.cart&&id_0027a.cart.items&&id_0027a.cart.items.map(id_0027b=>({
          id:id_0027b.id,
          variant_id:id_0027b.variant_id,
          product_id:id_0027b.product_id,
          handle:id_0027b.handle,
          quantity:id_0027b.quantity,
          properties:id_0027b.properties
        }))
      }),
      await hiddenUpsellDelay(150)
    }
  })().catch(id_0028=>{
    console.error(id_0028),
    hiddenUpsellDebugLog("error", {
      message:id_0028&&id_0028.message?id_0028.message:String(id_0028)
    })
  }).finally(()=>{
    if(hiddenUpsellDebugEnabled()&&localStorage.getItem("hiddenUpsellDebugPause")==="true")
    {
      id_0026&&(id_0026.disabled=!1, id_0026.classList.remove("loading"));
      const id_0028a=id_0026&&id_0026.querySelector(".loading-overlay__spinner");
      id_0028a&&id_0028a.classList.add("hidden");
      hiddenUpsellDebugLog("redirect-paused");
      return
    }
    window.location="/checkout"
  });
  return !0
};
window.checkoutLoad=function(id_0028b)
{
  const id_0028c=id_0028b&&id_0028b.currentTarget,
  id_0028d=id_0028c&&id_0028c.closest("cart-drawer"),
  id_0028e=id_0028d&&id_0028d.querySelector("tnc-chekcbox[id^=\"TNC-Checkbox-\"]")||document.querySelector("tnc-chekcbox[id^=\"TNC-Checkbox-\"]"),
  id_0028ea=id_0028e&&id_0028e.querySelector('input[type="checkbox"]');
  if(id_0028e&&(id_0028ea?!id_0028ea.checked:id_0028e.dataset.checked!="true"))return;
  if(!id_0028c)return;
  id_0028c.classList.add("loading");
  const id_0028f=id_0028c.querySelector(".loading-overlay__spinner");
  id_0028f&&id_0028f.classList.remove("hidden");
  if(window.handleHiddenCartUpsellsCheckout&&window.handleHiddenCartUpsellsCheckout(id_0028b, id_0028c))return;
  setTimeout(()=>{
    if(document.location.pathname.includes("/checkout"))return;
    id_0028c.classList.remove("loading"),
    id_0028c.disabled=!1,
    id_0028f&&id_0028f.classList.add("hidden")
  }, 8e3)
};
window.hiddenUpsellDebug={
  enable:function(id_0029=!0)
  {
    localStorage.setItem("hiddenUpsellDebug", "true"),
    localStorage.setItem("hiddenUpsellDebugPause", id_0029?"true":"false"),
    localStorage.removeItem("hiddenUpsellDebugLog");
    return "Hidden upsell debug enabled. Click checkout, then run hiddenUpsellDebug.log()."
  },
  disable:function()
  {
    localStorage.removeItem("hiddenUpsellDebug"),
    localStorage.removeItem("hiddenUpsellDebugPause");
    return "Hidden upsell debug disabled."
  },
  log:function()
  {
    try{
      return JSON.parse(localStorage.getItem("hiddenUpsellDebugLog")||"[]")
    }
    catch{
      return[]
    }
  },
  inspect:async function()
  {
    const id_0030=getHiddenCartUpsellItems(),
    id_0031=await getFreshHiddenCartUpsellItems().catch(id_0032=>({
      error:id_0032.message
    })),
    id_0033=await getHiddenUpsellCartSnapshot().catch(id_0034=>({
      error:id_0034.message
    }));
    return{
      domItems:id_0030,
      freshItems:id_0031,
      cart:id_0033
    }
  }
};
const GIFTS_LOCAL_STORAGE_KEY="exa_gift_goals";
let shouldCheckExaGifts=!1,
giftsToMonitor=[];
function initExaGiftsCheck(id_0007=!1)
{
  let id_0008=document.querySelector("cart-drawer-items")||document["querySelector"]("cart-items");
  if(id_0007&&(id_0008=document["querySelector"]("cart-items")), !id_0008||id_0008.dataset["giftsStorage"]!=="true")return;
  const id_0009=localStorage["getItem"](GIFTS_LOCAL_STORAGE_KEY);
  if(!id_0009)return;
  const id_0010=JSON["parse"](id_0009);
  !id_0010||typeof id_0010!=="object"||(Object["values"](id_0010)["forEach"](id_0011=>{
    !id_0008["querySelector"](".cart-item--product-"+id_0011["gift_handle"])||document["querySelector"](".gift--product-"+id_0011["gift_handle"])||(shouldCheckExaGifts=!0, giftsToMonitor["push"](id_0011))
  }), updateGiftStyles(), checkExaGifts(id_0007))
}
function updateGiftStyles()
{
  let id_0012=document["getElementById"]("exa-gifts-style");
  if(id_0012&&id_0012["remove"](), !giftsToMonitor["length"])return;
  const id_0013=document["createElement"]("style");
  id_0013.id="exa-gifts-style";
  const id_0014=giftsToMonitor["map"](id_0015=>".cart-item--product-"+id_0015["gift_handle"]+" .quantity { display: none !important; }").join(`
`);
  id_0013["textContent"]=id_0014,
  document.head["appendChild"](id_0013)
}
function checkExaGifts(id_0016=!1)
{
  let id_0017=document["querySelector"]("cart-drawer-items")||document["querySelector"]("cart-items");
  if(id_0016&&(id_0017=document["querySelector"]("cart-items")), !(!shouldCheckExaGifts||!id_0017||!id_0017["updateQuantity"]))for(const id_0018 of giftsToMonitor)
  {
    const id_0019=id_0017["querySelector"](".cart-item--product-"+id_0018.gift_handle);
    if(!id_0019)
    {
      const id_0020=giftsToMonitor["findIndex"](id_0021=>id_0021["gift_handle"]===id_0018["gift_handle"]);
      id_0020>-1&&giftsToMonitor.splice(id_0020, 1),
      removeLocalStorageGift(id_0018);
      continue
    }
    let id_0022=!1;
    if(id_0018["type"]==="product_quantity")
    {
      let id_0023=0;
      id_0017["querySelectorAll"](".cart-item--product-"+id_0018["unlock_product_handle"]).forEach(id_0024=>{
        id_0023+=parseInt(id_0024["dataset"]["quantity"])
      }),
      id_0023<parseInt(id_0018["required_quantity"])&&(id_0022=!0)
    }
    else{
      let id_0022a=parseInt(id_0017["dataset"]["subtotal"])||0;
      id_0019&&(id_0022a-=parseInt(id_0019["dataset"]["linePrice"])||0),
      id_0022a<parseInt(id_0018["required_quantity"])&&(id_0022=!0)
    }
    if(id_0022)
    {
      const id_0025=giftsToMonitor.findIndex(id_0026=>id_0026["gift_handle"]===id_0018.gift_handle);
      id_0025>-1&&giftsToMonitor["splice"](id_0025, 1);
      removeLocalStorageGift(id_0018);
      const cartItemIndex=id_0019["dataset"]["index"]??id_0019.id["replace"]("CartItem-", "");
      id_0017["updateQuantity"](cartItemIndex, 0);
      return
    }
  }
}
function removeLocalStorageGift(id_0027)
{
  const id_0028=localStorage.getItem(GIFTS_LOCAL_STORAGE_KEY);
  if(id_0028)try{
    const id_0029=JSON["parse"](id_0028);
    id_0029&&id_0029[id_0027.gift_handle]&&(delete id_0029[id_0027["gift_handle"]], localStorage["setItem"](GIFTS_LOCAL_STORAGE_KEY, JSON["stringify"](id_0029)))
  }
  catch(id_0030)
  {
    console["warn"]("Error parsing gift localStorage data:", id_0030)
  }
}
class CartRemoveButton extends HTMLElement
{
  constructor()
  {
    super(),
    this["isLoading"]=!1,
    this["loadingResetTimer"]=null,
    this["addEventListener"]("click", this["handleRemoveClick"].bind(this))
  }
  disconnectedCallback()
  {
    this.loadingResetTimer&&clearTimeout(this["loadingResetTimer"])
  }
  ["handleRemoveClick"](id_0031)
  {
    if(id_0031["preventDefault"](), this["isLoading"])return;
    this["setLoadingState"](!0);
    const id_0032=Date.now();
    if(this["dataset"]["handle"])
    {
      const id_0033=this["dataset"]["handle"]; document["querySelectorAll"]('.quantity-break__upsell[data-handle="'+id_0033+'"]')["forEach"](id_0034=>{
        id_0034["dataset"].selected="false", id_0034["classList"]["remove"]("quantity-break__upsell--selected"); const id_0035=id_0034["querySelector"]('input[type="checkbox"]'); id_0035&&(id_0035["checked"]=!1)
      })
    }
    const id_0036=this["closest"]("cart-items")||this["closest"]("cart-drawer-items");
    if(!id_0036)
    {
      this["setLoadingState"](!1);
      return
    }
    let id_0037;
    try{
      id_0037=this["clearCart"]?id_0036.clearCart():id_0036.updateQuantity(this["dataset"]["index"], 0)
    }
    catch{
      this["setLoadingState"](!1);
      return
    }
    this.loadingResetTimer&&clearTimeout(this["loadingResetTimer"]),
    this["loadingResetTimer"]=setTimeout(()=>{
      this["setLoadingState"](!1)
    }, 12e3),
    Promise.resolve(id_0037)["catch"](()=>{})["finally"](()=>{
      const id_0038=Math.max(0, 350-(Date.now()-id_0032)); setTimeout(()=>{
        this["setLoadingState"](!1)
      }, id_0038)
    })
  }
  ["setLoadingState"](id_0036)
  {
    this["isLoading"]=id_0036;
    const id_0039=this["querySelector"]("button, a");
    if(!id_0039)return;
    const id_0040=id_0039["querySelector"]("svg");
    id_0036?(id_0039.setAttribute("aria-disabled", "true"), id_0039["style"]["pointerEvents"]="none", id_0039["style"]["opacity"]="0.6", id_0040&&id_0040["style"]&&(id_0040.style["display"]="none"), this["loadingSpinner"]||(this["loadingSpinner"]=document["createElement"]("span"), this["loadingSpinner"]["setAttribute"]("aria-hidden", "true"), this["loadingSpinner"]["style"]["display"]="inline-block", this["loadingSpinner"].style["width"]="1.45rem", this["loadingSpinner"]["style"]["height"]="1.45rem", this["loadingSpinner"]["style"]["border"]="0.2rem solid currentColor", this["loadingSpinner"]["style"]["borderLeftColor"]="transparent", this["loadingSpinner"]["style"]["borderRadius"]="50%", this["loadingSpinner"]["style"]["verticalAlign"]="middle", this["loadingSpinner"]["style"]["boxSizing"]="border-box"), id_0039["contains"](this["loadingSpinner"])||id_0039["appendChild"](this["loadingSpinner"]), this.loadingSpinnerAnimation&&this["loadingSpinnerAnimation"]["cancel"](), this["loadingSpinnerAnimation"]=this.loadingSpinner["animate"]([
      {
        transform:"rotate(0deg)"
      },
      {
        transform:"rotate(360deg)"
      }], {
      duration:700,
      iterations:Infinity
    })):(this["loadingResetTimer"]&&clearTimeout(this["loadingResetTimer"]), this["loadingResetTimer"]=null, id_0039["removeAttribute"]("aria-disabled"), id_0039["style"]["pointerEvents"]="", id_0039.style["opacity"]="", id_0040&&id_0040["style"]&&(id_0040.style["display"]=""), this.loadingSpinnerAnimation&&(this["loadingSpinnerAnimation"]["cancel"](), this["loadingSpinnerAnimation"]=null), this["loadingSpinner"]&&this.loadingSpinner["remove"]())
  }
}
customElements["define"]("cart-remove-button", CartRemoveButton);
class CartItems extends HTMLElement
{
  constructor()
  {
    super(),
    this.lineItemContainer=!0,
    this["lineItemStatusElement"]=document.getElementById("shopping-cart-line-item-status")||document["getElementById"]("CartDrawer-LineItemStatus"),
    this["secondCartItems"]=document.querySelector("cart-drawer-items"),
    this["quantityGifts"]=document.querySelector(".quantity-gifts");
    const id_0036=debounce(id_0037=>{
      this["onChange"](id_0037)
    }, ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener("change", id_0036["bind"](this)),
    this["isCartPage"]=this["nodeName"]==="CART-ITEMS",
    this["otherCartItemsSelector"]=this["isCartPage"]?"cart-drawer-items":"cart-items",
    this.hasOtherCartItems=document.querySelector(this["otherCartItemsSelector"])!==null,
    this.hasOtherCartItems?this.isCartPage&&initExaGiftsCheck(!0):initExaGiftsCheck(!1)
  }
  cartUpdateUnsubscriber=void 0;
  ["connectedCallback"]()
  {
    this.cartUpdateUnsubscriber=subscribe(PUB_SUB_EVENTS["cartUpdate"], id_0038=>{
      id_0038["source"]!=="cart-items"&&this.isCartPage&&this["onCartUpdate"]()
    })
  }
  ["disconnectedCallback"]()
  {
    this["cartUpdateUnsubscriber"]&&this["cartUpdateUnsubscriber"]()
  }
  ["onChange"](id_0039)
  {
    this.updateQuantity(id_0039.target["dataset"].index, id_0039["target"].value, document["activeElement"]["getAttribute"]("name"))
  }
  ["onCartUpdate"]()
  {
    return cartUpdateCoordinator.refreshMainCartItems()
  }
  getCartOwners()
  {
    return[this, this.secondCartItems].filter((owner, index, owners)=>owner&&owners.indexOf(owner)===index)
  }
  getCoordinatedSectionsToRender()
  {
    const sections=new Set;
    this.getCartOwners().forEach(owner=>owner.getSectionsToRender().forEach(section=>{
      sections.add(section.section||section.id)
    }));
    return[...sections]
  }
  renderSectionsFromCartData(cartData)
  {
    if(!cartData||!cartData.sections)return;
    this.classList.toggle("is-empty", cartData.item_count===0),
    this.getSectionsToRender().forEach(section=>{
      const sectionHTML=cartData.sections[section.section||section.id],
      container=document.getElementById(section.id);
      if(!sectionHTML||!container)return;
      const content=container.querySelector(section.selector)||container;
      content.innerHTML=this.getSectionInnerHTML(sectionHTML, section.selector);
      const cartBody=content.querySelector(".cart-drawer__body");
      cartBody&&this.cartDrawer&&(cartBody.scrollTop=this.cartDrawer.cartBodyScroll)
    })
  }
  async updateCart()
  {
    try{
      const cartData=await cartUpdateCoordinator.fetchJSON("/cart/update.js", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({
          updates:{},
          sections:this.getCoordinatedSectionsToRender(),
          sections_url:window.location.pathname
        })
      }, "Cart refresh");
      this.getCartOwners().forEach(owner=>owner.renderSectionsFromCartData(cartData));
      return cartData
    }
    catch(error)
    {
      if(!cartUpdateCoordinator.isAbortError(error))console.error(error)
    }
  }
  ["getSectionsToRender"]()
  {
    return[{
      id:"main-cart-items",
      section:document["getElementById"]("main-cart-items")["dataset"].id,
      selector:".js-contents"
    },
    {
      id:"cart-icon-bubble",
      section:"cart-icon-bubble",
      selector:".shopify-section"
    },
    {
      id:"cart-live-region-text",
      section:"cart-live-region-text",
      selector:".shopify-section"
    },
    {
      id:"main-cart-footer",
      section:document["getElementById"]("main-cart-footer").dataset.id,
      selector:".js-contents"
    }]
  }
  async["updateQuantity"](id_0052, id_0053, id_0054)
  {
    const quantityUpdateToken=Symbol("quantity-update");
    this.quantityUpdateToken=quantityUpdateToken;
    let dispatchCompletion=!1;
    this["enableLoading"](id_0052);
    const id_0055=document.getElementById("cart-errors")||document["getElementById"]("CartDrawer-CartErrors");
    id_0055&&(id_0055["textContent"]="");
    const id_0056=JSON.stringify({
      line:id_0052, quantity:id_0053, sections:this.getCoordinatedSectionsToRender(), sections_url:window["location"]["pathname"]
    });
    try{
      const id_0058=buildAjaxCartUrl(routes["cart_change_url"], "/cart/change"),
      id_0060=await cartUpdateCoordinator.fetchJSON(id_0058, {
        ...fetchConfig(), body:id_0056
      }, "Cart change");
      dispatchCompletion=!0;
      const id_0061=document.getElementById("Quantity-"+id_0052)||document["getElementById"]("Drawer-quantity-"+id_0052),
      id_0062=document["querySelectorAll"](".cart-item");
      if(id_0060["errors"])
      {
        id_0061["value"]=id_0061.getAttribute("value"),
        this["updateLiveRegions"](id_0052, id_0060["errors"]);
        return
      }
      if(!this["lineItemContainer"])return;
      if(!id_0060["sections"])
      {
        id_0060.sections=await cartUpdateCoordinator.fetchJSON(routes.cart_url+"?sections="+this.getCoordinatedSectionsToRender()["join"](","), {}, "Cart section refresh")
      }
      this.classList["toggle"]("is-empty", id_0060["item_count"]===0);
      const id_0065=document["querySelector"]("cart-drawer"),
      id_0066=document["getElementById"]("main-cart-footer");
      id_0066&&id_0066["classList"]["toggle"]("is-empty", id_0060.item_count===0),
      id_0065&&id_0065["classList"]["toggle"]("is-empty", id_0060["item_count"]===0),
      this.getCartOwners().forEach(owner=>owner.renderSectionsFromCartData(id_0060));
      const id_0070a=syncCartDrawerVisibleEmptyState(id_0065);
      const id_0071=id_0060["items"][id_0052-1]?id_0060.items[id_0052-1]["quantity"]:void 0;
      let id_0072="";
      id_0062.length===id_0060.items["length"]&&id_0071!==parseInt(id_0061["value"])&&(typeof id_0071==="undefined"?id_0072=window["cartStrings"]["error"]:id_0072=window["cartStrings"]["quantityError"]["replace"]("[quantity]", id_0071)),
      this["updateLiveRegions"](id_0052, id_0072);
      const id_0073=document.getElementById("CartItem-"+id_0052)||document.getElementById("CartDrawer-Item-"+id_0052);
      if(id_0073&&id_0073["querySelector"]("[name=\""+id_0054+'"]')?id_0065?trapFocus(id_0065, id_0073["querySelector"]("[name=\""+id_0054+'"]')):id_0073["querySelector"]("[name=\""+id_0054+'"]')["focus"]():id_0070a===0&&id_0065?trapFocus(id_0065["querySelector"](".drawer__inner-empty"), id_0065["querySelector"]("a")):document["querySelector"](".cart-item:not(.cart-item--hidden)")&&id_0065&&trapFocus(id_0065, document["querySelector"](".cart-item:not(.cart-item--hidden) .cart-item__name")), id_0065)
      {
        id_0065["checkForClear"]();
        const id_0074=id_0065["querySelector"]("countdown-timer");
        id_0074&&id_0074["playTimer"]();
        let id_0075=!1;
        if(id_0065["querySelector"]("cart-drawer-gift, cart-drawer-upsell[data-toggle=\"true\"]"))
        {
          id_0065["checkForClear"]();
          let id_0076=[],
          id_0077=[];
          id_0065["querySelectorAll"]('cart-drawer-gift, cart-drawer-upsell[data-toggle="true"]')["forEach"](id_0078=>{
            id_0078["getUpdateRequired"]()&&(id_0065["querySelector"](".cart-item--product-"+id_0078["dataset"]["handle"])?id_0078["dataset"]["selected"]==="false"&&id_0077.push(id_0078):id_0078["dataset"]["selected"]==="true"&&id_0076["push"](id_0078))
          }),
          id_0077["length"]>0?(id_0075=!0, id_0077[0]["removeFromCart"]()):id_0076.length>0&&(id_0075=!0, id_0076[0].addToCart())
        }
        this["quantityGifts"]&&!id_0075&&this["quantityGifts"].initUnlock(),
        document["querySelectorAll"]("quantity-break-upsells")["forEach"](id_0079=>{
          id_0075||id_0079["initUnlock"]()
        })
      }
      publish(PUB_SUB_EVENTS["cartUpdate"], {
        source:"cart-items", cartData:id_0060
      })
    }
    catch(id_0080)
    {
      if(cartUpdateCoordinator.isAbortError(id_0080))return;
      console["error"](id_0080),
      this["querySelectorAll"](".loading-overlay").forEach(id_0081=>id_0081["classList"]["add"]("hidden"));
      const id_0082=document["getElementById"]("cart-errors")||document["getElementById"]("CartDrawer-CartErrors");
      id_0082["textContent"]=id_0080&&id_0080["message"]?id_0080["message"]:window["cartStrings"]["error"]
    }
    finally{
      if(this.quantityUpdateToken!==quantityUpdateToken)return;
      this.disableLoading(id_0052);
      if(!dispatchCompletion)return;
      const id_0083=new CustomEvent("cartQuantityUpdated", {
        detail:{
          handle:"handle"
        }
      });
      document["dispatchEvent"](id_0083),
      shouldCheckExaGifts&&checkExaGifts(this["isCartPage"])
    }
  }
  ["updateLiveRegions"](id_0084, id_0085)
  {
    const id_0086=document["getElementById"]("Line-item-error-"+id_0084)||document["getElementById"]("CartDrawer-LineItemError-"+id_0084);
    id_0086&&(id_0086.querySelector(".cart-item__error-text")["innerHTML"]=id_0085),
    this.lineItemStatusElement.setAttribute("aria-hidden", !0);
    const id_0087=document["getElementById"]("cart-live-region-text")||document["getElementById"]("CartDrawer-LiveRegionText");
    id_0087["setAttribute"]("aria-hidden", !1),
    setTimeout(()=>{
      id_0087["setAttribute"]("aria-hidden", !0)
    }, 1e3)
  }
  ["getSectionInnerHTML"](id_0088, id_0089)
  {
    return parseSectionElement(id_0088, id_0089).innerHTML
  }
  enableLoading(id_0090)
  {
    (document.getElementById("main-cart-items")||document.getElementById("CartDrawer-CartItems"))["classList"]["add"]("cart__items--disabled");
    const id_0091=this["querySelectorAll"]("#CartItem-"+id_0090+" .loading-overlay"),
    id_0092=this["querySelectorAll"]("#CartDrawer-Item-"+id_0090+" .loading-overlay");
    [...id_0091,
    ...id_0092]["forEach"](id_0093=>id_0093["classList"]["remove"]("hidden")),
    document.activeElement["blur"](),
    this.lineItemStatusElement["setAttribute"]("aria-hidden", !1)
  }
  ["disableLoading"](id_0094)
  {
    (document["getElementById"]("main-cart-items")||document.getElementById("CartDrawer-CartItems")).classList["remove"]("cart__items--disabled");
    const id_0095=this["querySelectorAll"]("#CartItem-"+id_0094+" .loading-overlay"),
    id_0096=this["querySelectorAll"]("#CartDrawer-Item-"+id_0094+" .loading-overlay");
    id_0095["forEach"](id_0097=>id_0097.classList.add("hidden")),
    id_0096["forEach"](id_0098=>id_0098.classList["add"]("hidden"))
  }
  ["clearCart"]()
  {
    const id_0099=document.getElementById("cart-errors")||document["getElementById"]("CartDrawer-CartErrors");
    id_0099&&(id_0099["textContent"]="");
    const id_0100=JSON["stringify"]({
      sections:this.getCoordinatedSectionsToRender(), sections_url:window.location["pathname"]
    }),
    id_0101=buildAjaxCartUrl(routes.cart_clear_url, "/cart/clear");
    return cartUpdateCoordinator.fetchJSON(id_0101, {
      ...fetchConfig(), body:id_0100
    }, "Clear cart")["then"](async id_0104=>{
      if(!id_0104["sections"])try{
        const id_0105=this.getCoordinatedSectionsToRender().join(","), id_0108=await cartUpdateCoordinator.fetchJSON(routes["cart_url"]+"?sections="+id_0105, {}, "Clear cart section refresh"); id_0104["sections"]=id_0108
      }
      catch(id_0109)
      {
        throw console["error"]("Clear cart fallback failed", id_0109), id_0109
      }
      this["classList"]["add"]("is-empty"); const id_0110=document["querySelector"]("cart-drawer"), id_0111=document["getElementById"]("main-cart-footer"); id_0111&&id_0111["classList"]["add"]("is-empty"), id_0110&&id_0110["classList"]["add"]("is-empty"), this.getCartOwners().forEach(owner=>owner.renderSectionsFromCartData(id_0104)), id_0110&&trapFocus(id_0110["querySelector"](".drawer__inner-empty"), id_0110["querySelector"]("a")), publish(PUB_SUB_EVENTS.cartUpdate, {
        source:"cart-items"
      }), shouldCheckExaGifts&&checkExaGifts(this["isCartPage"])
    })["catch"](id_0114=>{
      if(cartUpdateCoordinator.isAbortError(id_0114))return;
      console["error"](id_0114),
      this["querySelectorAll"](".loading-overlay").forEach(id_0115=>id_0115["classList"]["add"]("hidden")); const id_0116=document.getElementById("cart-errors")||document["getElementById"]("CartDrawer-CartErrors"); id_0116["textContent"]=id_0114&&id_0114["message"]?id_0114["message"]:window["cartStrings"]["error"]
    })
  }
}
customElements.define("cart-items", CartItems);
var search="search";
!customElements["get"]("cart-note")&&customElements.define("cart-note", class extends HTMLElement
{
  constructor()
  {
    super(), this["addEventListener"]("change", debounce(id_0116=>{
      const id_0117=JSON["stringify"]({
        note:id_0116["target"]["value"]
      }); fetch(""+routes.cart_update_url, {
        ...fetchConfig(), body:id_0117
      })
    }, ON_CHANGE_DEBOUNCE_TIMER))
  }
});
function handleDiscountForm(id_0118)
{
  id_0118["preventDefault"]();
  const id_0119=id_0118["target"]["querySelector"]("[name=cart-discount-field]"),
  id_0120=id_0118["target"]["querySelector"](".cart-discount-form__error"),
  id_0121=id_0119["value"];
  if(id_0121===void 0||id_0121["length"]===0)
  {
    id_0120["style"]["display"]="block";
    return
  }
  id_0120["style"]["display"]="none";
  const id_0122="/checkout?discount=",
  id_0123=id_0122+id_0121;
  window.location.href=id_0123
}
function handleDiscountFormChange(id_0124)
{
  document["querySelectorAll"](".cart-discount-form__error")["forEach"](id_0125=>{
    id_0125["style"]["display"]="none"
  })
}
class SearchForm extends HTMLElement
{
  constructor()
  {
    super(),
    this["input"]=this.querySelector("input[type=\"search\"]"),
    this["resetButton"]=this["querySelector"]('button[type="reset"]'),
    this.input&&(this.input["form"]["addEventListener"]("reset", this["onFormReset"]["bind"](this)), this["input"].addEventListener("input", debounce(id_0126=>{
      this["onChange"](id_0126)
    }, 300).bind(this)))
  }
  toggleResetButton()
  {
    const id_0127=this["resetButton"]["classList"]["contains"]("hidden");
    this["input"]["value"]["length"]>0&&id_0127?this["resetButton"]["classList"].remove("hidden"):this["input"].value["length"]===0&&!id_0127&&this["resetButton"]["classList"]["add"]("hidden")
  }
  ["onChange"]()
  {
    this["toggleResetButton"]()
  }
  ["shouldResetForm"]()
  {
    return!document["querySelector"]("[aria-selected=\"true\"] a")
  }
  ["onFormReset"](id_0128)
  {
    id_0128["preventDefault"](),
    this.shouldResetForm()&&(this.input["value"]="", this.input["focus"](), this["toggleResetButton"]())
  }
}
customElements["define"]("search-form", SearchForm);
class PredictiveSearch extends SearchForm
{
  constructor()
  {
    super(),
    this["cachedResults"]={
    },
    this["predictiveSearchResults"]=this.querySelector("[data-predictive-search]"),
    this["allPredictiveSearchInstances"]=document.querySelectorAll("predictive-search"),
    this.isOpen=!1,
    this["abortController"]=new AbortController,
    this["searchTerm"]="",
    this.setupEventListeners()
  }
  ["setupEventListeners"]()
  {
    this["input"].form["addEventListener"]("submit", this["onFormSubmit"]["bind"](this)),
    this["input"]["addEventListener"]("focus", this["onFocus"].bind(this)),
    this["addEventListener"]("focusout", this["onFocusOut"].bind(this)),
    this["addEventListener"]("keyup", this.onKeyup["bind"](this)),
    this["addEventListener"]("keydown", this.onKeydown.bind(this))
  }
  ["getQuery"]()
  {
    return this["input"].value["trim"]()
  }
  ["onChange"]()
  {
    super["onChange"]();
    const id_0129=this["getQuery"]();
    if((!this["searchTerm"]||!id_0129["startsWith"](this.searchTerm))&&this.querySelector("#predictive-search-results-groups-wrapper")?.remove(), this["updateSearchForTerm"](this["searchTerm"], id_0129), this["searchTerm"]=id_0129, !this["searchTerm"]["length"])
    {
      this["close"](!0);
      return
    }
    this["getSearchResults"](this.searchTerm)
  }
  ["onFormSubmit"](id_0130)
  {
    (!this["getQuery"]().length||this.querySelector("[aria-selected=\"true\"] a"))&&id_0130.preventDefault()
  }
  ["onFormReset"](id_0131)
  {
    super.onFormReset(id_0131),
    super["shouldResetForm"]()&&(this.searchTerm="", this["abortController"].abort(), this["abortController"]=new AbortController, this["closeResults"](!0))
  }
  ["onFocus"]()
  {
    const id_0132=this["getQuery"]();
    id_0132.length&&(this["searchTerm"]!==id_0132?this["onChange"]():this["getAttribute"]("results")==="true"?this.open():this.getSearchResults(this.searchTerm))
  }
  ["onFocusOut"]()
  {
    setTimeout(()=>{
      this["contains"](document["activeElement"])||this["close"]()
    })
  }
  ["onKeyup"](id_0133)
  {
    switch(this["getQuery"]()["length"]||this["close"](!0), id_0133.preventDefault(), id_0133["code"])
    {
      case "ArrowUp":this["switchOption"]("up");
      break;
      case "ArrowDown":this.switchOption("down");
      break;
      case"Enter":this["selectOption"]();
      break
    }
  }
  ["onKeydown"](id_0134)
  {
    (id_0134["code"]==="ArrowUp"||id_0134["code"]==="ArrowDown")&&id_0134.preventDefault()
  }
  ["updateSearchForTerm"](id_0135, id_0136)
  {
    const id_0137=this["querySelector"]("[data-predictive-search-search-for-text]"),
    id_0138=id_0137?.["innerText"];
    if(id_0138)
    {
      if(id_0138["match"](new RegExp(id_0135, "g")).length>1)return;
      const id_0139=id_0138["replace"](id_0135, id_0136);
      id_0137.innerText=id_0139
    }
  }
  switchOption(id_0140)
  {
    if(!this["getAttribute"]("open"))return;
    const id_0141=id_0140==="up",
    id_0142=this["querySelector"]("[aria-selected=\"true\"]"),
    id_0143=Array.from(this.querySelectorAll("li, button.predictive-search__item"))["filter"](id_0144=>id_0144["offsetParent"]!==null);
    let id_0145=0;
    if(id_0141&&!id_0142)return;
    let id_0146=-1,
    id_0147=0;
    for(; id_0146===-1&&id_0147<=id_0143.length; )id_0143[id_0147]===id_0142&&(id_0146=id_0147),
    id_0147++;
    if(this["statusElement"]["textContent"]="", !id_0141&&id_0142?id_0145=id_0146===id_0143["length"]-1?0:id_0146+1:id_0141&&(id_0145=id_0146===0?id_0143["length"]-1:id_0146-1), id_0145===id_0146)return;
    const id_0148=id_0143[id_0145];
    id_0148["setAttribute"]("aria-selected", !0),
    id_0142&&id_0142["setAttribute"]("aria-selected", !1),
    this["input"]["setAttribute"]("aria-activedescendant", id_0148.id)
  }
  selectOption()
  {
    const id_0149=this["querySelector"]("[aria-selected=\"true\"] a, button[aria-selected=\"true\"]");
    id_0149&&id_0149["click"]()
  }
  ["getSearchResults"](id_0150)
  {
    const id_0151=id_0150["replace"](" ", "-")["toLowerCase"]();
    if(this["setLiveRegionLoadingState"](), this["cachedResults"][id_0151])
    {
      this.renderSearchResults(this["cachedResults"][id_0151]);
      return
    }
    fetch(routes["predictive_search_url"]+"?q="+encodeURIComponent(id_0150)+"&section_id=predictive-search", {
      signal:this["abortController"].signal
    })["then"](id_0152=>{
      if(!id_0152.ok)
      {
        var id_0153=new Error(id_0152["status"]); throw this["close"](), id_0153
      }
      return id_0152["text"]()
    })["then"](id_0154=>{
      const id_0155=new DOMParser()["parseFromString"](id_0154, "text/html")["querySelector"]("#shopify-section-predictive-search")["innerHTML"]; this.allPredictiveSearchInstances["forEach"](id_0156=>{
        id_0156.cachedResults[id_0151]=id_0155
      }), this.renderSearchResults(id_0155)
    }).catch(id_0157=>{
      if(id_0157?.["code"]!==20)throw this["close"](), id_0157
    })
  }
  ["setLiveRegionLoadingState"]()
  {
    this["statusElement"]=this["statusElement"]||this["querySelector"](".predictive-search-status"),
    this["loadingText"]=this["loadingText"]||this["getAttribute"]("data-loading-text"),
    this["setLiveRegionText"](this["loadingText"]),
    this["setAttribute"]("loading", !0)
  }
  setLiveRegionText(id_0158)
  {
    this.statusElement.setAttribute("aria-hidden", "false"),
    this["statusElement"]["textContent"]=id_0158,
    setTimeout(()=>{
      this["statusElement"]["setAttribute"]("aria-hidden", "true")
    }, 1e3)
  }
  ["renderSearchResults"](id_0159)
  {
    this.predictiveSearchResults.innerHTML=id_0159,
    this["setAttribute"]("results", !0),
    this.setLiveRegionResults(),
    this["open"]()
  }
  ["setLiveRegionResults"]()
  {
    this.removeAttribute("loading"),
    this["setLiveRegionText"](this["querySelector"]("[data-predictive-search-live-region-count-value]").textContent)
  }
  ["getResultsMaxHeight"]()
  {
    return this["resultsMaxHeight"]=window["innerHeight"]-document["querySelector"](".section-header")["getBoundingClientRect"]()["bottom"],
    this["resultsMaxHeight"]
  }
  ["open"]()
  {
    this.predictiveSearchResults["style"]["maxHeight"]=this["resultsMaxHeight"]||this["getResultsMaxHeight"]()+"px",
    this.setAttribute("open", !0),
    this["input"].setAttribute("aria-expanded", !0),
    this["isOpen"]=!0
  }
  close(id_0160=!1)
  {
    this["closeResults"](id_0160),
    this.isOpen=!1
  }
  ["closeResults"](id_0161=!1)
  {
    id_0161&&(this["input"]["value"]="", this["removeAttribute"]("results"));
    const id_0162=this["querySelector"]("[aria-selected=\"true\"]");
    id_0162&&id_0162["setAttribute"]("aria-selected", !1),
    this["input"].setAttribute("aria-activedescendant", ""),
    this.removeAttribute("loading"),
    this["removeAttribute"]("open"),
    this["input"]["setAttribute"]("aria-expanded", !1),
    this["resultsMaxHeight"]=!1,
    this.predictiveSearchResults["removeAttribute"]("style")
  }
}
customElements["define"]("predictive-search", PredictiveSearch);
const defMed="dat"+"a-def"+"er";
class CartDrawer extends HTMLElement
{
  constructor()
  {
    super(),
    this["upsellHandles"]=this["getUpsellHandles"](),
    this["quantityGifts"]=document["querySelector"](".quantity-gifts"),
    this.checkForClear(),
    this["addEventListener"]("keyup", id_0163=>id_0163.code==="Escape"&&this["close"]()),
    this["querySelector"]("#CartDrawer-Overlay")["addEventListener"]("click", this["close"].bind(this)),
    this["setHeaderCartIconAccessibility"](),
    this["cartBodyScroll"]=0,
    this["countdownTimerPlayRequest"]=0,
    this["countdownTimerShouldPlay"]=!1,
    syncCartDrawerVisibleEmptyState(this)
  }
  ["setHeaderCartIconAccessibility"]()
  {
    let id_0164=document["querySelector"]("#cart-icon-bubble");
    if(!id_0164||(id_0164["tagName"]=="DIV"&&(id_0164=id_0164.closest(".header__icon--cart")), !id_0164))return;
    const id_0165=id_0164["closest"](".header__icons");
    id_0164["setAttribute"]("role", "button"),
    id_0164["setAttribute"]("aria-haspopup", "dialog"),
    id_0164.addEventListener("click", id_0166=>{
      id_0166["preventDefault"](), this.open(id_0164)
    }),
    this.oseid=id_0165.querySelector("form")["dataset"][this["dataset"]["type"]],
    id_0164["addEventListener"]("keydown", id_0167=>{
      id_0167["code"].toUpperCase()==="SPACE"&&(id_0167["preventDefault"](), this["open"](id_0164))
    })
  }
  ["open"](id_0168)
  {
    id_0168&&this["setActiveElement"](id_0168);
    const id_0169=this["querySelector"]('[id^="Details-"] summary');
    id_0169&&!id_0169.hasAttribute("role")&&this["setSummaryAccessibility"](id_0169),
    setTimeout(()=>{
      this["classList"].add("animate", "active")
    }),
    this.addEventListener("transitionend", ()=>{
      const id_0170=this["classList"]["contains"]("is-empty")?this.querySelector(".drawer__inner-empty"):document["getElementById"]("CartDrawer"), id_0171=this["querySelector"](".drawer__inner")||this.querySelector(".drawer__close"); trapFocus(id_0170, id_0171)
    }, {
      once:!0
    }),
    document.body["classList"]["add"]("overflow-hidden");
    this["playCountdownTimerWhenReady"]()
  }
  close()
  {
    this["cancelCountdownTimerPlay"](),
    this["classList"]["remove"]("active"),
    removeTrapFocus(this["activeElement"]),
    document["body"].classList.remove("overflow-hidden")
  }
  ["playCountdownTimerWhenReady"]()
  {
    const countdownTimer=this.querySelector("countdown-timer");
    if(!countdownTimer)return;
    this.countdownTimerShouldPlay=!0;
    const requestId=++this.countdownTimerPlayRequest;
    if(typeof countdownTimer.playTimer==="function")
    {
      countdownTimer.playTimer();
      return
    }
    if(!window.customElements||typeof customElements.whenDefined!=="function")return;
    customElements.whenDefined("countdown-timer").then(()=>{
      if(this.countdownTimerPlayRequest!==requestId||!this.countdownTimerShouldPlay||!this.isConnected||!countdownTimer.isConnected)return;
      if(this.querySelector("countdown-timer")!==countdownTimer||typeof countdownTimer.playTimer!=="function")return;
      countdownTimer.playTimer()
    }, ()=>{
    })
  }
  ["cancelCountdownTimerPlay"]()
  {
    this.countdownTimerShouldPlay=!1;
    this.countdownTimerPlayRequest+=1
  }
  disconnectedCallback()
  {
    this.cancelCountdownTimerPlay()
  }
  ["getUpsellHandles"]()
  {
    const id_0173=this["querySelectorAll"]("cart-drawer-upsell[data-toggle=\"true\"], cart-drawer-gift"),
    id_0174=[];
    return id_0173["forEach"](id_0175=>{
      id_0175.dataset["handle"]&&id_0174["push"](id_0175["dataset"]["handle"])
    }),
    id_0174
  }
  ["oneNonUpellRemaining"]()
  {
    return this.querySelectorAll(".cart-item:not(.cart-item--hidden)")["length"]<=1
  }
  checkForClear()
  {
    const id_0180=this["oneNonUpellRemaining"]();
    this["querySelectorAll"]("cart-remove-button").forEach(id_0181=>{
      id_0180?id_0181["clearCart"]=!0:id_0181["clearCart"]=!1
    })
  }
  ["setSummaryAccessibility"](id_0182)
  {
    id_0182["setAttribute"]("role", "button"),
    id_0182["setAttribute"]("aria-expanded", "false"),
    id_0182["nextElementSibling"]["getAttribute"]("id")&&id_0182["setAttribute"]("aria-controls", id_0182.nextElementSibling.id),
    id_0182["addEventListener"]("click", id_0183=>{
      id_0183.currentTarget["setAttribute"]("aria-expanded", !id_0183["currentTarget"]["closest"]("details")["hasAttribute"]("open"))
    }),
    id_0182["parentElement"]["addEventListener"]("keyup", onKeyUpEscape)
  }
  renderContents(id_0184, id_0185=!1)
  {
    this.querySelector(".drawer__inner").classList["contains"]("is-empty")&&this.querySelector(".drawer__inner")["classList"]["remove"]("is-empty"),
    this["productId"]=id_0184.id,
    this["getSectionsToRender"]()["forEach"](id_0186=>{
      const id_0187=id_0186["selector"]?document["querySelector"](id_0186["selector"]):document["getElementById"](id_0186.id); if(id_0187)
      {
        id_0187["innerHTML"]=this["getSectionInnerHTML"](id_0184["sections"][id_0186.id], id_0186["selector"]); const id_0188=id_0187.querySelector(".cart-drawer__body"); id_0188&&(id_0188["scrollTop"]=this["cartBodyScroll"])
      }
    }),
    syncCartDrawerVisibleEmptyState(this),
    this["checkForClear"]();
    const id_0189=this["querySelector"]("countdown-timer");
    id_0189&&id_0189.playTimer&&id_0189["playTimer"]();
    let id_0190=[],
    id_0191=[];
    this["querySelectorAll"]('cart-drawer-gift, cart-drawer-upsell[data-toggle="true"]')["forEach"](id_0192=>{
      id_0192["getUpdateRequired"]()&&(this["querySelector"](".cart-item--product-"+id_0192["dataset"]["handle"])?id_0192["dataset"]["selected"]==="false"&&id_0191["push"](id_0192):id_0192["dataset"]["selected"]==="true"&&id_0190.push(id_0192))
    });
    id_0190["sort"]((id_0193a, id_0193b)=>id_0193a["tagName"]==="CART-DRAWER-GIFT"?1:id_0193b["tagName"]==="CART-DRAWER-GIFT"?-1:0);
    const id_0190_designMode=window["Shopify"]&&window["Shopify"]["designMode"],
    id_0190_needsSync=!id_0190_designMode&&(id_0191["length"]>0||id_0190["length"]>0);
    if(id_0190_needsSync&&!id_0185){this._openAfterSync=!0}
    !id_0190_designMode&&id_0191["length"]>0?id_0191[0]["removeFromCart"]():!id_0190_designMode&&id_0190["length"]>0?id_0190[0]["addToCart"]():(this["quantityGifts"]&&this.quantityGifts["initUnlock"](), document["querySelectorAll"]("quantity-break-upsells")["forEach"](id_0193=>{
      id_0193.initUnlock()
    })),
    setTimeout(()=>{
      this["querySelector"]("#CartDrawer-Overlay")["addEventListener"]("click", this["close"]["bind"](this));
      if(id_0190_needsSync){return}
      if(this._openAfterSync){this._openAfterSync=false;this.open();document.dispatchEvent(new CustomEvent("cartDrawerOpened"));return}
      !id_0185&&this.open()
    })
  }
  ["getSectionInnerHTML"](id_0194, id_0195=".shopify-section")
  {
    let id_0196=parseSectionElement(id_0194, id_0195);
    return id_0195==="#CartDrawer"&&fixParsedHtml(this, id_0196),
    id_0196["innerHTML"]
  }
  ["getSectionsToRender"]()
  {
    return[{
      id:"cart-drawer",
      selector:"#CartDrawer"
    },
    {
      id:"cart-icon-bubble"
    }]
  }
  ["getSectionDOM"](id_0197, id_0198=".shopify-section")
  {
    return new DOMParser()["parseFromString"](id_0197, "text/html")["querySelector"](id_0198)
  }
  ["setActiveElement"](id_0199)
  {
    this["activeElement"]=id_0199
  }
}
customElements.define("cart-drawer", CartDrawer);
class CartDrawerItems extends CartItems
{
  constructor()
  {
    super(),
    this["cartDrawer"]=document["querySelector"]("cart-drawer"),
    this["secondCartItems"]=document["querySelector"]("cart-items")
  }
  ["getSectionInnerHTML"](id_0203, id_0204)
  {
    let id_0205=parseSectionElement(id_0203, id_0204);
    return id_0204===".drawer__inner"&&fixParsedHtml(this["cartDrawer"], id_0205),
    id_0205.innerHTML
  }
  getSectionsToRender()
  {
    return[{
      id:"CartDrawer",
      section:"cart-drawer",
      selector:".drawer__inner"
    },
    {
      id:"cart-icon-bubble",
      section:"cart-icon-bubble",
      selector:".shopify-section"
    }]
  }
}
customElements["define"]("cart-drawer-items", CartDrawerItems);
function fixParsedHtml(id_0206, id_0207)
{
  const id_0208=id_0206["querySelector"](".cart-drawer__body");
  if(id_0208)
  {
    const id_0209=document["querySelector"]("cart-drawer");
    id_0209.cartBodyScroll=id_0208["scrollTop"]
  }
  const id_0210=id_0207.querySelector(".cart-timer");
  if(id_0210)
  {
    const oldTimer=id_0206.querySelector(".cart-timer");
    oldTimer&&(id_0210.innerHTML=oldTimer["innerHTML"])
  }
  const id_0211=id_0206["querySelectorAll"]("cart-drawer-upsell[data-toggle=\"true\"], cart-drawer-gift");
  let id_0212=id_0207["querySelectorAll"]("cart-drawer-upsell[data-toggle=\"true\"], cart-drawer-gift");
  id_0211["forEach"]((id_0213, id_0214)=>{
    let id_0215=[...id_0212]["find"](id_0215a=>id_0215a["dataset"]["handle"]===id_0213["dataset"]["handle"]&&id_0215a["nodeName"]["toLowerCase"]()===id_0213["nodeName"]["toLowerCase"]())||id_0212[id_0214]; if(id_0215)
    {
      if(id_0213["nodeName"].toLowerCase()==="cart-drawer-upsell"&&(id_0215["dataset"]["selected"]=id_0213.dataset["selected"]), id_0215["dataset"].id=id_0213["dataset"].id, id_0215["querySelector"]("[name=\"id\"]")["value"]=id_0213["querySelector"]("[name=\"id\"]")["value"], id_0215["querySelector"](".upsell__image__img")&&(id_0215["querySelector"](".upsell__image__img")["src"]=id_0213["querySelector"](".upsell__image__img").src), id_0215["querySelector"](".upsell__variant-picker"))
      {
        const id_0216=id_0213["querySelectorAll"](".select__select"); id_0215["querySelectorAll"](".select__select").forEach((id_0217, id_0218)=>{
          id_0217["value"]=id_0216[id_0218]["value"], id_0217["querySelectorAll"]("option")["forEach"](id_0219=>{
            id_0219["removeAttribute"]("selected"), id_0219["value"]===id_0216[id_0218]["value"]["trim"]()&&id_0219["setAttribute"]("selected", "")
          })
        })
      }
      if(id_0213["dataset"]["updatePrices"]==="true")
      {
        var id_0220=id_0215["querySelector"](".upsell__price"), id_0221=id_0213["querySelector"](".upsell__price"); id_0220&&id_0221&&(id_0220.innerHTML=id_0221["innerHTML"])
      }
    }
  })
}
!customElements.get("product-form")&&customElements.define("product-form", class extends HTMLElement
{
  constructor()
  {
    super(), this.form=this["querySelector"]("form"), this["form"]["addEventListener"]("submit", this["handleSubmit"]["bind"](this)), this.formIdInput=this["form"]["querySelector"]("[name=id]"), this["formIdInput"]["disabled"]=!1, this.isMain=this["classList"]["contains"]("main-product-form"), this["additionalAtcButtons"]=this.isMain?document.querySelectorAll(".main-product-atc"):[], this.skipCart=this["formIdInput"].dataset.skipCart==="true", this.cart=document["querySelector"]("cart-drawer")||document["querySelector"]("cart-notification"), this["isCartUpsell"]=this["dataset"]["isCartUpsell"]==="true", this["submitButton"]=this["querySelector"]("[type=\"submit\"]"), document.querySelector("cart-drawer")&&(this.submitButton&&this["submitButton"]["setAttribute"]("aria-haspopup", "dialog"), this["hasDrawer"]=!0), this["submitButton"]&&(this["loadingSpinner"]=this["submitButton"]["querySelector"](".loading-overlay__spinner")), this.displayedSubmitButton=document["getElementById"]("DisplayedSubmitBtn-"+this["dataset"].section), this["bundleDeals"]=document["getElementById"]("bundle-deals-"+this.dataset["section"]), this["productInfo"]=document["getElementById"]("ProductInfo-"+this["dataset"]["section"]), this["quantityBreaks"]=document["getElementById"]("quantity-breaks-"+this.dataset["section"]), this["quantityBreakUpsells"]=this["productInfo"]?this["productInfo"]["querySelectorAll"]("quantity-break-upsells"):this["quantityBreaks"]?this["quantityBreaks"]["querySelectorAll"]("quantity-break-upsells"):document["querySelectorAll"]('quantity-break-upsells[data-section="'+this["dataset"].section+'"]'), this.quantityPicker=document["getElementById"]("Quantity-Form-"+this["dataset"].section), this.customFields=document.querySelectorAll("[id^='CustomField-"+this.dataset["section"]+"-']"), this["quantityGifts"]=document.getElementById("quantity-gifts-"+this["dataset"]["section"]), this["mainBundleItems"]=document["querySelectorAll"](".main-offer-item-"+this["dataset"].section), this.variantInputs=this.form["querySelector"](".product-form__variants")
  }
  ["handleSubmit"](id_0222)
  {
    let id_0223=null, id_0224=!1; if(id_0222&&(id_0222["preventDefault"](), id_0222.currentTarget["classList"]["contains"]("button--has-spinner")&&(id_0223=id_0222["currentTarget"], id_0223["classList"].add("loading")), id_0222["currentTarget"]["dataset"]["skipCartButton"]==="true"&&(id_0224=!0)), this.submitButton["getAttribute"]("aria-disabled")==="true")return; this["handleErrorMessage"](), this.submitButton["setAttribute"]("aria-disabled", !0), id_0223||(this["submitButton"]["classList"]["add"]("loading"), this["loadingSpinner"]&&this["loadingSpinner"]["classList"]["remove"]("hidden")), this.additionalAtcButtons["forEach"](id_0225=>{
      id_0225["setAttribute"]("disabled", "")
    }), this.displayedSubmitButton&&(this["displayedSubmitButton"].setAttribute("disabled", ""), this["displayedSubmitButton"]["classList"].add("loading")); let id_0226=!1, id_0227=!1; if(this.variantInputs&&!this.ref)
    {
      this["variantInputs"]["innerHTML"]=""; const id_0229=[], id_0230=new Set, id_0231=id_0232=>{
        const id_0233=id_0232==null?"":String(id_0232)["trim"](); return id_0233===""?null:id_0233
      }, id_0234=(id_0235, id_0236=1, id_0237=null, id_0237a=null)=>{
        if(!id_0235)return; const id_0238=String(id_0235), id_0239=Number(id_0236)||1, id_0240=id_0231(id_0237), id_0241=id_0229["findIndex"](id_0242=>{
          const id_0243=id_0231(id_0242.selling_plan); return id_0242.id===id_0238&&id_0243===id_0240
        }); if(id_0241===-1)
        {
          const id_0244={
            id:id_0238, quantity:id_0239
          }; id_0240&&(id_0244["selling_plan"]=id_0240), Array.isArray(id_0237a)&&id_0237a.length&&(id_0244.properties=id_0237a), id_0229["push"](id_0244)
        }
        else id_0229[id_0241]["quantity"]+=id_0239, Array.isArray(id_0237a)&&id_0237a.length&&!id_0229[id_0241].properties&&(id_0229[id_0241].properties=id_0237a)
      }, id_0245=id_0246=>{
        id_0246&&id_0230["add"](String(id_0246))
      }; if(this["hasDrawer"]&&!this["isCartUpsell"]&&!this["skipCart"]&&this["cart"]["querySelectorAll"]('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift')["forEach"](id_0247=>{
        if(id_0247.dataset["selected"]==="true"&&!this["cart"].querySelector(".cart-item--product-"+id_0247["dataset"].handle))
        {
          const id_0248=id_0247.querySelector('input[name="selling_plan"]')?.value||null, id_0248a=[]; id_0247.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]').forEach(id_0248b=>{
            const id_0248c=id_0248b.name.match(/properties\[(.*?)\]/); id_0248c&&id_0248a.push({
              fieldName:id_0248c[1], value:id_0248b.value
            })
          }), id_0234(id_0247["dataset"].id, 1, id_0248, id_0248a.length?id_0248a:null)
        }
      }), this["bundleDeals"])this["dataset"]["includePdpUpsells"]==="true"&&document["querySelectorAll"]("product-info-upsell")["forEach"](id_0249=>{
        const id_0250=id_0249["querySelector"]('input[name="selling_plan"]')?.["value"]||null; id_0249["dataset"]["selected"]==="true"&&id_0234(id_0249["dataset"].id, 1, id_0250)
      }), (this["bundleDeals"]["formVariants"]||[])["forEach"](id_0251=>{
        id_0251&&id_0234(id_0251.id, Number(id_0251["quantity"])||1)
      }); else{
        this["quantityGifts"]&&Array["isArray"](this["quantityGifts"]["unlockedItems"])&&this["quantityGifts"]["unlockedItems"]["length"]>0&&this["quantityGifts"]["unlockedItems"]["forEach"](id_0252=>id_0234(id_0252, 1)), this["upsells"]=document["querySelectorAll"](".product-info-upsell-"+this["dataset"]["section"]); for(let id_0253=this["upsells"]["length"]-1; id_0253>=0; id_0253--)
        {
          const id_0254=this.upsells[id_0253]; if(id_0254&&id_0254["dataset"].selected==="true")
          {
            const id_0255=id_0254["querySelector"]('input[name="selling_plan"]')?.["value"]||null; id_0234(id_0254["dataset"].id, 1, id_0255)
          }
        }
        if(this["quantityBreakUpsells"]&&this["quantityBreakUpsells"]["forEach"](id_0256=>{
          (id_0256["unlockedItems"]||[])["forEach"](id_0257=>{
            const id_0258=String(id_0257); !id_0229.some(id_0259=>String(id_0259.id)===id_0258)&&id_0234(id_0258, 1)
          })
        }), this["mainBundleItems"]["length"]===0)if(this["quantityBreaks"]&&Array["isArray"](this["quantityBreaks"]["formVariants"])&&this["quantityBreaks"].formVariants.length>0)this["quantityBreaks"].formVariants["forEach"](id_0260=>{
          id_0234(id_0260, 1), id_0245(id_0260)
        }), id_0227=!0; else{
          let id_0261=1; this.quantityBreaks?id_0261=Number(this["quantityBreaks"]["selectedQuantity"])||1:this.quantityPicker?id_0261=Number(this["quantityPicker"]["querySelector"](".quantity__input")?.["value"])||1:this["querySelector"]("input[name=\"quantity\"]")&&(id_0261=Number(this["querySelector"]("input[name=\"quantity\"]")["value"])||1); const id_0262=this["formIdInput"]["value"]; id_0234(id_0262, id_0261), id_0245(id_0262)
        }
        for(let id_0263=this["mainBundleItems"]["length"]-1; id_0263>=0; id_0263--)
        {
          const id_0264=this.mainBundleItems[id_0263]; id_0234(id_0264["dataset"].id, 1), id_0245(id_0264.dataset.id)
        }
      }
      if(id_0229["length"]>1||id_0227||this["bundleDeals"]||this["mainBundleItems"]["length"]>0)
      {
        const id_0265=[]; this["customFields"]?.["forEach"](id_0266=>{
          id_0266?.["dataset"]?.["disabled"]!=="true"&&id_0265.push({
            fieldName:id_0266["fieldName"], value:id_0266["prevValue"]
          })
        }); let id_0267=null; this["classList"]["contains"]("main-product-form")&&(id_0267=this["querySelector"](".appstle-active-option [name=\"selling_plan\"]")||this["querySelector"]("[name=\"selling_plan\"]")||document.querySelector(".sls-purchase [name=\"selling_plan\"]")); const id_0268=this.querySelector("#simple-bundles-io-options"), id_0269="input[name^=\"properties[\"]:not(#validator):not(.exa-property-input):not([type=\"radio\"]),"+"input[name^=\"properties[\"][type=\"radio\"]:checked:not(#validator):not(.exa-property-input),"+"select[name^=\"properties[\"]:not(#validator):not(.exa-property-input)", id_0270=this["classList"].contains("main-product-form")?this.productInfo?this["productInfo"]["querySelectorAll"](id_0269):document["querySelectorAll"](id_0269):[]; id_0229["forEach"](id_0271=>{
          if(id_0230["has"](id_0271.id))
          {
            const id_0272=[]; id_0265["forEach"](id_0273=>{
              id_0272["push"]({
                fieldName:id_0273["fieldName"], value:id_0273["value"]
              })
            }), id_0268&&id_0268.querySelectorAll('select[name^="properties["], input[name^="properties["]')["forEach"](id_0274=>{
              const id_0275=id_0274.name["match"](/properties\[(.*?)\]/); id_0275&&id_0272["push"]({
                fieldName:id_0275[1], value:id_0274["value"]
              })
            }), this["classList"]["contains"]("main-product-form")&&id_0270["forEach"](id_0276=>{
              const id_0277=id_0276["name"]["match"](/properties\[(.*?)\]/); id_0277&&id_0272.push({
                fieldName:id_0277[1], value:id_0276["value"]
              })
            }), id_0272.length&&(id_0271["properties"]=id_0272), id_0267&&typeof id_0267.value==="string"&&id_0267["value"]["length"]>0&&(id_0271.selling_plan=id_0267["value"])
          }
        }); const id_0278=document["createDocumentFragment"](), appendHiddenInput=(id_0278a, id_0278b)=>{
          const id_0278c=document["createElement"]("input"); id_0278c.type="hidden", id_0278c.name=id_0278a, id_0278c.value=String(id_0278b), id_0278["appendChild"](id_0278c)
        }; id_0229.forEach((id_0279, id_0280)=>{
          appendHiddenInput("items["+id_0280+"][quantity]", id_0279["quantity"]), appendHiddenInput("items["+id_0280+"][id]", id_0279.id), id_0279["selling_plan"]&&appendHiddenInput("items["+id_0280+"][selling_plan]", id_0279["selling_plan"]), Array.isArray(id_0279.properties)&&id_0279["properties"].length&&id_0279.properties.forEach(id_0281=>{
            appendHiddenInput("items["+id_0280+"][properties]["+id_0281["fieldName"]+"]", serializePropValue(id_0281["value"]))
          })
        }), Array.from(this.form.querySelectorAll('input[name^="items["], select[name^="items["], textarea[name^="items["]')).forEach(_el=>_el.parentNode&&_el.parentNode.removeChild(_el)), this.form.appendChild(id_0278), id_0226=!0, this.form.querySelector('input[name^="items["]')||(id_0226=!1)
      }
    }
    var id_0283=fetchConfig("json"); id_0283.headers.Accept="application/json", delete id_0283.headers["Content-Type"]; var id_0284=new FormData(this["form"]); const _itemsExists2=this.form?.querySelector('input[name^="items["]'); if(this["ref"]&&_itemsExists2?id_0226=!0:this["ref"]&&!_itemsExists2&&(id_0226=!1), this["cart"]&&(id_0284["append"]("sections", this["cart"]["getSectionsToRender"]()["map"](id_0285=>id_0285.id)), id_0284["append"]("sections_url", window["location"]["pathname"]), this["cart"].setActiveElement(document["activeElement"])), _itemsExists2)
    {
      const id_0286=this["dataset"].options?this.dataset["options"].split(","):[], id_0287=["id", "quantity", ...id_0286]; for(let id_0288=0; id_0288<id_0287["length"]; id_0288++)id_0284["delete"](id_0287[id_0288])
    }
    else id_0226=!1; const id_0289=window["routes"]&&window["routes"]["cart_add_url"]?window["routes"]["cart_add_url"]:"/cart/add",
    id_0290=id_0289.match(/[?#].*$/),
    id_0291=id_0290?id_0290[0]:"",
    id_0292=id_0290?id_0289.slice(0, -id_0291.length):id_0289,
    id_0293=id_0292.endsWith(".js")?id_0292:id_0292+".js",
    id_0294=id_0293+id_0291; id_0283.body=id_0284, fetch(id_0294, id_0283).then(async id_0295=>{
      const id_0296=await id_0295.text();
      let id_0297={
      };
      if(id_0296)try{
        id_0297=JSON.parse(id_0296)
      }
      catch{
        throw new Error("Invalid add-to-cart response ("+id_0295.status+")")
      }
      if(!id_0295.ok)
      {
        if(id_0295.status===401)throw new Error("Add to cart unauthorized (401). Open the Shopify preview URL and retry.");
        throw new Error(id_0297.description||id_0297.message||"Add to cart failed ("+id_0295.status+")")
      }
      return id_0297
    }).then(async id_0290=>{
      if(id_0290["status"])
      {
        this["handleErrorMessage"](id_0290["description"]); const id_0291=this["submitButton"].querySelector(".sold-out-message"); if(!id_0291)return; this["submitButton"]["setAttribute"]("aria-disabled", !0), this.submitButton["querySelector"]("span")["classList"]["add"]("hidden"), id_0291["classList"]["remove"]("hidden"), this.error=!0; return
      }
      if(!id_0290["sections"]&&this["cart"])try{
        const id_0294=await cartUpdateCoordinator.fetchJSON(routes.cart_url+"?sections="+this["cart"]["getSectionsToRender"]()["map"](id_0293=>id_0293.id)["join"](","), {}, "Add to cart section refresh"); id_0290["sections"]=id_0294
      }
      catch(id_0295)
      {
        console.error("Add to cart fallback failed", id_0295), this["handleErrorMessage"](window["cartStrings"]["error"]||"Something went wrong."); return
      }
      if(this.skipCart||id_0224)
      {
        window["location"]="/checkout"; return
      }
      else if(!this.cart)
      {
        window["location"]=window["routes"]["cart_url"]; return
      }
      this["error"]||publish(PUB_SUB_EVENTS["cartUpdate"], {
        source:"product-form", productVariantId:this["formIdInput"].id, cartData:id_0290
      }), this["error"]=!1; const id_0296=this["closest"]("quick-add-modal"); id_0296?(document["body"]["addEventListener"]("modalClosed", ()=>{
        setTimeout(()=>{
          this["cart"]["renderContents"](id_0290, this.isCartUpsell)
        })
      }, {
        once:!0
      }), id_0296["hide"](!0)):this["cart"].renderContents(id_0290, this["isCartUpsell"])
    })["catch"](id_0297=>{
      console["error"](id_0297),
      this["handleErrorMessage"](id_0297&&id_0297["message"]?id_0297["message"]:window["cartStrings"]["error"]||"Something went wrong.")
    })["finally"](()=>{
      const id_0298_clearLoading=()=>{id_0223?id_0223["classList"].remove("loading"):(this["submitButton"]["classList"]["remove"]("loading"), this["loadingSpinner"]&&this["loadingSpinner"]["classList"]["add"]("hidden")), this["additionalAtcButtons"]["forEach"](id_0298=>{
        id_0298["removeAttribute"]("disabled")
      }), this["displayedSubmitButton"]&&(this["displayedSubmitButton"]["removeAttribute"]("disabled"), this["displayedSubmitButton"].classList.remove("loading")), this["error"]||this["submitButton"]["removeAttribute"]("aria-disabled")};
      if(this["cart"]&&this["cart"]["_openAfterSync"]){document.addEventListener("cartDrawerOpened",()=>{id_0298_clearLoading()},{once:true})}else{id_0298_clearLoading()}
      this["cart"]&&syncCartDrawerVisibleEmptyState(this["cart"])
    })
  }
  handleErrorMessage(id_0299=!1)
  {
    this["errorMessageWrapper"]=this["errorMessageWrapper"]||this.querySelector(".product-form__error-message-wrapper"), this.errorMessageWrapper&&(this["errorMessage"]=this["errorMessage"]||this["errorMessageWrapper"]["querySelector"](".product-form__error-message"), this["errorMessageWrapper"]["toggleAttribute"]("hidden", !id_0299), id_0299&&(this["errorMessage"]["textContent"]=id_0299))
  }
});
function serializePropValue(id_0300)
{
  if(id_0300==null)return"";
  if(typeof id_0300==="object")try{
    return JSON["stringify"](id_0300)
  }
  catch{
    return String(id_0300)
  }
  return String(id_0300)
}
!customElements["get"]("product-info")&&customElements["define"]("product-info", class extends HTMLElement
{
  constructor()
  {
    super(), this["deferredMedia"]=document["querySelector"]("["+defMed+"]"), this["input"]=this["querySelector"](".quantity__input"), this["currentVariant"]=this["querySelector"](".product-variant-id"), this["variantSelects"]=this["querySelector"]("variant-radios"), this["submitButton"]=this["querySelector"]("[type=\"submit\"]"), this["form"]=this["querySelector"]("#ProductForm-"+this["dataset"].section), this["hasUrl"]=this["dataset"]["url"]!==null
  }
  ["cartUpdateUnsubscriber"]=void 0; variantChangeUnsubscriber=void 0; ["connectedCallback"]()
  {
    this["form"]&&(this["input"]&&(this["quantityForm"]=this["querySelector"](".product-form__quantity"), this["quantityForm"]&&(this.setQuantityBoundries(), !this["dataset"]["originalSection"]&&(this["cartUpdateUnsubscriber"]=subscribe(PUB_SUB_EVENTS["cartUpdate"], this["fetchQuantityRules"]["bind"](this))), this["variantChangeUnsubscriber"]=subscribe(PUB_SUB_EVENTS["variantChange"], id_0301=>{
      const id_0302=this["dataset"]["originalSection"]?this["dataset"]["originalSection"]:this["dataset"].section; id_0301["data"]["sectionId"]===id_0302&&(this["updateQuantityRules"](id_0301["data"]["sectionId"], id_0301["data"]["html"]), this.setQuantityBoundries())
    }))))
  }
  ["disconnectedCallback"]()
  {
    this.cartUpdateUnsubscriber&&this.cartUpdateUnsubscriber(), this["variantChangeUnsubscriber"]&&this.variantChangeUnsubscriber()
  }
  ["setQuantityBoundries"]()
  {
    const id_0303={
      cartQuantity:this["input"].dataset.cartQuantity?parseInt(this["input"].dataset["cartQuantity"]):0, min:this["input"]["dataset"]["min"]?parseInt(this.input.dataset["min"]):1, max:this["input"]["dataset"]["max"]?parseInt(this["input"]["dataset"]["max"]):null, step:this.input["step"]?parseInt(this["input"]["step"]):1
    }; let id_0304=id_0303["min"]; const id_0305=id_0303.max===null?id_0303["max"]:id_0303["max"]-id_0303["cartQuantity"]; id_0305!==null&&(id_0304=Math.min(id_0304, id_0305)), id_0303["cartQuantity"]>=id_0303.min&&(id_0304=Math["min"](id_0304, id_0303["step"])), this["input"]["min"]=id_0304, this["input"]["max"]=id_0305, this["input"].value=id_0304, publish(PUB_SUB_EVENTS["quantityUpdate"], void 0)
  }
  initShareLinks()
  {
    this["querySelector"](".share-url-button")||(this["shareUrl"]=!0)
  }
  fetchQuantityRules()
  {
    !this.currentVariant||!this.currentVariant.value||(this["querySelector"](".quantity__rules-cart .loading-overlay").classList.remove("hidden"), fetch(this["dataset"]["url"]+"?variant="+this.currentVariant.value+"&section_id="+this["dataset"]["section"]).then(id_0306=>id_0306["text"]())["then"](id_0307=>{
      const id_0308=new DOMParser()["parseFromString"](id_0307, "text/html"); this["updateQuantityRules"](this["dataset"].section, id_0308), this["setQuantityBoundries"]()
    })["catch"](id_0309=>{
      console.error(id_0309)
    })["finally"](()=>{
      this["querySelector"](".quantity__rules-cart .loading-overlay").classList["add"]("hidden")
    }))
  }
  ["updateQuantityRules"](id_0310, id_0311)
  {
    const id_0312=id_0311["getElementById"]("Quantity-Form-"+id_0310); if(id_0312&&this.quantityForm)
    {
      const id_0313=[".quantity__input", ".quantity__rules", ".quantity__label"]; for(let id_0314 of id_0313)
      {
        const id_0315=this["quantityForm"]["querySelector"](id_0314), id_0316=id_0312["querySelector"](id_0314); if(!(!id_0315||!id_0316))if(id_0314===".quantity__input")
        {
          const id_0317=["data-cart-quantity", "data-min", "data-max", "step"]; for(let id_0318 of id_0317)
          {
            const id_0319=id_0316["getAttribute"](id_0318); id_0319!==null&&id_0315["setAttribute"](id_0318, id_0319)
          }
        }
        else id_0315["innerHTML"]=id_0316["innerHTML"]
      }
    }
  }
});
function getFocusableElements(id_0320)
{
  return Array.from(id_0320.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"))
}
document.querySelectorAll("[id^=\"Details-\"] summary")["forEach"](id_0321=>{
  id_0321["setAttribute"]("role", "button"), id_0321["setAttribute"]("aria-expanded", id_0321["parentNode"].hasAttribute("open")), id_0321["nextElementSibling"]?.["getAttribute"]("id")&&id_0321.setAttribute("aria-controls", id_0321.nextElementSibling.id), id_0321["addEventListener"]("click", id_0322=>{
    id_0322["currentTarget"]["setAttribute"]("aria-expanded", !id_0322.currentTarget.closest("details")["hasAttribute"]("open"))
  }), !id_0321["closest"]("header-drawer")&&id_0321["parentElement"]["addEventListener"]("keyup", onKeyUpEscape)
});
const trapFocusHandlers={
};
function trapFocus(id_0323, id_0324=id_0323)
{
  var id_0325=getFocusableElements(id_0323),
  id_0326=id_0325[0],
  id_0327=id_0325[id_0325["length"]-1];
  removeTrapFocus(),
  trapFocusHandlers.focusin=id_0328=>{
    id_0328.target!==id_0323&&id_0328["target"]!==id_0327&&id_0328["target"]!==id_0326||document["addEventListener"]("keydown", trapFocusHandlers.keydown)
  },
  trapFocusHandlers["focusout"]=function()
  {
    document["removeEventListener"]("keydown", trapFocusHandlers["keydown"])
  },
  trapFocusHandlers.keydown=function(id_0329)
  {
    id_0329["code"]["toUpperCase"]()==="TAB"&&(id_0329["target"]===id_0327&&!id_0329["shiftKey"]&&(id_0329["preventDefault"](), id_0326["focus"]()), (id_0329["target"]===id_0323||id_0329.target===id_0326)&&id_0329.shiftKey&&(id_0329.preventDefault(), id_0327["focus"]()))
  },
  document["addEventListener"]("focusout", trapFocusHandlers["focusout"]),
  document["addEventListener"]("focusin", trapFocusHandlers["focusin"]),
  id_0324["focus"](),
  id_0324["tagName"]==="INPUT"&&["search",
  "text",
  "email",
  "url"]["includes"](id_0324["type"])&&id_0324["value"]&&id_0324.setSelectionRange(0, id_0324.value["length"])
}
function pauseAllMedia()
{
  document["querySelectorAll"](".js-youtube")["forEach"](id_0330=>{
    id_0330.contentWindow.postMessage("{\"event\":\"command\",\"func\":\""+'pauseVideo","args":""}', "*")
  }),
  document["querySelectorAll"](".js-vimeo").forEach(id_0331=>{
    id_0331["contentWindow"]["postMessage"]("{\"method\":\"pause\"}", "*")
  }),
  document.querySelectorAll("media-gallery template video")["forEach"](id_0332=>id_0332["pause"]()),
  document["querySelectorAll"]("product-model")["forEach"](id_0333=>{
    id_0333["modelViewerUI"]&&id_0333.modelViewerUI["pause"]()
  })
}
function removeTrapFocus(id_0334=null)
{
  document["removeEventListener"]("focusin", trapFocusHandlers["focusin"]),
  document["removeEventListener"]("focusout", trapFocusHandlers.focusout),
  document["removeEventListener"]("keydown", trapFocusHandlers["keydown"]),
  id_0334&&id_0334["focus"]()
}
function onKeyUpEscape(id_0335)
{
  if(id_0335["code"].toUpperCase()!=="ESCAPE")return;
  const id_0336=id_0335["target"]["closest"]("details[open]");
  if(!id_0336)return;
  const id_0337=id_0336["querySelector"]("summary");
  id_0336["removeAttribute"]("open"),
  id_0337.setAttribute("aria-expanded", !1),
  id_0337["focus"]()
}
class QuantityInput extends HTMLElement
{
  constructor()
  {
    super(),
    this["input"]=this["querySelector"]("input"),
    this["changeEvent"]=new Event("change", {
      bubbles:!0
    }),
    this.quantityGifts=document.getElementById("quantity-gifts-"+this.dataset["section"]),
    this["input"]["addEventListener"]("change", this["onInputChange"]["bind"](this)),
    this.querySelectorAll("button").forEach(id_0338=>id_0338["addEventListener"]("click", this.onButtonClick["bind"](this)))
  }
  ["quantityUpdateUnsubscriber"]=void 0;
  connectedCallback()
  {
    this.validateQtyRules(),
    this["quantityUpdateUnsubscriber"]=subscribe(PUB_SUB_EVENTS.quantityUpdate, this["validateQtyRules"]["bind"](this))
  }
  ["disconnectedCallback"]()
  {
    this.quantityUpdateUnsubscriber&&this.quantityUpdateUnsubscriber()
  }
  ["onInputChange"](id_0339)
  {
    this.validateQtyRules()
  }
  onButtonClick(id_0340)
  {
    id_0340["preventDefault"]();
    const id_0341=this["input"]["value"],
    id_0342=id_0340["currentTarget"];
    id_0342&&id_0342["name"]==="plus"?this["input"]["stepUp"]():this["input"]["stepDown"](),
    id_0341!==this.input["value"]&&this["input"]["dispatchEvent"](this["changeEvent"])
  }
  validateQtyRules()
  {
    const id_0342=parseInt(this["input"]["value"]);
    if(this["input"]["min"])
    {
      const id_0343=parseInt(this["input"]["min"]);
      this["querySelector"](".quantity__button[name='minus']")["classList"]["toggle"]("disabled", id_0342<=id_0343)
    }
    if(this["input"]["max"])
    {
      const id_0344=parseInt(this.input["max"]);
      this["querySelector"](".quantity__button[name='plus']").classList["toggle"]("disabled", id_0342>=id_0344)
    }
    this["quantityGifts"]&&this.quantityGifts.unlockGifts&&this["quantityGifts"]["unlockGifts"](id_0342)
  }
}
customElements.define("quantity-input", QuantityInput);
function debounce(id_0345, id_0346)
{
  let id_0347;
  return(...id_0348)=>{
    clearTimeout(id_0347),
    id_0347=setTimeout(()=>id_0345["apply"](this, id_0348), id_0346)
  }
}
function fetchConfig(id_0349="json")
{
  return{
    method:"POST",
    headers:{
      "Content-Type":"application/json",
      Accept:"application/"+id_0349
    }
  }
}
function buildAjaxCartUrl(id_0350, id_0351)
{
  const id_0352=id_0350||id_0351,
  id_0353=id_0352.match(/[?#].*$/),
  id_0354=id_0353?id_0353[0]:"",
  id_0355=id_0354?id_0352.slice(0, -id_0354.length):id_0352,
  id_0356=id_0355.endsWith(".js")?id_0355:id_0355+".js";
  return id_0356+id_0354
}
function addDays(id_0350, id_0351)
{
  var id_0352=new Date(id_0350);
  return id_0352.setDate(id_0352["getDate"]()+id_0351),
  id_0352
}
function formatDates(id_0353, id_0354, id_0355=27)
{
  if(!id_0353||!id_0354)return;
  const id_0356=new Date(id_0354+"T00:00:00Z"),
  id_0357=id_0356["getFullYear"](),
  id_0358=id_0356["getMonth"](),
  id_0359=id_0356["getDate"](),
  id_0360=new Date(id_0357, id_0358, id_0359),
  id_0361=id_0353-id_0360;
  return Math.ceil(id_0361/(1e3*3600*24))<=id_0355
}
typeof window.Shopify=="undefined"&&(window["Shopify"]={
}),
Shopify["bind"]=function(id_0365, id_0366)
{
  return function()
  {
    return id_0365["apply"](id_0366, arguments)
  }
},
Shopify["setSelectorByValue"]=function(id_0367, id_0368)
{
  for(var id_0369=0, id_0370=id_0367.options.length; id_0369<id_0370; id_0369++)
  {
    var id_0371=id_0367["options"][id_0369];
    if(id_0368==id_0371["value"]||id_0368==id_0371["innerHTML"])return id_0367["selectedIndex"]=id_0369,
    id_0369
  }
},
Shopify["addListener"]=function(id_0372, id_0373, id_0374)
{
  id_0372["addEventListener"]?id_0372["addEventListener"](id_0373, id_0374, !1):id_0372["attachEvent"]("on"+id_0373, id_0374)
},
Shopify["postLink"]=function(id_0375, id_0376)
{
  id_0376=id_0376||{
  };
  var id_0377=id_0376.method||"post",
  id_0378=id_0376["parameters"]||{
  },
  id_0379=document["createElement"]("form");
  id_0379["setAttribute"]("method", id_0377),
  id_0379["setAttribute"]("action", id_0375);
  for(var id_0380 in id_0378)
  {
    var id_0381=document["createElement"]("input");
    id_0381["setAttribute"]("type", "hidden"),
    id_0381["setAttribute"]("name", id_0380),
    id_0381["setAttribute"]("value", id_0378[id_0380]),
    id_0379["appendChild"](id_0381)
  }
  document["body"].appendChild(id_0379),
  id_0379.submit(),
  document["body"]["removeChild"](id_0379)
},
Shopify["CountryProvinceSelector"]=function(id_0396, id_0397, id_0398)
{
  this["countryEl"]=document["getElementById"](id_0396),
  this.provinceEl=document["getElementById"](id_0397),
  this["provinceContainer"]=document["getElementById"](id_0398["hideElement"]||id_0397),
  Shopify["addListener"](this.countryEl, "change", Shopify["bind"](this["countryHandler"], this)),
  this["initCountry"](),
  this["initProvince"]()
},
Shopify["CountryProvinceSelector"].prototype={
  initCountry:function()
  {
    var id_0399=this["countryEl"]["getAttribute"]("data-default");
    Shopify.setSelectorByValue(this["countryEl"], id_0399),
    this["countryHandler"]()
  },
  initProvince:function()
  {
    var id_0400=this["provinceEl"].getAttribute("data-default");
    id_0400&&this.provinceEl["options"].length>0&&Shopify["setSelectorByValue"](this["provinceEl"], id_0400)
  },
  countryHandler:function(id_0401)
  {
    var id_0402=this["countryEl"]["options"][this["countryEl"]["selectedIndex"]],
    id_0403=id_0402["getAttribute"]("data-provinces"),
    id_0404=JSON["parse"](id_0403);
    if(this.clearOptions(this["provinceEl"]), id_0404&&id_0404["length"]==0)this["provinceContainer"].style["display"]="none";
    else{
      for(var id_0405=0; id_0405<id_0404.length; id_0405++)
      {
        var id_0402=document.createElement("option");
        id_0402["value"]=id_0404[id_0405][0],
        id_0402["innerHTML"]=id_0404[id_0405][1],
        this["provinceEl"]["appendChild"](id_0402)
      }
      this["provinceContainer"]["style"]["display"]=""
    }
  },
  clearOptions:function(id_0406)
  {
    for(; id_0406["firstChild"]; )id_0406["removeChild"](id_0406["firstChild"])
  },
  setOptions:function(id_0407, id_0408)
  {
    for(var id_0409=0, id_0410=id_0408.length; id_0409<id_0408["length"]; id_0409++)
    {
      var id_0411=document["createElement"]("option");
      id_0411["value"]=id_0408[id_0409],
      id_0411["innerHTML"]=id_0408[id_0409],
      id_0407["appendChild"](id_0411)
    }
  }
};
class InternalVideo extends HTMLElement
{
  constructor()
  {
    super();
    this.loaded=false;
    this.suspended=false;
    this.dragging=false;
    this.dragFrame=null;
    this.pendingDragClientX=null;
    this.listenersConnected=false;
    this.observer=null;
    this.onPlayButtonClick=this.playVideo.bind(this);
    this.onSoundButtonClick=this.toggleSound.bind(this);
    this.onEnded=this.endedVideo.bind(this);
    this.onPlay=this.handlePlay.bind(this);
    this.onPause=this.syncPlaybackState.bind(this);
    this.onVolumeChange=this.syncSoundState.bind(this);
    this.onWaiting=this.showSpinner.bind(this);
    this.onCanPlay=this.hideSpinner.bind(this);
    this.onTimelineUpdate=this.updateTimeline.bind(this);
    this.onTimelineKeydown=this.handleTimelineKeydown.bind(this);
    this.onTimelineClick=this.seekVideo.bind(this);
    this.onTimelineMouseDown=this.startDrag.bind(this);
    this.onTimelineTouchStart=this.startDrag.bind(this);
    this.onDocumentMouseMove=this.drag.bind(this);
    this.onDocumentMouseUp=this.stopDrag.bind(this);
    this.onDocumentTouchMove=this.drag.bind(this);
    this.onDocumentTouchEnd=this.stopDrag.bind(this);
    this.onWebkitBeginFullscreen=()=>this.syncFullscreenState(true);
    this.onWebkitEndFullscreen=()=>this.syncFullscreenState(false);
    this.onIntersection=this.handleIntersection.bind(this)
  }
  connectedCallback()
  {
    this.cacheElements();
    if(!this.video||this.listenersConnected)return;
    this.attachListeners();
    this.syncPlaybackState();
    this.syncSoundState();
    this.syncFullscreenState();
    if(this.timeline)this.setupTimelineA11y();
    this.setupAutoplayObserver()
  }
  disconnectedCallback()
  {
    this.detachListeners();
    this.stopDrag();
    if(this.observer)
    {
      this.observer.disconnect();
      this.observer=null
    }
  }
  cacheElements()
  {
    this.playButton=this.querySelector(".internal-video__play");
    this.soundButton=this.querySelector(".internal-video__sound-btn");
    this.fullscreenButton=this.querySelector(".internal-video__fullscreen-btn");
    this.timeline=this.querySelector(".internal-video__timeline");
    this.video=this.querySelector("video");
    this.noPlayBtn=this.dataset.noPlayBtn==="true";
    const invalidFormatSrc=this.video&&this.video.querySelector('source[type="application/x-mpegURL"]');
    if(invalidFormatSrc)invalidFormatSrc.remove()
  }
  attachListeners()
  {
    if(this.listenersConnected)return;
    if(this.playButton)this.playButton.addEventListener("click",this.onPlayButtonClick);
    if(this.soundButton)this.soundButton.addEventListener("click",this.onSoundButtonClick);
    this.video.addEventListener("ended",this.onEnded);
    this.video.addEventListener("play",this.onPlay);
    this.video.addEventListener("pause",this.onPause);
    this.video.addEventListener("volumechange",this.onVolumeChange);
    this.video.addEventListener("waiting",this.onWaiting);
    this.video.addEventListener("canplaythrough",this.onCanPlay);
    this.video.addEventListener("webkitbeginfullscreen",this.onWebkitBeginFullscreen);
    this.video.addEventListener("webkitendfullscreen",this.onWebkitEndFullscreen);
    if(this.timeline)
    {
      this.video.addEventListener("timeupdate",this.onTimelineUpdate);
      this.video.addEventListener("loadedmetadata",this.onTimelineUpdate);
      this.video.addEventListener("durationchange",this.onTimelineUpdate);
      this.video.addEventListener("emptied",this.onTimelineUpdate);
      this.timeline.addEventListener("keydown",this.onTimelineKeydown);
      this.timeline.addEventListener("click",this.onTimelineClick);
      this.timeline.addEventListener("mousedown",this.onTimelineMouseDown);
      this.timeline.addEventListener("touchstart",this.onTimelineTouchStart,{passive:false})
    }
    this.listenersConnected=true
  }
  detachListeners()
  {
    if(!this.listenersConnected||!this.video)return;
    if(this.playButton)this.playButton.removeEventListener("click",this.onPlayButtonClick);
    if(this.soundButton)this.soundButton.removeEventListener("click",this.onSoundButtonClick);
    this.video.removeEventListener("ended",this.onEnded);
    this.video.removeEventListener("play",this.onPlay);
    this.video.removeEventListener("pause",this.onPause);
    this.video.removeEventListener("volumechange",this.onVolumeChange);
    this.video.removeEventListener("waiting",this.onWaiting);
    this.video.removeEventListener("canplaythrough",this.onCanPlay);
    this.video.removeEventListener("webkitbeginfullscreen",this.onWebkitBeginFullscreen);
    this.video.removeEventListener("webkitendfullscreen",this.onWebkitEndFullscreen);
    if(this.timeline)
    {
      this.video.removeEventListener("timeupdate",this.onTimelineUpdate);
      this.video.removeEventListener("loadedmetadata",this.onTimelineUpdate);
      this.video.removeEventListener("durationchange",this.onTimelineUpdate);
      this.video.removeEventListener("emptied",this.onTimelineUpdate);
      this.timeline.removeEventListener("keydown",this.onTimelineKeydown);
      this.timeline.removeEventListener("click",this.onTimelineClick);
      this.timeline.removeEventListener("mousedown",this.onTimelineMouseDown);
      this.timeline.removeEventListener("touchstart",this.onTimelineTouchStart)
    }
    this.listenersConnected=false
  }
  playVideo()
  {
    if(!this.video)return;
    if(this.video.paused||this.video.ended)
    {
      if(!this.loaded)
      {
        this.video.load();
        this.loaded=true
      }
      this.classList.add("internal-video--playing");
      const playPromise=this.video.play();
      if(playPromise!==void 0)playPromise.then(()=>this.syncPlaybackState()).catch(()=>this.syncPlaybackState())
    }
    else
    {
      this.video.pause();
      this.syncPlaybackState()
    }
  }
  handlePlay()
  {
    this.hideSpinner();
    this.syncPlaybackState()
  }
  endedVideo()
  {
    this.syncPlaybackState()
  }
  syncPlaybackState()
  {
    if(!this.video)return;
    const isPlaying=!this.video.paused&&!this.video.ended;
    this.classList.toggle("internal-video--playing",isPlaying);
    if(!this.playButton)return;
    const playLabel=this.playButton.dataset.playLabel||"Play video";
    const pauseLabel=this.playButton.dataset.pauseLabel||"Pause video";
    this.playButton.setAttribute("aria-label",isPlaying?pauseLabel:playLabel);
    this.playButton.setAttribute("aria-pressed",String(isPlaying));
    if(this.noPlayBtn)this.playButton.style.visibility=isPlaying?"hidden":"visible"
  }
  toggleSound()
  {
    if(!this.video)return;
    this.video.muted=!this.video.muted;
    this.syncSoundState()
  }
  syncSoundState()
  {
    if(!this.video)return;
    const isMuted=this.video.muted;
    this.classList.toggle("internal-video--muted",isMuted);
    if(!this.soundButton)return;
    const muteLabel=this.soundButton.dataset.muteLabel||"Mute video";
    const unmuteLabel=this.soundButton.dataset.unmuteLabel||"Unmute video";
    this.soundButton.setAttribute("aria-label",isMuted?unmuteLabel:muteLabel);
    this.soundButton.setAttribute("aria-pressed",String(!isMuted))
  }
  syncFullscreenState(forcedState)
  {
    const nativeFullscreen=document.fullscreenElement===this||this.video&&this.video.webkitDisplayingFullscreen===true;
    const isFullscreen=typeof forcedState==="boolean"?forcedState:this.classList.contains("internal-video--modal-open")||nativeFullscreen;
    this.classList.toggle("internal-video--fullscreen",isFullscreen);
    if(!this.fullscreenButton)return;
    const enterLabel=this.fullscreenButton.dataset.enterFullscreenLabel||"Enter fullscreen";
    const exitLabel=this.fullscreenButton.dataset.exitFullscreenLabel||"Exit fullscreen";
    this.fullscreenButton.setAttribute("aria-label",isFullscreen?exitLabel:enterLabel);
    this.fullscreenButton.setAttribute("aria-pressed",String(isFullscreen))
  }
  setupTimelineA11y()
  {
    this.timeline.hasAttribute("role")||this.timeline.setAttribute("role","slider");
    this.timeline.hasAttribute("tabindex")||(this.timeline.tabIndex=0);
    this.timeline.hasAttribute("aria-label")||this.timeline.hasAttribute("aria-labelledby")||this.timeline.setAttribute("aria-label",this.timeline.dataset.label||"Video progress");
    this.timeline.setAttribute("aria-orientation","horizontal");
    this.timeline.setAttribute("aria-valuemin","0");
    this.updateTimeline()
  }
  getVideoDuration()
  {
    const duration=Number(this.video.duration);
    return Number.isFinite(duration)&&duration>0?duration:0
  }
  formatTimelineTime(value)
  {
    const seconds=Math.max(0,Math.round(value));
    const hours=Math.floor(seconds/3600);
    const minutes=Math.floor(seconds%3600/60);
    const remainingSeconds=seconds%60;
    const paddedSeconds=String(remainingSeconds).padStart(2,"0");
    return hours>0?hours+":"+String(minutes).padStart(2,"0")+":"+paddedSeconds:minutes+":"+paddedSeconds
  }
  syncTimelineA11y(duration,currentTime)
  {
    const roundedDuration=Math.round(duration*1000)/1000;
    const roundedCurrent=Math.min(Math.round(currentTime*1000)/1000,roundedDuration);
    this.timeline.setAttribute("aria-valuemax",String(roundedDuration));
    this.timeline.setAttribute("aria-valuenow",String(roundedCurrent));
    this.timeline.setAttribute("aria-valuetext",this.formatTimelineTime(roundedCurrent)+" / "+this.formatTimelineTime(roundedDuration));
    if(roundedDuration)this.timeline.removeAttribute("aria-disabled");
    else this.timeline.setAttribute("aria-disabled","true")
  }
  setCurrentTime(value)
  {
    const duration=this.getVideoDuration();
    if(!duration)return;
    const nextTime=Math.min(duration,Math.max(0,Number(value)||0));
    try
    {
      this.video.currentTime=nextTime
    }
    catch{}
    this.updateTimeline()
  }
  handleTimelineKeydown(event)
  {
    const duration=this.getVideoDuration();
    if(!duration)return;
    const isRtl=getComputedStyle(this.timeline).direction==="rtl";
    const step=5;
    let nextTime;
    if(event.key==="Home")nextTime=0;
    else if(event.key==="End")nextTime=duration;
    else if(event.key==="ArrowLeft")nextTime=this.video.currentTime+(isRtl?step:-step);
    else if(event.key==="ArrowRight")nextTime=this.video.currentTime+(isRtl?-step:step);
    else if(event.key==="ArrowUp"||event.key==="PageUp")nextTime=this.video.currentTime+step;
    else if(event.key==="ArrowDown"||event.key==="PageDown")nextTime=this.video.currentTime-step;
    else return;
    event.preventDefault();
    this.setCurrentTime(nextTime)
  }
  updateTimeline()
  {
    if(!this.timeline||!this.video)return;
    const duration=this.getVideoDuration();
    const currentTime=duration?Math.min(duration,Math.max(0,Number(this.video.currentTime)||0)):0;
    const completed=duration?currentTime/duration*100:0;
    this.style.setProperty("--completed",completed+"%");
    this.syncTimelineA11y(duration,currentTime)
  }
  startDrag(event)
  {
    if(!this.timeline)return;
    if(event.type==="mousedown"&&event.button!==0)return;
    this.timeline.focus({preventScroll:true});
    if(event.cancelable)event.preventDefault();
    this.dragging=true;
    document.addEventListener("mousemove",this.onDocumentMouseMove);
    document.addEventListener("mouseup",this.onDocumentMouseUp);
    document.addEventListener("touchmove",this.onDocumentTouchMove,{passive:false});
    document.addEventListener("touchend",this.onDocumentTouchEnd);
    document.addEventListener("touchcancel",this.onDocumentTouchEnd);
    this.drag(event)
  }
  stopDrag()
  {
    this.dragging=false;
    if(this.dragFrame!==null)
    {
      cancelAnimationFrame(this.dragFrame);
      this.dragFrame=null
    }
    this.pendingDragClientX=null;
    document.removeEventListener("mousemove",this.onDocumentMouseMove);
    document.removeEventListener("mouseup",this.onDocumentMouseUp);
    document.removeEventListener("touchmove",this.onDocumentTouchMove);
    document.removeEventListener("touchend",this.onDocumentTouchEnd);
    document.removeEventListener("touchcancel",this.onDocumentTouchEnd)
  }
  drag(event)
  {
    if(!this.dragging)return;
    if(event.cancelable)event.preventDefault();
    const pointer=event.touches&&event.touches.length?event.touches[0]:event.changedTouches&&event.changedTouches.length?event.changedTouches[0]:event;
    this.pendingDragClientX=pointer.clientX;
    if(this.dragFrame!==null)return;
    this.dragFrame=requestAnimationFrame(()=>{
      this.dragFrame=null;
      if(!this.dragging||this.pendingDragClientX===null)return;
      this.seekVideo({clientX:this.pendingDragClientX})
    })
  }
  seekVideo(event)
  {
    if(!this.timeline)return;
    const bounds=this.timeline.getBoundingClientRect();
    if(!bounds.width)return;
    let ratio=Math.min(1,Math.max(0,(event.clientX-bounds.left)/bounds.width));
    if(getComputedStyle(this.timeline).direction==="rtl")ratio=1-ratio;
    this.setCurrentTime(ratio*this.getVideoDuration())
  }
  showSpinner()
  {
    this.classList.add("internal-video--loading")
  }
  hideSpinner()
  {
    this.classList.remove("internal-video--loading")
  }
  setupAutoplayObserver()
  {
    if(this.dataset.autoplay!=="true"||this.loaded||!("IntersectionObserver"in window))return;
    this.observer=new IntersectionObserver(this.onIntersection,{root:null,rootMargin:"0px",threshold:.05});
    this.observer.observe(this)
  }
  handleIntersection(entries)
  {
    entries.forEach(entry=>{
      if(!entry.isIntersecting)return;
      for(const source of this.video.querySelectorAll("source[data-src]"))
      {
        source.setAttribute("src",source.getAttribute("data-src"));
        source.removeAttribute("data-src")
      }
      this.loaded=true;
      this.video.load();
      const playPromise=this.video.play();
      if(playPromise!==void 0)playPromise.catch(()=>this.syncPlaybackState());
      if(this.observer)
      {
        this.observer.disconnect();
        this.observer=null
      }
    })
  }
}
customElements.define("internal-video",InternalVideo);
const internalVideoModalRegistry=new WeakMap;
function openInternalVideoModal(internalVideo)
{
  const openVideo=document.querySelector("internal-video.internal-video--modal-open");
  if(openVideo&&openVideo!==internalVideo)closeInternalVideoModal(openVideo);
  if(internalVideoModalRegistry.has(internalVideo)||!internalVideo.parentNode)return;
  const placeholder=document.createComment("internal-video-modal-placeholder");
  const overlay=document.createElement("div");
  const panel=document.createElement("div");
  const internalVideoStyles=getComputedStyle(internalVideo);
  const modalRadius=internalVideoStyles.getPropertyValue("--internal-video-modal-radius").trim()||internalVideoStyles.borderRadius;
  const returnFocus=document.activeElement instanceof HTMLElement?document.activeElement:null;
  const fullscreenButton=internalVideo.querySelector(".internal-video__fullscreen-btn");
  overlay.className="internal-video-modal";
  overlay.setAttribute("role","dialog");
  overlay.setAttribute("aria-modal","true");
  overlay.setAttribute("aria-label",internalVideo.dataset.dialogLabel||fullscreenButton&&fullscreenButton.dataset.dialogLabel||"Video player");
  panel.className="internal-video-modal__panel";
  if(modalRadius)panel.style.setProperty("--internal-video-modal-radius",modalRadius);
  internalVideo.parentNode.insertBefore(placeholder,internalVideo);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  panel.appendChild(internalVideo);
  internalVideo.classList.add("internal-video--modal-open");
  internalVideo.syncFullscreenState(true);
  document.documentElement.classList.add("internal-video-modal-open");
  document.body.classList.add("internal-video-modal-open");
  const closeOnOverlayClick=event=>{
    if(event.target===overlay)closeInternalVideoModal(internalVideo)
  };
  overlay.addEventListener("click",closeOnOverlayClick);
  internalVideoModalRegistry.set(internalVideo,{placeholder,overlay,closeOnOverlayClick,returnFocus});
  if(fullscreenButton)trapFocus(overlay,fullscreenButton);
  else overlay.setAttribute("tabindex","-1"),overlay.focus()
}
function closeInternalVideoModal(internalVideo)
{
  const state=internalVideoModalRegistry.get(internalVideo);
  if(!state)return;
  if(state.overlay)state.overlay.removeEventListener("click",state.closeOnOverlayClick);
  removeTrapFocus();
  if(state.placeholder&&state.placeholder.parentNode)state.placeholder.parentNode.insertBefore(internalVideo,state.placeholder);
  else document.body.appendChild(internalVideo);
  if(state.placeholder&&state.placeholder.parentNode)state.placeholder.parentNode.removeChild(state.placeholder);
  if(state.overlay)state.overlay.remove();
  internalVideo.classList.remove("internal-video--modal-open");
  internalVideo.syncFullscreenState(false);
  internalVideoModalRegistry.delete(internalVideo);
  if(!document.querySelector("internal-video.internal-video--modal-open"))
  {
    document.documentElement.classList.remove("internal-video-modal-open");
    document.body.classList.remove("internal-video-modal-open")
  }
  const focusTarget=state.returnFocus&&state.returnFocus.isConnected?state.returnFocus:internalVideo.querySelector(".internal-video__fullscreen-btn");
  if(focusTarget)focusTarget.focus({preventScroll:true})
}
function toggleInternalVideoFullscreen(internalVideo)
{
  if(document.fullscreenElement===internalVideo)
  {
    const exitPromise=document.exitFullscreen&&document.exitFullscreen();
    if(exitPromise&&typeof exitPromise.catch==="function")exitPromise.catch(()=>internalVideo.syncFullscreenState());
    return
  }
  if(internalVideo.requestFullscreen)
  {
    const requestPromise=internalVideo.requestFullscreen();
    if(requestPromise&&typeof requestPromise.catch==="function")requestPromise.catch(()=>internalVideo.syncFullscreenState());
    return
  }
  const video=internalVideo.querySelector("video");
  if(video&&video.webkitEnterFullscreen)video.webkitEnterFullscreen()
}
document.addEventListener("click",event=>{
  const button=event.target.closest(".internal-video__fullscreen-btn");
  const internalVideo=button?button.closest("internal-video"):event.target.closest("internal-video");
  if(!internalVideo)return;
  if(!button)
  {
    if(internalVideo.dataset.fullscreenMode!=="modal")return;
    const skippedControl=event.target.closest(".internal-video__sound-btn,.internal-video__timeline,a,input,select,textarea,label,summary");
    if(skippedControl||internalVideoModalRegistry.has(internalVideo)||!internalVideo.querySelector(".internal-video__fullscreen-btn"))return;
    event.preventDefault();
    event.stopPropagation();
    openInternalVideoModal(internalVideo);
    return
  }
  event.preventDefault();
  event.stopPropagation();
  if(internalVideo.dataset.fullscreenMode==="modal")
  {
    if(internalVideoModalRegistry.has(internalVideo))closeInternalVideoModal(internalVideo);
    else openInternalVideoModal(internalVideo)
  }
  else toggleInternalVideoFullscreen(internalVideo)
});
document.addEventListener("fullscreenchange",()=>{
  document.querySelectorAll("internal-video").forEach(internalVideo=>internalVideo.syncFullscreenState())
});
document.addEventListener("keydown",event=>{
  if(event.key!=="Escape")return;
  const openVideo=document.querySelector("internal-video.internal-video--modal-open");
  if(!openVideo)return;
  event.preventDefault();
  closeInternalVideoModal(openVideo)
});
class ComparisonSlider extends HTMLElement
{
  constructor()
  {
    super(),
    this.sliderOverlay=this["querySelector"](".comparison-slider__overlay"),
    this["sliderLine"]=this["querySelector"](".comparison-slider__line"),
    this["sliderInput"]=this["querySelector"](".comparison-slider__input"),
    this["sliderInput"]["addEventListener"]("input", this["handleChange"].bind(this))
  }
  ["handleChange"](id_0427)
  {
    const id_0428=id_0427.currentTarget["value"];
    this.sliderOverlay.style.width=id_0428+"%",
    this["sliderLine"].style["left"]=id_0428+"%",
    this["style"]["setProperty"]("--position", id_0428+"%")
  }
}
customElements["define"]("comparison-slider", ComparisonSlider);
class PromoPopup extends HTMLElement
{
  constructor()
  {
    super();
    this.boundClick=this.handleClick.bind(this);
    this.boundKeydown=this.handleKeydown.bind(this)
  }
  connectedCallback()
  {
    if(this.controllerConnected)return;
    this.controllerConnected=true;
    this.testMode=this.dataset.testMode==="true";
    this.secondsDelay=this.dataset.delaySeconds;
    this.daysFrequency=this.dataset.delayDays;
    this.modal=this.querySelector(".sign-up-popup-modal");
    this.timer=this.querySelector(".popup-modal__timer");
    this.timerDuration=this.dataset.timerDuration;
    this.timerKey=this.dataset.timerKey;
    this.minutesSpan=this.querySelector(".popup-modal__timer__minutes");
    this.secondsSpan=this.querySelector(".popup-modal__timer__seconds");
    this.timerConnectAttempts=0;
    this.timerConnectRetry=null;
    this.timerRegistrationId=null;
    this.timerExpired=false;
    this.overlay=this.querySelector(".sign-up-popup-overlay");
    this.storageKey="promo-bar-data-"+window.location.host;
    this.addEventListener("click", this.boundClick);
    document.addEventListener("keydown", this.boundKeydown);
    this.connectTimer();
    if(!this.modal)return;
    if(this.modal.classList.contains("popup-modal--active"))
    {
      this.openPopup(false);
      return
    }
    this.setOpenState(false);
    if(this.testMode)
    {
      this.timer&&this.displayPromoTimer();
      return
    }
    this.scheduleStoredPopup()
  }
  scheduleStoredPopup()
  {
    let storedValue=null;
    try
    {
      storedValue=localStorage.getItem(this.storageKey)
    }
    catch(error)
    {
      storedValue=null
    }
    if(storedValue===null)
    {
      this.openPopupModal();
      return
    }
    try
    {
      const storedData=JSON.parse(storedValue),
      nextDisplayDate=new Date(storedData.next_display_date);
      if(currentDate.getTime()>nextDisplayDate.getTime())this.openPopupModal()
    }
    catch(error)
    {
      this.openPopupModal()
    }
  }
  openPopupModal()
  {
    this.clearOpenTimeout();
    const requestedDelay=parseInt(this.secondsDelay)*1e3+3e3,
    delay=Number.isFinite(requestedDelay)?Math.max(0, requestedDelay):0;
    this.openTimeout=setTimeout(()=>{
      this.openTimeout=null;
      this.openPopup(true)
    }, delay)
  }
  openPopup(persistDisplay)
  {
    if(!this.modal||!this.isConnected)return;
    this.beginModalSession();
    this.setOpenState(true);
    if(persistDisplay)this.persistNextDisplay();
    this.timer&&this.displayPromoTimer();
    this.focusEntryControl()
  }
  persistNextDisplay()
  {
    const nextDisplayDate=addDays(currentDate, parseInt(this.daysFrequency)),
    storageData={next_display_date:nextDisplayDate, dismissed:false};
    try
    {
      localStorage.setItem(this.storageKey, JSON.stringify(storageData))
    }
    catch(error)
    {
      return
    }
  }
  clearOpenTimeout()
  {
    if(!this.openTimeout)return;
    clearTimeout(this.openTimeout);
    this.openTimeout=null
  }
  displayPromoTimer()
  {
    this.connectTimer()
  }
  connectTimer()
  {
    if(!this.timer||this.timerRegistrationId||this.timerExpired)return;
    const durationMinutes=Number.parseFloat(this.timerDuration);
    if(!Number.isFinite(durationMinutes)||durationMinutes<=0)return;
    const engine=window.sessionCountdownEngine;
    if(!engine||typeof engine.register!=="function")
    {
      if(this.timerConnectAttempts>=20)return;
      this.timerConnectAttempts+=1;
      clearTimeout(this.timerConnectRetry);
      this.timerConnectRetry=setTimeout(()=>{
        this.timerConnectRetry=null;
        if(this.isConnected)this.connectTimer()
      }, 50);
      return
    }
    clearTimeout(this.timerConnectRetry);
    this.timerConnectRetry=null;
    const maximumSafeDurationSeconds=Math.max(1, Math.floor((Number.MAX_SAFE_INTEGER-Date.now())/1000)),
    requestedDurationSeconds=durationMinutes*60,
    durationSeconds=Math.min(Number.isFinite(requestedDurationSeconds)?Math.max(1, Math.round(requestedDurationSeconds)):maximumSafeDurationSeconds, maximumSafeDurationSeconds),
    timerKey=this.timerKey||"promo-popup-legacy:"+(this.dataset.sectionId||"unknown")+":"+durationMinutes,
    registrationId=engine.register(timerKey, durationSeconds, remainingSeconds=>this.updateTimer(remainingSeconds), ()=>{
      this.timerExpired=true;
      this.timerRegistrationId=null;
      this.updateTimer(0);
      this.timer.style.display="none"
    });
    if(!this.timerExpired)this.timerRegistrationId=registrationId
  }
  disconnectTimer()
  {
    clearTimeout(this.timerConnectRetry);
    this.timerConnectRetry=null;
    const engine=window.sessionCountdownEngine;
    if(this.timerRegistrationId&&engine&&typeof engine.unregister==="function")engine.unregister(this.timerRegistrationId);
    this.timerRegistrationId=null
  }
  updateTimer(totalSeconds)
  {
    if(!this.minutesSpan||!this.secondsSpan)return;
    const safeSeconds=Math.max(0, Math.floor(Number(totalSeconds)||0)),
    minutes=Math.floor(safeSeconds/60).toString().padStart(2, "0"),
    seconds=Math.floor(safeSeconds%60).toString().padStart(2, "0");
    this.minutesSpan.textContent=minutes,
    this.secondsSpan.textContent=seconds
  }
  setOpenState(open)
  {
    if(this.overlay)
    {
      this.overlay.classList.toggle("popup-overlay--active", open&&this.isOverlayEnabled());
      this.overlay.setAttribute("aria-hidden", "true")
    }
    if(!this.modal)return;
    this.modal.classList.toggle("popup-modal--active", open);
    this.modal.setAttribute("aria-hidden", open?"false":"true");
    this.modal.toggleAttribute("inert", !open)
  }
  isOverlayEnabled()
  {
    const setting=this.dataset.showOverlay!==undefined?this.dataset.showOverlay:this.dataset.displayOverlay!==undefined?this.dataset.displayOverlay:this.dataset.overlay;
    return setting!=="false"
  }
  handleClick(event)
  {
    if(!(event.target instanceof Element))return;
    if(event.target===this.overlay)
    {
      if(this.isOverlayEnabled())this.closeModal(true);
      return
    }
    const closeControl=event.target.closest(".promp-popup__close-btn");
    if(closeControl&&this.contains(closeControl))this.closeModal(true)
  }
  handleKeydown(event)
  {
    if(!this.modal||!this.modal.classList.contains("popup-modal--active"))return;
    if(event.key==="Tab")
    {
      this.trapFocus(event);
      return
    }
    if(event.key!=="Escape")return;
    event.preventDefault();
    this.closeModal(true)
  }
  getFocusableControls()
  {
    if(!this.modal)return[];
    const selector=[
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled]):not([type=\"hidden\"])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      "[tabindex]:not([tabindex=\"-1\"])"
    ].join(",");
    return Array.from(this.modal.querySelectorAll(selector)).filter(control=>!control.closest("[inert]")&&control.getClientRects().length>0)
  }
  trapFocus(event)
  {
    const controls=this.getFocusableControls();
    if(controls.length===0)
    {
      event.preventDefault();
      this.modal.focus();
      return
    }
    const firstControl=controls[0],
    lastControl=controls[controls.length-1];
    if(!this.modal.contains(document.activeElement))
    {
      event.preventDefault();
      firstControl.focus()
    }
    else if(event.shiftKey&&document.activeElement===firstControl)
    {
      event.preventDefault();
      lastControl.focus()
    }
    else if(!event.shiftKey&&document.activeElement===lastControl)
    {
      event.preventDefault();
      firstControl.focus()
    }
  }
  focusEntryControl()
  {
    if(!this.modal)return;
    const control=this.modal.querySelector("[autofocus],.promo-popup-legacy__close")||this.getFocusableControls()[0]||this.modal;
    clearTimeout(this.focusTimer);
    this.focusTimer=null;
    let attempts=0;
    const focusControl=()=>{
      this.focusTimer=null;
      if(!this.modal.classList.contains("popup-modal--active")||this.modal.contains(document.activeElement))return;
      control.focus({preventScroll:true});
      attempts+=1;
      if(document.activeElement!==control&&attempts<8)this.focusTimer=setTimeout(focusControl, 25)
    };
    focusControl()
  }
  beginModalSession()
  {
    if(this.modalSessionActive)return;
    this.modalSessionActive=true;
    if(this.focusRestoreFrame)
    {
      cancelAnimationFrame(this.focusRestoreFrame);
      this.focusRestoreFrame=null
    }
    this.previouslyFocusedElement=document.activeElement instanceof HTMLElement?document.activeElement:null;
    if(!document.body)return;
    this.bodyStyleSnapshot={
      overflow:document.body.style.overflow,
      paddingRight:document.body.style.paddingRight
    };
    const scrollbarWidth=Math.max(0, window.innerWidth-document.documentElement.clientWidth);
    if(scrollbarWidth>0)
    {
      const currentPadding=parseFloat(window.getComputedStyle(document.body).paddingRight)||0;
      document.body.style.paddingRight=currentPadding+scrollbarWidth+"px"
    }
    document.body.style.overflow="hidden"
  }
  endModalSession(restoreFocus)
  {
    if(!this.modalSessionActive)return;
    this.modalSessionActive=false;
    if(document.body&&this.bodyStyleSnapshot)
    {
      document.body.style.overflow=this.bodyStyleSnapshot.overflow;
      document.body.style.paddingRight=this.bodyStyleSnapshot.paddingRight
    }
    this.bodyStyleSnapshot=null;
    const focusTarget=this.previouslyFocusedElement;
    this.previouslyFocusedElement=null;
    if(restoreFocus&&focusTarget&&focusTarget.isConnected)
    {
      this.focusRestoreFrame=requestAnimationFrame(()=>{
        this.focusRestoreFrame=null;
        focusTarget.focus()
      })
    }
  }
  closeModal(restoreFocus=true)
  {
    this.clearOpenTimeout();
    clearTimeout(this.focusTimer);
    this.focusTimer=null;
    this.setOpenState(false);
    this.endModalSession(restoreFocus)
  }
  disconnectedCallback()
  {
    if(!this.controllerConnected)return;
    this.removeEventListener("click", this.boundClick);
    document.removeEventListener("keydown", this.boundKeydown);
    this.disconnectTimer();
    this.closeModal(false);
    if(this.focusRestoreFrame)
    {
      cancelAnimationFrame(this.focusRestoreFrame);
      this.focusRestoreFrame=null
    }
    this.controllerConnected=false
  }
}
customElements.define("promo-popup", PromoPopup);
class SectionsGroup extends HTMLElement
{
  constructor()
  {
    super(),
    this["sectionOneContainer"]=this["querySelector"](".section-group__section-one-container"),
    this["sectionTwoContainer"]=this.querySelector(".section-group__section-two-container"),
    this.transferSections()
  }
  ["transferSections"]()
  {
    this["sectionOne"]=document.querySelector(this["dataset"]["sectionOneId"]+" .content-for-grouping"),
    this["sectionTwo"]=document["querySelector"](this.dataset.sectionTwoId+" .content-for-grouping"),
    this["sectionOne"]&&!this.sectionOneContainer["childNodes"].length&&this.sectionOneContainer["appendChild"](this.sectionOne),
    this["sectionTwo"]&&!this["sectionTwoContainer"]["childNodes"]["length"]&&this["sectionTwoContainer"]["appendChild"](this["sectionTwo"])
  }
}
customElements.define("section-group", SectionsGroup);
class ClickableDiscount extends HTMLElement
{
  constructor()
  {
    super(),
    this["button"]=this["querySelector"](".clickable-discount__btn"),
    this["button"]["addEventListener"]("click", this.handleClick["bind"](this)),
    this["dataset"]["applied"]==="true"?this["handleClick"]():this["reapplyDiscountIfApplicable"]()
  }
  ["handleClick"]()
  {
    this["dataset"]["loading"]="true",
    this.button["disabled"]=!0,
    this["dataset"]["error"]="false",
    fetch("/discount/"+this.dataset["code"])["then"](id_0440=>{
      if(!id_0440.ok)throw new Error("Error"); this["dataset"]["applied"]="true", sessionStorage["setItem"]("discount-"+this["dataset"]["code"]+"-applied", "true")
    }).catch(id_0441=>{
      this["dataset"]["error"]="true", this["button"]["disabled"]=!1
    })["finally"](()=>{
      this.dataset["loading"]="false"
    })
  }
  ["reapplyDiscountIfApplicable"]()
  {
    const id_0442=this["dataset"].code;
    sessionStorage["getItem"]("discount-"+id_0442+"-applied")&&(this.dataset["applied"]="true", this["button"].disabled=!0, setTimeout(()=>{
      fetch("/discount/"+id_0442)["catch"](id_0443=>{
        this["dataset"]["applied"]="false", this["button"].disabled=!1
      })
    }, 3e3))
  }
}
customElements["define"]("clickable-discount", ClickableDiscount);
class DynamicDates extends HTMLElement
{
  constructor()
  {
    super(),
    this["dateFormat"]=this["dataset"]["dateFormat"],
    this["days"]=this["rearrangeDays"](this["dataset"]["dayLabels"].split(",")),
    this["months"]=this["dataset"].monthLabels["split"](","),
    this["elementsToChange"]=this["querySelectorAll"]("[data-dynamic-date=\"true\"]"),
    this.daysToSkip=this["parseDaysToSkip"](this["dataset"].daysToSkip||""),
    this.datesToSkip=this["parseDatesToSkip"](this.dataset.datesToSkip||""),
    this.insertDates()
  }
  addDaysSkipping(id_0445, id_0446)
  {
    let id_0447=new Date(id_0445),
    id_0448=Number(id_0446)||0;
    for(; id_0448>0; )id_0447=addDays(id_0447, 1),
    this["isSkipped"](id_0447)||id_0448--;
    return id_0447
  }
  ["parseDaysToSkip"](id_0449)
  {
    const id_0450=new Map([["sunday", 0], ["monday", 1], ["tuesday", 2], ["wednesday", 3], ["thursday", 4], ["friday", 5], ["saturday", 6]]),
    id_0451=new Set;
    return id_0449&&id_0449["split"](",")["forEach"](id_0452=>{
      const id_0453=id_0452.trim(); id_0450["has"](id_0453)&&id_0451.add(id_0450["get"](id_0453))
    }),
    id_0451
  }
  parseDatesToSkip(id_0454)
  {
    const id_0455=new Set;
    return id_0454&&id_0454.split(",")["forEach"](id_0456=>{
      const id_0457=id_0456["trim"](); if(!id_0457)return; const id_0458=id_0457.split("-"); if(id_0458.length===2)
      {
        const id_0459=String(Math["max"](1, Math.min(12, Number(id_0458[0]))))["padStart"](2, "0"), id_0460=String(Math.max(1, Math["min"](31, Number(id_0458[1]))))["padStart"](2, "0"); id_0455["add"](id_0459+"-"+id_0460)
      }
      else if(id_0458["length"]===3)
      {
        const id_0461=String(Math["max"](1, Math["min"](12, Number(id_0458[1]))))["padStart"](2, "0"), id_0462=String(Math["max"](1, Math.min(31, Number(id_0458[2]))))["padStart"](2, "0"); id_0455["add"](id_0461+"-"+id_0462)
      }
    }),
    id_0455
  }
  ["isSkipped"](id_0463)
  {
    const id_0464=this["daysToSkip"].has(id_0463["getDay"]()),
    id_0465=String(id_0463["getMonth"]()+1)["padStart"](2, "0"),
    id_0466=String(id_0463.getDate()).padStart(2, "0"),
    id_0467=this.datesToSkip["has"](id_0465+"-"+id_0466);
    return id_0464||id_0467
  }
  ["nextValidDate"](id_0468)
  {
    let id_0469=new Date(id_0468);
    for(; this["isSkipped"](id_0469); )id_0469=addDays(id_0469, 1);
    return id_0469
  }
  ["insertDates"]()
  {
    this["elementsToChange"].forEach(id_0470=>{
      const id_0471=id_0470["dataset"]["text"], id_0472=parseInt(id_0470["dataset"]["minDays"]), id_0473=parseInt(id_0470.dataset["maxDays"]), id_0474=this.addDaysSkipping(currentDate, id_0472), id_0475=this.addDaysSkipping(currentDate, id_0473); let id_0476="th"; const id_0477=id_0474["getDate"](); [1, 21, 31].includes(id_0477)?id_0476="st":[2, 22]["includes"](id_0477)?id_0476="nd":[3, 23]["includes"](id_0477)&&(id_0476="rd"); let id_0478="th"; const id_0479=id_0475.getDate(); [1, 21, 31]["includes"](id_0479)?id_0478="st":[2, 22].includes(id_0479)?id_0478="nd":[3, 23]["includes"](id_0479)&&(id_0478="rd"); let id_0480, id_0481; if(this["dateFormat"]==="day_dd_mm")id_0480=this["days"][id_0474["getDay"]()]+", "+id_0474["getDate"]()+". "+this["months"][id_0474["getMonth"]()], id_0481=this["days"][id_0475["getDay"]()]+", "+id_0475["getDate"]()+". "+this.months[id_0475["getMonth"]()]; else if(this.dateFormat==="mm_dd")id_0480=this["months"][id_0474["getMonth"]()]+" "+id_0474.getDate()+id_0476, id_0481=this["months"][id_0475.getMonth()]+" "+id_0475["getDate"]()+id_0478; else if(this["dateFormat"]==="dd_mm")id_0480=id_0474["getDate"]()+". "+this["months"][id_0474["getMonth"]()], id_0481=id_0475["getDate"]()+". "+this["months"][id_0475["getMonth"]()]; else if(this["dateFormat"]==="dd_mm_no_dot")id_0480=id_0474["getDate"]()+" "+this["months"][id_0474["getMonth"]()], id_0481=id_0475["getDate"]()+" "+this["months"][id_0475["getMonth"]()]; else if(this.dateFormat==="day_dd_mm_numeric")
      {
        const id_0482=String(id_0474.getDate())["padStart"](2, "0"), id_0483=String(id_0474["getMonth"]()+1)["padStart"](2, "0"); id_0480=this["days"][id_0474.getDay()]+", "+id_0482+". "+id_0483+"."; const id_0484=String(id_0475["getDate"]())["padStart"](2, "0"), id_0485=String(id_0475["getMonth"]()+1)["padStart"](2, "0"); id_0481=this["days"][id_0475.getDay()]+", "+id_0484+". "+id_0485+"."
      }
      else if(this["dateFormat"]==="dd_mm_numeric")
      {
        const id_0486=String(id_0474["getDate"]()).padStart(2, "0"), id_0487=String(id_0474["getMonth"]()+1)["padStart"](2, "0"); id_0480=id_0486+". "+id_0487+"."; const id_0488=String(id_0475["getDate"]()).padStart(2, "0"), id_0489=String(id_0475["getMonth"]()+1)["padStart"](2, "0"); id_0481=id_0488+". "+id_0489+"."
      }
      else id_0480=this["days"][id_0474.getDay()]+", "+this["months"][id_0474["getMonth"]()]+" "+id_0474.getDate()+id_0476, id_0481=this["days"][id_0475["getDay"]()]+", "+this["months"][id_0475.getMonth()]+" "+id_0475["getDate"]()+id_0478; const id_0490=id_0471.replace("[start_date]", id_0480), id_0491=id_0490["replace"]("[end_date]", id_0481); id_0470.innerHTML=id_0491
    })
  }
  rearrangeDays(id_0492)
  {
    return id_0492.unshift(id_0492[6]),
    id_0492["length"]=7,
    id_0492
  }
}
customElements.define("dynamic-dates", DynamicDates);
function refreshSectionOwnedElements()
{
  document.querySelectorAll("section-group").forEach(sectionGroup=>sectionGroup.transferSections()),
  document.querySelectorAll("dynamic-dates").forEach(dynamicDates=>dynamicDates.insertDates())
}
document.addEventListener("shopify:section:load", refreshSectionOwnedElements);
class StickyAtc extends HTMLElement {
  constructor() {
    super();
    this.displayMode = this.dataset.displayMode || (this.dataset.afterScroll === "true" ? "after_scroll" : "always");
    this.displayThreshold = Number(this.dataset.displayThreshold || 0);
    this.isScrollBtn = this.dataset.scrollBtn === "true";
    this.handleVisibility = this.handleVisibility.bind(this);
    this.scheduleVisibility = this.scheduleVisibility.bind(this);
    this.handleScrollBtn = this.handleScrollBtn.bind(this);
    this.listenersConnected = false;
    this.visibilityFrame = null
  }

  connectedCallback() {
    if (this.listenersConnected) return;
    this.mainAtcBtn = document.querySelector("#ProductSubmitButton-" + this.dataset.section);
    this.floatingBtns = document.querySelectorAll(".floating-btn");
    this.scrollBtn = this.isScrollBtn ? this.querySelector(".sticky-atc__scroll-btn") : null;
    this.scrollDestination = this.scrollBtn && this.dataset.scrollDestination
      ? document.querySelector("" + this.dataset.scrollDestination.replace("id", this.dataset.section))
      : null;
    this.footerSpacing();
    this.handleVisibility();
    document.addEventListener("scroll", this.scheduleVisibility, { passive: true });
    window.addEventListener("resize", this.scheduleVisibility);
    if (this.scrollBtn && this.scrollDestination) {
      this.scrollBtn.addEventListener("click", this.handleScrollBtn);
    }
    this.listenersConnected = true
  }

  disconnectedCallback() {
    if (!this.listenersConnected) return;
    document.removeEventListener("scroll", this.scheduleVisibility);
    window.removeEventListener("resize", this.scheduleVisibility);
    if (this.scrollBtn) this.scrollBtn.removeEventListener("click", this.handleScrollBtn);
    if (this.visibilityFrame !== null) {
      cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = null
    }
    this.listenersConnected = false;
    const nextStickyAtc = document.querySelector("sticky-atc");
    if (nextStickyAtc && nextStickyAtc !== this) nextStickyAtc.footerSpacing();
    else if (this.footerSpacer) this.footerSpacer.remove()
  }

  scheduleVisibility() {
    if (this.visibilityFrame !== null) return;
    this.visibilityFrame = requestAnimationFrame(() => {
      this.visibilityFrame = null;
      if (!this.isConnected) return;
      this.footerSpacing();
      this.handleVisibility()
    })
  }

  handleVisibility() {
    let visible = true;
    if (this.displayMode === "after_scroll") {
      if (this.mainAtcBtn) {
        const atcTop = this.mainAtcBtn.getBoundingClientRect().top + window.scrollY;
        visible = window.scrollY > atcTop + this.mainAtcBtn.offsetHeight;
      }
    } else if (this.displayMode === "after_pixels") {
      visible = window.scrollY > this.displayThreshold;
    } else if (this.displayMode === "after_percent") {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const scrolledPercent = scrollable > 0 ? window.scrollY / scrollable * 100 : 100;
      visible = scrolledPercent > this.displayThreshold;
    }

    if (this.displayMode === "always") {
      this.style.transform = "";
    } else {
      this.style.transform = visible ? "translateY(0)" : "translateY(100%)";
    }
    this.scrolledPast = visible;
    this.floatingBtns.forEach((button) => {
      button.style.setProperty("--sticky-atc-offset", visible ? this.offsetHeight + "px" : "0px");
    });
  }

  handleScrollBtn() {
    const stickyHeader = document.querySelector("sticky-header");
    const headerHeight = stickyHeader ? stickyHeader.clientHeight : 0;
    const destinationTop = this.scrollDestination.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: destinationTop - headerHeight - 15,
      behavior: "smooth"
    });
  }

  footerSpacing() {
    let spacer = document.querySelector(".sticky-atc-footer-spacer");
    if (!spacer) {
      spacer = document.createElement("div");
      spacer.className = "sticky-atc-footer-spacer";
      document.body.appendChild(spacer)
    }
    this.footerSpacer = spacer;
    spacer.style.height = this.clientHeight - 1 + "px";
    spacer.style.display = "block";
    const footer = document.querySelector(".footer");
    if (footer) spacer.style.background = window.getComputedStyle(footer).background;
  }
}
customElements["define"]("sticky-atc", StickyAtc);
class BundleDeals extends HTMLElement
{
  constructor()
  {
    super(),
    this["productContainers"]=this["querySelectorAll"](".bundle-deals__product-js"),
    this["mediaItemContainers"]=this["querySelectorAll"](".bundle-deals__media-item-container-js"),
    this["mediaItemImgs"]=this.querySelectorAll(".bundle-deals__media-item-img-js"),
    this.checkboxes=this.querySelectorAll(".bundle-deals__checkbox-js"),
    this["variantPickers"]=this["querySelectorAll"](".bundle-deals__variant-selects-js"),
    this.skipNonExistent=this.dataset["skipNonExistent"]==="true",
    this["skipUnavailable"]=this["dataset"]["skipUnavailable"]==="true",
    this["prices"]=this.querySelectorAll(".bundle-deals__price-js"),
    this["comparePrices"]=this["querySelectorAll"](".bundle-deals__compare-price-js"),
    this["totalPrice"]=this["querySelector"](".bundle-deals__total-price-js"),
    this["totalComparePrice"]=this.querySelector(".bundle-deals__total-compare-price-js"),
    this["updatePrices"]=this.dataset.updatePrices==="true",
    this["percentageLeft"]=parseFloat(this.dataset["percentageLeft"]),
    this["fixedDiscount"]=parseFloat(this["dataset"]["fixedDiscount"]),
    this["currencySymbol"]=this["dataset"].currencySymbol,
    this["moneyFormat"]=this["dataset"]["moneyFormat"]||this["currencySymbol"]+"{{amount}}",
    this.selectedVariants={
      id_1:null,
      id_2:null,
      id_3:null,
      id_4:null,
      id_5:null
    },
    this.formVariants=[],
    this["initIds"](),
    this["checkboxes"]["forEach"](id_0503=>{
      id_0503["addEventListener"]("change", this["handleCheckboxChange"]["bind"](this))
    }),
    this["variantPickers"]["forEach"](id_0504=>{
      id_0504["addEventListener"]("change", this.handleSelectChange["bind"](this))
    })
  }
  initIds()
  {
    this["checkboxes"]["forEach"](id_0505=>{
      this["selectedVariants"][id_0505["dataset"]["idIndex"]]={
        id:id_0505["dataset"].id, price:id_0505.dataset.price, comparePrice:id_0505["dataset"]["comparePrice"], checked:!0
      }
    }),
    this["updateFormIds"]()
  }
  ["handleCheckboxChange"](id_0506)
  {
    const id_0507=id_0506["currentTarget"],
    id_0508=id_0507["checked"],
    id_0509=parseInt(id_0507["dataset"]["index"]);
    this["selectedVariants"][id_0507["dataset"].idIndex].checked=id_0508;
    const id_0510=this["productContainers"][id_0509],
    id_0511=id_0510["querySelectorAll"]("select");
    id_0508?(this["mediaItemContainers"][id_0509].classList["remove"]("bundle-deals__media-item--disabled"), id_0510["classList"]["remove"]("bundle-deals__product--deselected"), id_0511["forEach"](id_0512=>{
      id_0512["removeAttribute"]("disabled")
    })):(this["mediaItemContainers"][id_0509].classList["add"]("bundle-deals__media-item--disabled"), id_0510["classList"].add("bundle-deals__product--deselected"), id_0511["forEach"](id_0513=>{
      id_0513.setAttribute("disabled", "")
    })),
    this["updateFormIds"](),
    this["updatePrices"]&&this["updateTotalPrice"]()
  }
  ["handleSelectChange"](id_0514)
  {
    const id_0516=id_0514["currentTarget"],
    id_0517=parseInt(id_0516["dataset"]["index"]),
    id_0518=Array.from(id_0516["querySelectorAll"]("select"), id_0519=>id_0519["value"]),
    id_0520=JSON["parse"](id_0516.querySelector("[type=\"application/json\"]")["textContent"]).filter(id_0521=>id_0516.querySelector(":checked")["value"]===id_0521["option1"]),
    id_0522=[...id_0516["querySelectorAll"](".select--small")];
    updateVariantStatuses(id_0520, id_0522, this.skipUnavailable);
    const id_0523=JSON["parse"](id_0516["querySelector"]('[type="application/json"]')["textContent"])["find"](id_0524=>!id_0524.options["map"]((id_0526, id_0527)=>id_0518[id_0527]===id_0526)["includes"](!1));
    if(!id_0523)
    {
      this["skipNonExistent"]&&findAvailableVariant(id_0516, id_0518, !1, !0, this["skipUnavailable"]);
      return
    }
    if(this["skipUnavailable"]&&!id_0523["available"]&&findAvailableVariant(id_0516, id_0518, !1, !0, !0))return;
    let{
      price:id_0528,
      compare_at_price:id_0529,
      featured_image:id_0530
    }
    =id_0523;
    id_0528=parseInt(id_0528);
    let id_0531=id_0529?parseInt(id_0529):id_0528;
    const id_0532=id_0516.dataset["percentageLeft"]??1,
    id_0533=id_0516["dataset"]["fixedDiscount"]??0;
    id_0528=id_0528*id_0532-id_0533,
    id_0530&&(id_0530=id_0530["src"]);
    const id_0534=id_0523.id;
    this["selectedVariants"][id_0516["dataset"]["idIndex"]].id=id_0534,
    this["selectedVariants"][id_0516["dataset"].idIndex]["price"]=id_0528,
    this["selectedVariants"][id_0516["dataset"].idIndex]["comparePrice"]=id_0531,
    this["updateFormIds"](),
    this["updatePrices"]&&(this["prices"][id_0517].innerHTML=formatMoney(id_0528, this["moneyFormat"], !0), id_0531>id_0528?this["comparePrices"][id_0517].innerHTML=formatMoney(id_0531, this["moneyFormat"], !0):this["comparePrices"][id_0517]["innerHTML"]="", this["updateTotalPrice"]()),
    id_0530&&id_0530["length"]>0&&this["mediaItemImgs"][id_0517]&&(this.mediaItemImgs[id_0517]["src"]=id_0530)
  }
  updateFormIds()
  {
    const id_0535=[],
    id_0536=this["selectedVariants"];
    for(const id_0537 in id_0536)
    {
      const id_0538=id_0536[id_0537];
      if(id_0538!=null&&id_0538["checked"])
      {
        const id_0539=id_0535.findIndex(id_0540=>id_0540.id===id_0538.id);
        id_0539<0?id_0535["unshift"]({
          id:id_0538.id, quantity:1
        }):id_0535[id_0539].quantity+=1
      }
    }
    this["formVariants"]=id_0535
  }
  ["updateTotalPrice"]()
  {
    const id_0541=[],
    id_0542=[],
    id_0543=this["selectedVariants"];
    for(const id_0544 in id_0543)
    {
      const id_0545=id_0543[id_0544];
      id_0545!=null&&id_0545.checked&&(id_0541["push"](parseInt(id_0545["price"])), id_0542["push"](parseInt(id_0545["comparePrice"])))
    }
    const id_0546=id_0541.reduce((id_0547, id_0548)=>id_0547+id_0548, 0),
    id_0549=id_0546*this.percentageLeft-this["fixedDiscount"],
    id_0550=id_0542.reduce((id_0551, id_0552)=>id_0551+id_0552, 0);
    this["totalPrice"]["innerHTML"]=formatMoney(id_0549, this["moneyFormat"], !0),
    id_0550>id_0549?this["totalComparePrice"]["innerHTML"]=formatMoney(id_0550, this["moneyFormat"], !0):this["totalComparePrice"].innerHTML=""
  }
}
customElements["define"]("bundle-deals", BundleDeals);
class QuantityBreaks extends HTMLElement
{
  constructor()
  {
    super(),
    this.quantityGifts=document["getElementById"]("quantity-gifts-"+this["dataset"].section),
    this["quantityBreakUpsells"]=this["querySelectorAll"]("quantity-break-upsells"),
    this["subscriptionWidget"]=document.getElementById("SubscriptionWidget-"+this["dataset"]["section"]),
    this.inputs=this["querySelectorAll"]("input[name=\"quantity\"]"),
    this["labels"]=this["querySelectorAll"](".quantity-break"),
    this["jsonData"]=this["querySelector"]('[type="application/json"]'),
    this.hasVariants=this["jsonData"]["dataset"]["hasVariants"]==="true",
    this.selectedVariants={
      input_1:[],
      input_2:[],
      input_3:[],
      input_4:[],
      input_5:[],
      input_6:[]
    },
    this.updateUnavailable=this["dataset"].updateUnavailable==="true",
    this["skipNonExistent"]=this["dataset"]["skipNonExistent"]==="true",
    this["skipUnavailable"]=this.dataset["skipUnavailable"]==="true",
    this.formVariants=[],
    this["selectedQuantity"]=1,
    this["selectedOffer"]=1;
    const id_0560=this["querySelector"]("input[checked]");
    id_0560&&(this["selectedQuantity"]=parseInt(id_0560["value"]), this.selectedOffer=parseInt(id_0560["dataset"].input["replace"]("input_", ""))),
    this["variantSelects"]=this["querySelectorAll"](".quantity-break__selector-item"),
    this["updatePrices"]=this["dataset"]["updatePrices"]==="true",
    this["updatePdpPrices"]=this["dataset"]["updatePdpPrices"]==="true",
    this.moneyFormat=this.dataset["moneyFormat"],
    this["subDiscount"]=0,
    this.subDiscountType=null,
    this["offerPrices"]={
      offer_1:{
        price:0,
        compare_price:0,
        regular_price:0
      },
      offer_2:{
        price:0,
        compare_price:0,
        regular_price:0
      },
      offer_3:{
        price:0,
        compare_price:0,
        regular_price:0
      },
      offer_4:{
        price:0,
        compare_price:0,
        regular_price:0
      }
    },
    this["initOfferPrices"](),
    this["preventGalleryScroll"]=this.dataset.preventGalleryScroll==="true",
    this["hasVariants"]&&this["initVariants"](),
    this["inputs"].forEach(id_0561=>{
      id_0561.addEventListener("change", this["handleChange"].bind(this))
    }),
    this["variantSelects"].forEach(id_0562=>{
      id_0562.addEventListener("change", this["handleSelectChange"]["bind"](this))
    }),
    this["subscriptionWidget"]&&this["subscriptionWidget"]["displayedPrices"]!=="unit"&&this.subscriptionWidget["renderSubTexts"]&&this.subscriptionWidget["renderSubTexts"]()
  }
  ["applySubTo"](id_0563)
  {
    return!this.subDiscountType||!id_0563?id_0563:this.subDiscountType==="fixed_amount"?Math.max(0, id_0563-this.subDiscount):this.subDiscountType==="percentage"?Math["round"](id_0563*(100-this["subDiscount"])/100):this["subDiscountType"]==="price"?this["subDiscount"]:id_0563
  }
  ["initOfferPrices"]()
  {
    const id_0564=parseInt(this["dataset"]["basePrice"]);
    this["labels"]["forEach"](id_0565=>{
      const id_0566=id_0565.dataset.input["replace"]("input_", ""), id_0567=parseInt(id_0565.dataset["quantity"]), id_0568=parseFloat(id_0565["dataset"]["percentageLeft"]), id_0569=parseFloat(id_0565["dataset"]["fixedDiscount"]), id_0570=id_0564*id_0567*id_0568-id_0569, id_0571=parseInt(id_0565["dataset"].baseComparePrice), id_0572=id_0571*id_0567, id_0573=id_0564*id_0567; this.offerPrices["offer_"+id_0566]["price"]=id_0570, this.offerPrices["offer_"+id_0566].compare_price=id_0572, this.offerPrices["offer_"+id_0566].regular_price=id_0573
    }),
    this["updatePdpPrices"]&&this["updateDisplayedPdpPrices"]()
  }
  ["updateInnerPrices"]()
  {
    this.labels["forEach"](id_0573=>{
      const id_0574=id_0573["dataset"]["input"]["replace"]("input_", ""), id_0575=parseFloat(id_0573["dataset"].quantity), id_0576=this["applySubTo"](this.offerPrices["offer_"+id_0574]["price"]), id_0577=this["offerPrices"]["offer_"+id_0574].compare_price, id_0578=this["offerPrices"]["offer_"+id_0574].regular_price; this["updateLabelPrices"](id_0573, id_0575, id_0576, id_0577, id_0578)
    })
  }
  ["updateLabelPrices"](id_0578, id_0579, id_0580, id_0581, id_0582)
  {
    id_0582=id_0582||id_0581;
    const id_0583=Math["max"](0, id_0582-id_0580),
    id_0584=Math["round"](id_0583/100)*100,
    id_0585=id_0580/id_0579,
    id_0586=id_0581/id_0579,
    id_0587=formatMoney(id_0580, this.moneyFormat, !0),
    id_0588=formatMoney(id_0581, this["moneyFormat"], !0),
    id_0589=formatMoney(id_0583, this["moneyFormat"], !0),
    id_0590=formatMoney(id_0584, this["moneyFormat"], !0),
    id_0591=formatMoney(id_0585, this["moneyFormat"], !0),
    id_0592=formatMoney(id_0586, this["moneyFormat"], !0),
    priceEachHtml='<span class="price-item price-item--regular money">'+id_0591+"</span>";
    id_0578["querySelectorAll"](".variant-price-update")["forEach"](id_0593=>{
      let id_0594=id_0593["dataset"].text; id_0594=id_0594["replace"]("[quantity]", id_0579), id_0594=id_0594["replace"]("[price]", id_0587), id_0594=id_0594["replace"]("[compare_price]", id_0588), id_0594=id_0594.replace("[amount_saved]", id_0589), id_0594=id_0594.replace("[amount_saved_rounded]", id_0590), id_0594=id_0594["replace"]("[price_each]", priceEachHtml), id_0594=id_0594["replace"]("[compare_price_each]", id_0592), id_0593.innerHTML=id_0594
    });
    const id_0595=id_0578["querySelector"](".quantity-break__compare-price");
    id_0595&&(id_0581>id_0580?id_0595["classList"]["remove"]("hidden"):id_0595["classList"]["add"]("hidden"))
  }
  ["updateDisplayedPdpPrices"]()
  {
    const id_0595=this["applySubTo"](this["offerPrices"]["offer_"+this["selectedOffer"]]["price"]),
    id_0596=this.offerPrices["offer_"+this["selectedOffer"]]["compare_price"];
    updatePdpPrices(id_0595, id_0596, this["dataset"]["section"], this["moneyFormat"])
  }
  handleSelectChange(id_0597)
  {
    const id_0598=id_0597["currentTarget"],
    id_0599=Array["from"](id_0598["querySelectorAll"]("select"), id_0600=>id_0600["value"]);
    if(this["updateUnavailable"])
    {
      const id_0601=this["getVariantData"]()["filter"](id_0602=>id_0598["querySelector"](":checked").value===id_0602.option1),
      id_0603=[...id_0598["querySelectorAll"](".select--small")];
      updateVariantStatuses(id_0601, id_0603, this["skipUnavailable"])
    }
    const id_0604=this["getVariantData"]().find(id_0605=>{
      return!id_0605["options"]["map"]((id_0606, id_0607)=>id_0599[id_0607]===id_0606)["includes"](!1)
    });
    if(!id_0604)
    {
      this["skipNonExistent"]&&findAvailableVariant(id_0598, id_0599, !1, !0, this["skipUnavailable"]);
      return
    }
    if(this["skipUnavailable"]&&!id_0604["available"]&&findAvailableVariant(id_0598, id_0599, !1, !0, !0))return;
    id_0598["dataset"]["selectedId"]=id_0604.id;
    const id_0608=id_0598["dataset"].selectIndex,
    id_0609=id_0598["closest"](".quantity-break"),
    id_0610=id_0609["dataset"]["input"];
    this["selectedVariants"][id_0610][id_0608]=id_0604.id,
    this["formVariants"]=this["selectedVariants"][id_0610],
    this["preventGalleryScroll"]||this["updateMedia"](id_0604);
    const id_0611=id_0598.querySelector(".quantity-break__selector-item__image img");
    if(id_0611&&(id_0611["src"]=id_0604["featured_image"]["src"]), !this["updatePrices"])return;
    var id_0612=0,
    id_0613=0;
    const id_0614=parseFloat(id_0609["dataset"]["quantity"]),
    id_0615=parseFloat(id_0609["dataset"]["percentageLeft"]),
    id_0616=parseFloat(id_0609.dataset.fixedDiscount);
    var id_0617=!0;
    id_0609.dataset["compareMode"]==="price"&&(id_0617=!1);
    for(let id_0618=0; id_0618<id_0614; id_0618++)
    {
      const id_0619=parseInt(this["selectedVariants"][id_0610][id_0618]),
      id_0620=this.getVariantData()["find"](id_0621=>parseInt(id_0621.id)===id_0619);
      if(!id_0620)return;
      id_0612+=id_0620["price"],
      id_0620["compare_at_price"]&&id_0617&&id_0620["compare_at_price"]>id_0620["price"]?id_0613+=id_0620["compare_at_price"]:id_0613+=id_0620["price"]
    }
    const id_0622=id_0612;
    id_0612=id_0612*id_0615-id_0616,
    this["offerPrices"]["offer_"+this["selectedOffer"]].price=id_0612,
    this["offerPrices"]["offer_"+this["selectedOffer"]].compare_price=id_0613,
    this["offerPrices"]["offer_"+this["selectedOffer"]].regular_price=id_0622,
    id_0612=this["applySubTo"](id_0612),
    this["updateLabelPrices"](id_0609, id_0614, id_0612, id_0613, id_0622),
    this.updatePdpPrices&&this["updateDisplayedPdpPrices"](),
    this.subscriptionWidget&&this.subscriptionWidget["displayedPrices"]!=="unit"&&this["subscriptionWidget"].renderSubTexts&&this["subscriptionWidget"]["renderSubTexts"]()
  }
  ["getVariantData"]()
  {
    return this["variantData"]=this.variantData||JSON["parse"](this["jsonData"]["textContent"]),
    this["variantData"]
  }
  ["initVariants"]()
  {
    this.hasVariants&&(this["labels"]["forEach"](id_0622=>{
      if(id_0622.querySelector(".quantity-break__variants"))
      {
        let id_0623=[]; id_0622["querySelectorAll"](".quantity-break__selector-item")["forEach"](id_0624=>{
          id_0623["push"](id_0624["dataset"]["selectedId"])
        }), this.selectedVariants[id_0622["dataset"]["input"]]=id_0623
      }
    }), this["formVariants"]=[])
  }
  ["updateMedia"](id_0625)
  {
    if(!id_0625||!id_0625["featured_media"])return;
    document.querySelectorAll("[id^=\"MediaGallery-"+this["dataset"]["section"]+'"]')["forEach"](id_0626=>{
      id_0626["setActiveMedia"]&&id_0626["setActiveMedia"](this.dataset.section+"-"+id_0625["featured_media"].id, !0)
    })
  }
  ["handleChange"](id_0627)
  {
    const id_0628=parseInt(id_0627["target"].value);
    this["selectedQuantity"]=id_0628,
    this["selectedOffer"]=parseInt(id_0627["target"]["dataset"]["input"]["replace"]("input_", "")),
    this["hasVariants"]&&(this.formVariants=this["selectedVariants"][id_0627["target"].dataset["input"]]),
    this["quantityGifts"]&&this["quantityGifts"]["unlockGifts"](id_0628),
    this["quantityBreakUpsells"]["forEach"](id_0629=>{
      id_0629.initUnlock()
    }),
    this["updatePdpPrices"]&&this["updateDisplayedPdpPrices"](),
    this.subscriptionWidget&&this["subscriptionWidget"]["displayedPrices"]!=="unit"&&this["subscriptionWidget"]["renderSubTexts"]&&this["subscriptionWidget"]["renderSubTexts"]()
  }
}
customElements["define"]("quantity-breaks", QuantityBreaks);
class ExaSubscriptionWidget extends HTMLElement
{
  constructor()
  {
    super()
  }
  connectedCallback()
  {
    this["sectionId"]=this["dataset"]["section"],
    this["productForm"]=document["getElementById"]("product-form-"+this["sectionId"]),
    this["isCheckbox"]=this["dataset"]["isCheckbox"]==="true",
    this["quantityBreaks"]=document.getElementById("quantity-breaks-"+this["dataset"].section),
    this["oneTimeBox"]=this["querySelector"]("[data-type=\"one_time\"]"),
    this["oneTimeBoxTexts"]=this.querySelectorAll(".one-time-price-update"),
    this["subscriptionBox"]=this["querySelector"]("[data-type=\"subscription\"]"),
    this.frequencySelect=this["querySelector"](".subscription__select .select__select"),
    this["frequencyRadios"]=this.querySelectorAll(".subscription__frequency-input"),
    this["subBoxTexts"]=this["querySelectorAll"](".sub-price-update"),
    this["selectedPlanId"]=this["dataset"].firstSellingPlanId,
    this["isSubSelected"]=this.dataset["preselected"]==="subscription",
    this["sellingPlanInput"]=null,
    this["subscriptionName"]=this["dataset"].subscriptionName,
    this["basePrice"]=parseInt(this["dataset"]["basePrice"]),
    this.baseComparePrice=parseInt(this.dataset["baseComparePrice"]),
    this["updatePrices"]=this["dataset"]["updatePrices"]==="true",
    this.discountType=this.dataset["discountType"],
    this["discountValue"]=parseInt(this["dataset"]["discountValue"]),
    this["displayedPrices"]=this["dataset"]["displayedPrices"]||"unit",
    this.moneyFormat=this["dataset"].moneyFormat,
    this["quantityBreaks"]=document.getElementById("quantity-breaks-"+this["dataset"]["section"]),
    this["quantityBreaks"]&&(this["quantityBreaks"]["updatePrices"]=!0, this["quantityBreaks"]["updatePdpPrices"]=!0),
    this["updatePrices"]&&this["updateAllPrices"](),
    this["init"](),
    this["displayedPrices"]!=="unit"&&this["quantityBreaks"]&&this["quantityBreaks"]["offerPrices"]&&(this["updatePricesFromQtyBreaks"]=!0, this["renderSubTexts"]())
  }
  ["init"]()
  {
    this["createSellingPlanInput"](),
    this["syncBoxA11y"](),
    this["setupBoxClickListeners"](),
    this["setupFrequencyListeners"]()
  }
  ["createSellingPlanInput"]()
  {
    this["sellingPlanInput"]=document["createElement"]("input"),
    this.sellingPlanInput["type"]="hidden",
    this.sellingPlanInput["name"]="selling_plan",
    this["sellingPlanInput"]["value"]=this.isSubSelected?this["selectedPlanId"]:"",
    this["productForm"]&&this["productForm"]["appendChild"](this["sellingPlanInput"])
  }
  setupBoxClickListeners()
  {
    const id_0629=(this.classList.contains("subscription-widget--sub-first")?[this["subscriptionBox"], this.oneTimeBox]:[this.oneTimeBox, this["subscriptionBox"]]).filter(Boolean);
    id_0629.forEach(id_0630=>{
      const id_0630a=id_0630.querySelector(":scope > .subscription__content");
      id_0630&&id_0630.addEventListener("click", id_0631=>{
        if(!(this["isCheckbox"]&&id_0631["target"]["closest"](".subscription__frequency")))
        {
          if(this["isCheckbox"])
          {
            const id_0632=id_0630["classList"]["contains"]("subscription__box--selected"); this["isSubSelected"]=!id_0632, id_0630["classList"]["toggle"]("subscription__box--selected"), id_0630["dataset"]["selected"]=!id_0632, this["sellingPlanInput"]["value"]=id_0632?"":this.selectedPlanId
          }
          else this["isSubSelected"]=id_0630["dataset"]["type"]==="subscription", this.oneTimeBox?.["classList"].remove("subscription__box--selected"), this["subscriptionBox"]?.["classList"]["remove"]("subscription__box--selected"), this["oneTimeBox"]?.["setAttribute"]("data-selected", "false"), this["subscriptionBox"]?.["setAttribute"]("data-selected", "false"), id_0630.classList.add("subscription__box--selected"), id_0630["dataset"]["selected"]="true", this.sellingPlanInput["value"]=this["isSubSelected"]?this["selectedPlanId"]:""; this["syncBoxA11y"](), this["updatePrices"]&&this.updateAllPrices()
        }
      }),
      id_0630a?.addEventListener("keydown", id_0631=>{
        if(id_0631.target!==id_0630a)return;
        if(id_0631.key==="Enter"||id_0631.key===" ")return id_0631.preventDefault(), id_0630.click();
        if(this["isCheckbox"]||!["ArrowLeft", "ArrowUp", "ArrowRight", "ArrowDown", "Home", "End"].includes(id_0631.key))return;
        id_0631.preventDefault();
        const id_0632=id_0629.indexOf(id_0630), id_0633=id_0631.key==="Home"?0:id_0631.key==="End"?id_0629.length-1:(id_0632+(id_0631.key==="ArrowLeft"||id_0631.key==="ArrowUp"?-1:1)+id_0629.length)%id_0629.length, id_0634=id_0629[id_0633], id_0635=id_0634.querySelector(":scope > .subscription__content");
        id_0635?.focus(), id_0634.click()
      })
    })
  }
  syncBoxA11y()
  {
    [this.oneTimeBox, this["subscriptionBox"]].filter(Boolean).forEach(id_0630=>{
      const id_0631=id_0630.classList.contains("subscription__box--selected"), id_0632=id_0630.querySelector(":scope > .subscription__content");
      id_0630.dataset.selected=String(id_0631),
      id_0632?.setAttribute("aria-checked", String(id_0631)),
      id_0632&&(id_0632.tabIndex=this["isCheckbox"]||id_0631?0:-1)
    })
  }
  updateAllPrices()
  {
    if(this.basePrice=parseInt(this["dataset"]["basePrice"]), this["baseComparePrice"]=parseInt(this.dataset["baseComparePrice"]), this["quantityBreaks"])this["isSubSelected"]?(this.quantityBreaks.subDiscountType=this["discountType"], this.quantityBreaks["subDiscount"]=this["discountValue"]):(this["quantityBreaks"]["subDiscountType"]=null, this.quantityBreaks.subDiscount=0),
    this["quantityBreaks"]["updateDisplayedPdpPrices"](),
    this["quantityBreaks"]["updateInnerPrices"]();
    else{
      let id_0633=this.applySubTo(this["basePrice"]);
      updatePdpPrices(id_0633, this.baseComparePrice, this["dataset"]["section"], this.moneyFormat)
    }
    this["renderSubTexts"]()
  }
  ["applySubTo"](id_0634, id_0635=!1)
  {
    if(this["isSubSelected"]||id_0635)
    {
      if(this["discountType"]==="fixed_amount")return Math["max"](0, id_0634-this["discountValue"]);
      if(this["discountType"]==="percentage")return Math["round"](id_0634*(100-this["discountValue"])/100);
      if(this.discountType==="price")return this["discountValue"]
    }
    return id_0634
  }
  setupFrequencyListeners()
  {
    this.frequencyRadios.forEach(id_0636=>{
      id_0636["addEventListener"]("change", id_0637=>{
        this["applyFrequencyNode"](id_0637["currentTarget"])
      })
    }),
    this["frequencySelect"]&&this.frequencySelect.addEventListener("change", ()=>{
      const id_0638=this["frequencySelect"]["selectedOptions"]?.[0]; id_0638&&this["applyFrequencyNode"](id_0638)
    })
  }
  ["applyFrequencyNode"](id_0639)
  {
    this.selectedPlanId=id_0639.dataset["sellingPlanId"],
    this["discountType"]=id_0639["dataset"]["discountType"]||this.discountType,
    this.discountValue=id_0639["dataset"].discountValue?parseInt(id_0639["dataset"]["discountValue"], 10):this.discountValue,
    this.subscriptionBox?.["classList"]["contains"]("subscription__box--selected")&&(this["sellingPlanInput"].value=this.selectedPlanId||""),
    this.updatePrices&&this["updateAllPrices"]()
  }
  computeSubPrices(id_0640=!0)
  {
    let id_0641=this["basePrice"],
    id_0642=this.baseComparePrice;
    this["quantityBreaks"]&&this["displayedPrices"]!=="unit"&&(id_0641=this["quantityBreaks"]["offerPrices"]["offer_"+this["quantityBreaks"].selectedOffer]["price"], id_0642=this["quantityBreaks"]["offerPrices"]["offer_"+this.quantityBreaks["selectedOffer"]]["compare_price"], this.displayedPrices=="unit_with_discount"&&(id_0641=id_0641/this["quantityBreaks"]["selectedQuantity"], id_0642=id_0642/this["quantityBreaks"]["selectedQuantity"])),
    id_0640&&(id_0641=this.applySubTo(id_0641, !0));
    let id_0643=this["subscriptionName"];
    const id_0644=Math["max"](0, id_0642-id_0641),
    id_0645=Math.round(id_0644/100)*100,
    id_0646=id_0642>0?Math.round(id_0644*100/id_0642):0;
    return{
      price:id_0641,
      comparePrice:id_0642,
      amount_saved:id_0644,
      amount_saved_rounded:id_0645,
      percentage_saved:id_0646,
      subscription_name:id_0643
    }
  }
  ["renderSubTexts"]()
  {
    if(this["subBoxTexts"].length>0)
    {
      const{
        price:id_0647,
        compare_price:id_0648,
        amount_saved:id_0649,
        amount_saved_rounded:id_0650,
        percentage_saved:id_0651,
        subscription_name:id_0652
      }
      =this.computeSubPrices();
      this["updatePriceTexts"](this["subBoxTexts"], id_0647, id_0648, id_0649, id_0650, id_0651, id_0652)
    }
    this["updatePricesFromQtyBreaks"]&&this["renderOneTimeTexts"]()
  }
  renderOneTimeTexts()
  {
    if(this.oneTimeBoxTexts["length"]>0)
    {
      const{
        price:id_0653,
        compare_price:id_0654,
        amount_saved:id_0655,
        amount_saved_rounded:id_0656,
        percentage_saved:id_0657,
        subscription_name:id_0658
      }
      =this["computeSubPrices"](!1);
      this["updatePriceTexts"](this["oneTimeBoxTexts"], id_0653, id_0654, id_0655, id_0656, id_0657, id_0658)
    }
  }
  ["updatePriceTexts"](id_0659, id_0660, id_0661, id_0662, id_0663, id_0664, id_0665)
  {
    const id_0666=id_0667=>formatMoney(id_0667, this["moneyFormat"], !0);
    id_0659["forEach"](id_0668=>{
      let id_0669=id_0668.dataset.text||""; id_0669=id_0669["replaceAll"]("[price]", id_0666(id_0660))["replaceAll"]("[compare_price]", id_0666(id_0661))["replaceAll"]("[amount_saved]", id_0666(id_0662))["replaceAll"]("[amount_saved_rounded]", id_0666(id_0663))["replaceAll"]("[percentage_saved]", String(id_0664)+"%")["replaceAll"]("[subscription_name]", id_0665||""), id_0668["innerHTML"]=id_0669, id_0668["classList"]["contains"]("subscription__compare-price")&&id_0668.classList["toggle"]("hidden", !(id_0661>id_0660))
    })
  }
  ["hydrateFromExternalHTML"](id_0670)
  {
    const id_0671=id_0672=>{
      const id_0674=id_0672["querySelector"](".subscription__select .select__select"),
      id_0675=id_0672["querySelectorAll"](".subscription__frequency-input");
      if(id_0674)return{
        type:"select",
        count:id_0674["options"]["length"],
        index:id_0674["selectedIndex"]>=0?id_0674["selectedIndex"]:0
      };
      if(id_0675["length"])
      {
        const id_0676=Array["from"](id_0675)["findIndex"](id_0677=>id_0677.checked);
        return{
          type:"radio",
          count:id_0675["length"],
          index:id_0676>=0?id_0676:0
        }
      }
      return{
        type:"none",
        count:0,
        index:0
      }
    },
    id_0678=id_0671(this),
    id_0679=this["isSubSelected"];
    this["dataset"]["firstSellingPlanId"]=id_0670.dataset["firstSellingPlanId"]??"",
    this["dataset"]["subscriptionName"]=id_0670["dataset"]["subscriptionName"]??"",
    this["dataset"]["discountType"]=id_0670["dataset"]["discountType"]??"",
    this["dataset"].discountValue=id_0670["dataset"]["discountValue"]??"0";
    const id_0680=id_0670.querySelector(".subscription__wrapper"),
    id_0681=this["querySelector"](".subscription__wrapper");
    id_0680&&id_0681&&id_0681["replaceWith"](id_0680),
    this["oneTimeBox"]=this["querySelector"]("[data-type=\"one_time\"]"),
    this["oneTimeBoxTexts"]=this["querySelectorAll"](".one-time-price-update"),
    this.subscriptionBox=this["querySelector"]('[data-type="subscription"]'),
    this["subBoxTexts"]=this["querySelectorAll"](".sub-price-update"),
    this.frequencySelect=this.querySelector(".subscription__select .select__select"),
    this["frequencyRadios"]=this.querySelectorAll(".subscription__frequency-input");
    const id_0682=id_0671(this);
    let id_0683=0;
    id_0678["type"]!=="none"&&id_0682["type"]!=="none"&&id_0678["count"]===id_0682["count"]&&(id_0683=Math["max"](0, Math["min"](id_0678["index"], id_0682["count"]-1))),
    id_0682["type"]==="select"&&this["frequencySelect"]?this.frequencySelect["selectedIndex"]=id_0683:id_0682["type"]==="radio"&&this.frequencyRadios["length"]&&this.frequencyRadios["forEach"]((id_0684, id_0685)=>{
      id_0684.checked=id_0685===id_0683
    });
    let id_0686=null;
    this.frequencySelect?id_0686=this["frequencySelect"].selectedOptions?.[0]||this["frequencySelect"].options?.[0]||null:this["frequencyRadios"].length&&(id_0686=Array["from"](this["frequencyRadios"])["find"](id_0687=>id_0687["checked"])||this["frequencyRadios"][0]),
    this["selectedPlanId"]=id_0686?.["dataset"]["sellingPlanId"]||this["dataset"]["firstSellingPlanId"]||"",
    id_0686?this.applyFrequencyNode(id_0686):(this["selectedPlanId"]=this.dataset["firstSellingPlanId"]||"", this["discountType"]=this["dataset"]["discountType"]||"", this["discountValue"]=parseInt(this["dataset"].discountValue||"0", 10)),
    id_0679&&this["subscriptionBox"]?(this["subscriptionBox"]["classList"]["add"]("subscription__box--selected"), this["subscriptionBox"]["setAttribute"]("data-selected", "true"), this["oneTimeBox"]?.classList["remove"]("subscription__box--selected"), this["oneTimeBox"]?.["setAttribute"]("data-selected", "false"), this["isSubSelected"]=!0, this["sellingPlanInput"]["value"]=this.selectedPlanId||""):(this["subscriptionBox"]?.classList["remove"]("subscription__box--selected"), this.subscriptionBox?.setAttribute("data-selected", "false"), this["oneTimeBox"]&&(this["oneTimeBox"]["classList"]["add"]("subscription__box--selected"), this["oneTimeBox"]["setAttribute"]("data-selected", "true")), this.isSubSelected=!1, this["sellingPlanInput"]["value"]=""),
    this["syncBoxA11y"](),
    this["setupBoxClickListeners"](),
    this["setupFrequencyListeners"](),
    this["updatePrices"]?this["updateAllPrices"]():this["renderSubTexts"]()
  }
}
customElements["define"]("exa-subscription-widget", ExaSubscriptionWidget);
function updatePdpPrices(id_0688, id_0689, id_0690, id_0691)
{
  const id_0692=formatMoney(id_0688, id_0691),
  id_0693=formatMoney(id_0689, id_0691),
  id_0694=id_0689>id_0688;
  document["querySelectorAll"](".price-"+id_0690).forEach(id_0695=>{
    if(id_0695["classList"]["toggle"]("price--on-sale", id_0694), id_0695["querySelector"](".price__regular .price-item--regular")["innerHTML"]=id_0692, id_0695["querySelector"](".price__sale .price-item--sale")["innerHTML"]=id_0692, id_0694)
    {
      const id_0696=id_0695.querySelector(".price__sale .price-item--regular"); id_0696&&(id_0696.innerHTML=id_0693); const id_0697=id_0695["querySelector"](".badge__percentage-saved"); if(id_0697)
      {
        const id_0698=Math["round"]((id_0689-id_0688)*100/id_0689); id_0697["innerHTML"]=id_0698
      }
      const id_0699=id_0695["querySelector"](".badge__money-saved"); if(id_0699)
      {
        const id_0700=id_0689-id_0688, id_0701=formatMoney(id_0700, id_0691, !0); id_0699["innerHTML"]=id_0701
      }
    }
  }),
  document["querySelectorAll"](".main-atc-price-span-"+id_0690)["forEach"](id_0702=>{
    id_0702["innerHTML"]=id_0692
  })
}
function updateVariantStatuses(id_0703, id_0704, id_0705=!1)
{
  id_0704.forEach((id_0706, id_0707)=>{
    if(id_0707===0)return; const id_0708=[...id_0706["querySelectorAll"]("option")], id_0709=id_0704[id_0707-1]["querySelector"](":checked")["value"], id_0710=id_0703.filter(id_0711=>id_0711.available&&id_0711["option"+id_0707]===id_0709).map(id_0712=>id_0712["option"+(id_0707+1)]), id_0713=id_0703.filter(id_0714=>id_0714["option"+id_0707]===id_0709)["map"](id_0715=>id_0715["option"+(id_0707+1)]); id_0708.forEach(id_0716=>{
      id_0716["classList"].remove("unavailable", "non-existent"), id_0716["removeAttribute"]("disabled"), id_0716["removeAttribute"]("hidden"), id_0713["includes"](id_0716["getAttribute"]("value"))?id_0710.includes(id_0716["getAttribute"]("value"))?id_0716.innerText=id_0716["getAttribute"]("value"):(id_0716["innerText"]=window.variantStrings["unavailable_with_option"]["replace"]("[value]", id_0716["getAttribute"]("value")), id_0716.classList.add("unavailable"), id_0705&&(id_0716["setAttribute"]("disabled", ""), id_0716["setAttribute"]("hidden", ""))):(id_0716["innerText"]=window["variantStrings"].unavailable_with_option["replace"]("[value]", id_0716.getAttribute("value")), id_0716.classList["add"]("non-existent"), id_0716["setAttribute"]("disabled", ""), id_0716["setAttribute"]("hidden", ""))
    })
  })
}
function findAvailableVariant(id_0717, id_0718, id_0719=!0, id_0720=!1, id_0721=!1)
{
  for(let id_0722=id_0718["length"]-1; id_0722>=0; id_0722--)
  {
    const id_0723=id_0719?id_0717["querySelector"](".product-form__input:nth-child("+(id_0722+1)+")"):id_0717["querySelectorAll"](".select")[id_0722],
    id_0724=id_0719?id_0723["querySelector"](".product-form__input__type")["dataset"]["type"]:"dropdown",
    id_0725=id_0724==="dropdown"?id_0723.querySelectorAll("option"):id_0723["querySelectorAll"]("input[type=\"radio\"]");
    let id_0726=Array["from"](id_0725)["findIndex"](id_0727=>id_0727["value"]===id_0718[id_0722]),
    id_0728=!1;
    for(; id_0726<id_0725["length"]-1; )
    {
      id_0726++;
      const id_0729=id_0725[id_0726];
      if((!id_0720||!id_0729["classList"]["contains"]("non-existent"))&&(!id_0721||!id_0729["classList"]["contains"]("unavailable")))
      {
        id_0718[id_0722]=id_0729.value,
        id_0728=!0;
        break
      }
    }
    if(!id_0728)for(id_0726=Array["from"](id_0725)["findIndex"](id_0730=>id_0730["value"]===id_0718[id_0722]); id_0726>0; )
    {
      id_0726--;
      const id_0731=id_0725[id_0726];
      if((!id_0720||!id_0731.classList.contains("non-existent"))&&(!id_0721||!id_0731["classList"]["contains"]("unavailable")))
      {
        id_0718[id_0722]=id_0731.value,
        id_0728=!0;
        break
      }
    }
    if(id_0728)return id_0724==="dropdown"?id_0723["querySelector"](".select__select")["value"]=id_0718[id_0722]:id_0723.querySelector("input[type=\"radio\"][value=\""+id_0718[id_0722]+'"]')["checked"]=!0,
    id_0717["dispatchEvent"](new Event("change")),
    !0
  }
  return!1
}
function updateGiftGoalInStorage({
  giftHandle:id_0732, giftVariantId:id_0733, unlockType:id_0734, unlockRequirement:id_0735, unlockTarget:id_0736
})
{
  const id_0737=JSON["parse"](localStorage.getItem(GIFTS_LOCAL_STORAGE_KEY))||{
  };
  !id_0732||!id_0733||!id_0735||!id_0734||(id_0737[id_0732]={
    type:id_0734, gift_handle:id_0732, gift_variant_id:Number(id_0733), required_quantity:Number(id_0735), unlock_product_handle:id_0736||null
  }, localStorage["setItem"](GIFTS_LOCAL_STORAGE_KEY, JSON["stringify"](id_0737)))
}
class QuantityGifts extends HTMLElement
{
  constructor()
  {
    super(),
    this["gifts"]=this["querySelectorAll"](".quantity-gift"),
    this["quantityBreaks"]=document["getElementById"]("quantity-breaks-"+this.dataset.section),
    this["quantitySelector"]=document["getElementById"]("Quantity-Form--"+this.dataset.section),
    this["unlockedItems"]=[],
    this.mainProduct=this["dataset"]["mainProduct"],
    this["cartItems"]=document.querySelector("cart-drawer-items"),
    this["initUnlock"](),
    this.saveLocalStorage=this["dataset"].saveLocalStorage==="true",
    this["saveLocalStorage"]&&this["storeLocalStorageData"]()
  }
  storeLocalStorageData()
  {
    this["gifts"]["forEach"](id_0738=>{
      updateGiftGoalInStorage({
        giftHandle:id_0738["dataset"]["handle"], giftVariantId:id_0738["dataset"]["product"], unlockType:"product_quantity", unlockRequirement:id_0738.dataset["quantity"], unlockTarget:this["mainProduct"]
      })
    })
  }
  ["initUnlock"]()
  {
    let id_0739=1;
    const id_0740=this.mainProduct?document["querySelectorAll"](".cart-item--product-"+this["mainProduct"]):null;
    if(id_0740&&id_0740["length"])
    {
      id_0739=0;
      for(let id_0741=0; id_0741<id_0740["length"]; id_0741++)id_0739+=parseInt(id_0740[id_0741]["dataset"]["quantity"])
    }
    else if(this["quantityBreaks"])id_0739=parseInt(this["quantityBreaks"]["selectedQuantity"]);
    else if(this["quantitySelector"])
    {
      const id_0742=this.quantitySelector.querySelector("input[name=\"quantity\"]");
      id_0739=parseInt(id_0742["value"])
    }
    this.unlockGifts(id_0739, !0)
  }
  ["unlockGifts"](id_0743, id_0744=!1)
  {
    this["unlockedItems"]=[],
    this.gifts["forEach"](id_0745=>{
      if(parseInt(id_0745["dataset"].quantity)<=id_0743)
      {
        id_0745["classList"]["add"]("quantity-gift--unlocked"), id_0745.dataset.unlocked="true"; const id_0746=document["querySelector"](".cart-item--product-"+id_0745["dataset"]["handle"]); id_0745["dataset"]["product"]&&!id_0746&&this["unlockedItems"]["unshift"](id_0745["dataset"]["product"])
      }
      else id_0745["classList"]["remove"]("quantity-gift--unlocked"), id_0745.dataset["unlocked"]="false"
    }),
    this["mainProduct"]&&id_0744&&this.addRemoveProducts()
  }
  ["addRemoveProducts"]()
  {
    if(!this["mainProduct"])return;
    const id_0747=document["querySelectorAll"](".cart-item--product-"+this["mainProduct"]);
    this["gifts"].forEach(id_0748=>{
      id_0747["length"]?id_0748["dataset"]["unlocked"]==="true"&&id_0748["dataset"].product?this["addToCart"](id_0748):this.removeFromCart(id_0748):this["removeFromCart"](id_0748)
    })
  }
  addToCart(id_0749)
  {
    if(document.querySelector(".cart-item--product-"+id_0749["dataset"]["handle"]))return;
    const id_0750=id_0749["querySelector"]("product-form");
    id_0750&&id_0750["handleSubmit"]&&id_0750.handleSubmit()
  }
  ["removeFromCart"](id_0751)
  {
    const id_0752=document["querySelector"](".cart-item--product-"+id_0751.dataset.handle);
    !id_0752||!this["cartItems"]||this["cartItems"]["updateQuantity"](id_0752["dataset"]["index"], 0)
  }
}
customElements["define"]("quantity-gifts", QuantityGifts);
class QuantityBreakUpsells extends QuantityGifts
{
  constructor()
  {
    super(),
    this.offerIndex=this["dataset"].offer,
    this["upsells"]=this.querySelectorAll(".quantity-break__upsell"),
    this["boundToggleHandler"]=this["handleToggle"]["bind"](this),
    this["upsells"]["forEach"](id_0753=>{
      id_0753.dataset.toggle==="true"&&id_0753["addEventListener"]("click", this.boundToggleHandler)
    }),
    this["initUnlock"](),
    this["saveLocalStorage"]&&this["storeLocalStorageData"]()
  }
  ["storeLocalStorageData"]()
  {
    this["upsells"]&&this["upsells"].forEach(id_0754=>{
      id_0754["dataset"]["type"]==="gift"&&updateGiftGoalInStorage({
        giftHandle:id_0754["dataset"].handle, giftVariantId:id_0754["dataset"]["product"], unlockType:"product_quantity", unlockRequirement:id_0754.dataset.minQuantity, unlockTarget:this["dataset"]["mainProduct"]
      })
    })
  }
  disconnectedCallback()
  {
    this.upsells["forEach"](id_0755=>{
      id_0755["dataset"]["toggle"]==="true"&&id_0755.removeEventListener("click", this["boundToggleHandler"])
    })
  }
  ["unlockGifts"](id_0756, id_0757=!1)
  {
    this["unlockedItems"]=[],
    this["upsells"]&&(this["upsells"].forEach(id_0758=>{
      const id_0759=parseInt(id_0758.dataset.minQuantity||0), id_0760=id_0756>=id_0759, id_0761=id_0758.dataset.selected==="true", id_0762=id_0760&&id_0761; id_0758.dataset["unlocked"]=id_0762.toString(), id_0758["classList"]["toggle"]("quantity-gift--unlocked", id_0762); const id_0763=document["querySelector"](".cart-item--product-"+id_0758["dataset"]["handle"]); if(id_0762&&id_0758.dataset["product"]&&!id_0763)
      {
        const id_0764=id_0758["dataset"]["toggle"]==="true", id_0765=id_0758.dataset["selected"]==="true"; (!id_0764||id_0764&&id_0765)&&this["unlockedItems"].unshift(id_0758["dataset"]["product"])
      }
    }), this["mainProduct"]&&id_0757&&this["dataset"]["lastOffer"]===this["dataset"]["offer"]&&this["addRemoveProducts"]())
  }
  ["addRemoveProducts"]()
  {
    if(!this["mainProduct"])return;
    const id_0766=document["querySelectorAll"](".cart-item--product-"+this["mainProduct"]),
    id_0767=[],
    id_0768=[];
    this["upsells"]["forEach"](id_0769=>{
      const id_0770=document["querySelector"](".cart-item--product-"+id_0769.dataset["handle"]), id_0771=id_0769["dataset"].unlocked==="true"; id_0766["length"]?id_0771&&!id_0770?id_0767["push"](id_0769):!id_0771&&id_0770&&id_0768.push(id_0769):id_0770&&id_0768["push"](id_0769)
    }),
    id_0768["length"]?this["removeFromCart"](id_0768[0]):id_0767["length"]&&this["addToCart"](id_0767[0])
  }
  handleToggle(id_0772)
  {
    const id_0773=id_0772["currentTarget"],
    id_0774=id_0773["dataset"]["selected"]==="true";
    id_0773["dataset"].selected=(!id_0774)["toString"](),
    this["syncUpsellAcrossOffers"](id_0773.dataset["handle"], !id_0774),
    this["syncCart"](id_0773["dataset"].handle, !id_0774, id_0773)
  }
  ["syncUpsellAcrossOffers"](id_0775, id_0776)
  {
    document["querySelectorAll"]("quantity-break-upsells")["forEach"](id_0777=>{
      id_0777["querySelectorAll"](".quantity-break__upsell[data-handle=\""+id_0775+'"]')["forEach"](id_0778=>{
        id_0778["dataset"]["selected"]=id_0776["toString"]()
      })
    })
  }
  ["syncCart"](id_0779, id_0780, id_0781)
  {
    const id_0782=document.querySelector(".cart-item--product-"+id_0779);
    this["cartItems"]&&(id_0780&&!id_0782?this["addToCart"](id_0781):!id_0780&&id_0782&&this["removeFromCart"](id_0781))
  }
}
customElements.define("quantity-break-upsells", QuantityBreakUpsells);
class ProductInfoUpsell extends HTMLElement
{
  constructor()
  {
    super(),
    this["image"]=this["querySelector"](".upsell__image__img"),
    this["toggleBtn"]=this["querySelector"](".upsell-toggle-btn"),
    this["stateControl"]=this["querySelector"](".upsell__state-control"),
    this["variantSelects"]=this.querySelector(".upsell__variant-picker"),
    this["variantSelectElements"]=this["querySelectorAll"](".select__select"),
    this.jsonData=this["querySelector"]("[type=\"application/json\"]"),
    this["productForm"]=this.querySelector("product-form"),
    this["productForm"]&&(this["idInput"]=this["productForm"]["querySelector"]('[name="id"]')),
    this["skipNonExistent"]=this["dataset"].skipNonExistent==="true",
    this["skipUnavailable"]=this.dataset["skipUnavailable"]==="true",
    this["updatePrices"]=this.dataset["updatePrices"]==="true",
    this["updatePrices"]&&(this["price"]=parseInt(this.dataset.price), this.comparePrice=parseInt(this["dataset"]["comparePrice"]), this["priceSpan"]=this["querySelector"](".upsell__price .regular-price"), this.comparePriceSpan=this["querySelector"](".upsell__price .compare-price"), this["percentageLeft"]=parseFloat(this["dataset"].percentageLeft), this["fixedDiscount"]=parseFloat(this["dataset"]["fixedDiscount"]), this.moneyFormat=this["dataset"].moneyFormat, this.isMainOfferItem=this["dataset"]["mainOfferItem"]==="true", this["isMainOfferItem"]&&(this["mainOfferContainer"]=document["querySelector"]("#MainBundleOffer-"+this.dataset["section"]))),
    this["toggleHandler"]=this["handleToggle"].bind(this),
    this["selectHandler"]=this["handleSelectChange"].bind(this),
    this["controlsConnected"]=!1,
    this["syncToggleState"](),
    this["cacheUpsellControls"]()
  }
  connectedCallback()
  {
    queueMicrotask(()=>{
      if(!this.isConnected||this["controlsConnected"])return;
      this["cacheUpsellControls"](),
      this.toggleBtn&&this["toggleBtn"]["addEventListener"]("click", this["toggleHandler"]),
      this.variantSelects&&this["variantSelects"]["addEventListener"]("change", this["selectHandler"]),
      this["controlsConnected"]=!0,
      this["syncToggleState"]()
    })
  }
  disconnectedCallback()
  {
    this.toggleBtn&&this["toggleBtn"]["removeEventListener"]("click", this["toggleHandler"]),
    this.variantSelects&&this["variantSelects"]["removeEventListener"]("change", this["selectHandler"]),
    this["controlsConnected"]=!1
  }
  ["cacheUpsellControls"]()
  {
    this["image"]=this["querySelector"](".upsell__image__img"),
    this["toggleBtn"]=this["querySelector"](".upsell-toggle-btn"),
    this["stateControl"]=this["querySelector"](".upsell__state-control"),
    this["variantSelects"]=this.querySelector(".upsell__variant-picker"),
    this["variantSelectElements"]=this["querySelectorAll"](".select__select"),
    this.jsonData=this["querySelector"]("[type=\"application/json\"]"),
    this["productForm"]=this.querySelector("product-form"),
    this["productForm"]&&(this["idInput"]=this["productForm"]["querySelector"]('[name="id"]')),
    this.classicAddButton=this.querySelector(".upsell__add-btn")
  }
  static get observedAttributes()
  {
    return["data-selected"]
  }
  attributeChangedCallback(id_0780, id_0781, id_0782)
  {
    id_0780==="data-selected"&&id_0781!==id_0782&&this["syncToggleState"]()
  }
  ["syncToggleState"]()
  {
    const id_0780=this["stateControl"]||this["querySelector"](".upsell__state-control");
    id_0780&&(this["stateControl"]=id_0780, id_0780["setAttribute"]("aria-pressed", (this["dataset"]["selected"]==="true")["toString"]()))
  }
  ["disableToggle"]()
  {
    [this["toggleBtn"], this["stateControl"]]["forEach"]((id_0780, id_0781, id_0782)=>{
      !id_0780||id_0782["indexOf"](id_0780)!==id_0781||(id_0780["setAttribute"]("disabled", ""), id_0780["setAttribute"]("aria-disabled", "true"))
    })
  }
  ["handleToggle"](id_0783)
  {
    if(this["stateControl"]&&this["stateControl"].disabled||this["toggleBtn"]&&this["toggleBtn"]["hasAttribute"]("disabled"))return;
    id_0783.target["nodeName"].toLowerCase()==="select"||id_0783["target"]["nodeName"]["toLowerCase"]()==="option"||(this.dataset.selected==="true"?this["dataset"].selected="false":this["dataset"]["selected"]="true")
  }
  ["handleSelectChange"](id_0784)
  {
    const id_0786=id_0784.currentTarget,
    id_0787=Array.from(id_0786["querySelectorAll"]("select"), id_0788=>id_0788["value"]),
    id_0789=this["getVariantData"]()["find"](id_0790=>!id_0790.options["map"]((id_0792, id_0793)=>id_0787[id_0793]===id_0792).includes(!1)),
    id_0794=this["getVariantData"]().filter(id_0795=>id_0786["querySelector"](":checked")["value"]===id_0795["option1"]),
    id_0796=[...id_0786["querySelectorAll"]("select")];
    if(updateVariantStatuses(id_0794, id_0796, this.skipUnavailable), !id_0789)
    {
      this["skipNonExistent"]&&findAvailableVariant(id_0786, id_0787, !1, !0, this["skipUnavailable"]);
      return
    }
    this["skipUnavailable"]&&!id_0789.available&&findAvailableVariant(id_0786, id_0787, !1, !0, !0)||(this["updatePrices"]&&(this.price=id_0789["price"]*this.percentageLeft-this["fixedDiscount"], this["comparePrice"]=id_0789["price"], id_0789["compare_at_price"]&&id_0789["compare_at_price"]>id_0789.price&&(this["comparePrice"]=id_0789["compare_at_price"]), displayPrices(this.price, this.comparePrice, this["priceSpan"], this["comparePriceSpan"], this["moneyFormat"])), this["image"]&&id_0789["featured_image"]&&(this["image"]["src"]=id_0789.featured_image["src"]), this["updateId"](id_0789.id), this.isMainOfferItem&&this.mainOfferContainer["updateTotalPrices"]&&this["mainOfferContainer"].updateTotalPrices())
  }
  ["updateId"](id_0797)
  {
    this["dataset"].id=id_0797,
    this["idInput"]&&(this["idInput"]["value"]=id_0797)
  }
  ["getVariantData"]()
  {
    return this.variantData=this["variantData"]||JSON["parse"](this["jsonData"]["textContent"]),
    this["variantData"]
  }
}
customElements["define"]("product-info-upsell", ProductInfoUpsell);
class CartDrawerUpsell extends ProductInfoUpsell
{
  constructor()
  {
    super(),
    this["cartDrawer"]=document["querySelector"]("cart-drawer"),
    this["cartItems"]=this["cartDrawer"].querySelector("cart-drawer-items"),
    this.classicAddButton=this.querySelector(".upsell__add-btn")
  }
  ["handleToggle"](id_0798)
  {
    if(this["stateControl"]&&this["stateControl"].disabled||this["toggleBtn"]&&this["toggleBtn"]["hasAttribute"]("disabled"))return;
    id_0798["target"]["nodeName"]["toLowerCase"]()==="select"||id_0798["target"]["nodeName"]["toLowerCase"]()==="option"||(window["Shopify"]&&window["Shopify"]["designMode"]?this["dataset"]["selected"]=this["dataset"]["selected"]==="true"?"false":"true":this.dataset["selected"]==="true"?(this["dataset"]["selected"]="false", this["removeFromCart"]()):(this["dataset"]["selected"]="true", this["addToCart"]()))
  }
  addRemoveFromCart()
  {
    if(window["Shopify"]&&window["Shopify"]["designMode"])return;
    this["dataset"]["selected"]==="true"&&!this["cartDrawer"]["classList"]["contains"]("is-empty")?this["addToCart"]():this.removeFromCart()
  }
  ["addToCart"]()
  {
    this["cartDrawer"]["querySelector"](".cart-item--product-"+this["dataset"]["handle"])||(this["disableToggle"](), this.variantSelectElements["forEach"](id_0799=>{
      id_0799["setAttribute"]("disabled", "")
    }), this["productForm"]["handleSubmit"]())
  }
  ["removeFromCart"]()
  {
    const id_0800=this["cartDrawer"]["querySelector"](".cart-item--product-"+this["dataset"].handle);
    !id_0800||!this.cartItems||(this["disableToggle"](), this["variantSelectElements"]["forEach"](id_0801=>{
      id_0801.setAttribute("disabled", "")
    }), this["cartItems"]["updateQuantity"](id_0800["dataset"]["index"], 0))
  }
  ["updateId"](id_0802)
  {
    if(this.dataset.id=id_0802, this["idInput"]["value"]=id_0802, !this["classicAddButton"]&&this.dataset["selected"]==="true")
    {
      this["selectTimeout"]&&clearTimeout(this.selectTimeout),
      this["removeFromCart"]();
      const id_0804=id_0805=>{
        id_0805["detail"]["handle"]==="handle"&&(this.addToCart(), document["removeEventListener"]("cartQuantityUpdated", id_0804))
      };
      document["addEventListener"]("cartQuantityUpdated", id_0804)
    }
  }
  ["getUpdateRequired"]()
  {
    const id_0807=this["cartDrawer"].querySelector(".cart-item--product-"+this.dataset["handle"]);
    let id_0808=!1;
    return(id_0807&&this["dataset"]["selected"]==="false"||!id_0807&&this["dataset"]["selected"]==="true")&&(id_0808=!0),
    id_0808
  }
}
customElements["define"]("cart-drawer-upsell", CartDrawerUpsell);
function displayPrices(id_0809, id_0810, id_0811, id_0812, id_0813)
{
  if(id_0813)
  {
    if(id_0809&&id_0811)
    {
      var id_0814=formatMoney(id_0809, id_0813);
      id_0811["innerHTML"]=id_0814
    }
    if(id_0810&&id_0812)
    {
      var id_0815=formatMoney(id_0810, id_0813);
      id_0812["innerHTML"]=id_0815,
      id_0810>id_0809?id_0812.classList["remove"]("hidden"):id_0812["classList"]["add"]("hidden")
    }
  }
}
function formatMoney(id_0816, id_0817, id_0818=!1)
{
  typeof id_0816=="string"&&(id_0816=id_0816.replace(".", ""));
  var id_0819="",
  id_0820=/\{\{\s*(\w+)\s*\}\}/,
  id_0821=id_0817;
  function id_0822(id_0823, id_0824)
  {
    return typeof id_0823>"u"?id_0824:id_0823
  }
  function id_0825(id_0826, id_0827, id_0828, id_0829)
  {
    if(id_0827=id_0822(id_0827, 2), id_0828=id_0822(id_0828, ","), id_0829=id_0822(id_0829, "."), isNaN(id_0826)||id_0826==null)return 0;
    id_0826=(id_0826/100).toFixed(id_0827);
    var id_0830=id_0826["split"]("."),
    id_0831=id_0830[0]["replace"](/(\d)(?=(\d\d\d)+(?!\d))/g, "$1"+id_0828),
    id_0832=id_0830[1]?id_0829+id_0830[1]:"";
    return id_0818&&id_0832===id_0829+"00"&&(id_0832=""),
    id_0831+id_0832
  }
  switch(id_0821["match"](id_0820)[1])
  {
    case "amount":id_0819=id_0825(id_0816, 2);
    break;
    case "amount_no_decimals":id_0819=id_0825(id_0816, 0);
    break;
    case "amount_with_comma_separator":id_0819=id_0825(id_0816, 2, ".", ",");
    break;
    case"amount_no_decimals_with_comma_separator":id_0819=id_0825(id_0816, 0, ".", ",");
    break
  }
  return id_0821.replace(id_0820, id_0819)
}
class CartDrawerGift extends CartDrawerUpsell
{
  constructor()
  {
    super(),
    this["progressText"]=this["querySelector"](".cart-drawer-gift__progress"),
    this["saveLocalStorage"]=this.dataset["saveLocalStorage"]==="true",
    this["refreshUnlockState"](),
    this["saveLocalStorage"]&&this.storeLocalStorageData()
  }
  ["getEffectiveReference"]()
  {
    const id_0833=this["dataset"]["type"]==="product_quantity";
    let id_0834=Number(id_0833?this["dataset"]["itemsCount"]:this["dataset"]["cartSubtotal"]||this["dataset"]["goalReference"]||this["cartItems"]?.["dataset"]?.["subtotal"]||0)||0;
    return this["cartDrawer"]?.["querySelectorAll"]('cart-drawer-upsell[data-toggle="true"][data-selected="true"]')["forEach"](id_0835=>{
      id_0835["dataset"]["handle"]&&!this["cartDrawer"]["querySelector"](".cart-item--product-"+id_0835["dataset"]["handle"])&&(id_0834+=id_0833?1:Number(id_0835["dataset"]["price"]||0)||0)
    }),
    id_0834
  }
  ["refreshProgressText"](id_0836, id_0837)
  {
    if(!this["progressText"])return;
    const id_0838=id_0837?this["dataset"]["unlockedProgressText"]:this["dataset"]["lockedProgressText"];
    if(!id_0838)return;
    const id_0839=Number(this["dataset"]["requirement"]||0)||0,
    id_0840=Math.max(id_0839-id_0836, 0);
    let id_0841="";
    this["dataset"]["type"]==="product_quantity"?id_0841=String(Math.ceil(id_0840)):id_0841="<span>"+(this["dataset"]["moneyFormat"]?formatMoney(id_0840, this["dataset"]["moneyFormat"], !0):String(id_0840))+"</span>",
    this["progressText"]["innerHTML"]=id_0838.replace("[amount]", id_0841)
  }
  ["refreshUnlockState"]()
  {
    const id_0842=this["getEffectiveReference"](),
    id_0843=Number(this["dataset"]["requirement"]||0)||0,
    id_0844=id_0842>=id_0843;
    this["dataset"]["selected"]=id_0844?"true":"false",
    this["refreshProgressText"](id_0842, id_0844);
    const id_0845=this["querySelector"](".cart-drawer-gift__image__locked");
    id_0845&&id_0845["classList"]["toggle"]("hidden", id_0844)
  }
  ["getUpdateRequired"]()
  {
    return this["refreshUnlockState"](),
    super["getUpdateRequired"]()
  }
  ["storeLocalStorageData"]()
  {
    !this["dataset"]["handle"]||!this.dataset.id||!this["dataset"]["mainProduct"]||!this["dataset"]["requirement"]||!this["dataset"]["type"]||updateGiftGoalInStorage({
      giftHandle:this["dataset"]["handle"], giftVariantId:this.dataset.id, unlockType:this["dataset"]["type"], unlockRequirement:Number(this.dataset["requirement"]), unlockTarget:this["dataset"].mainProduct
    })
  }
}
customElements["define"]("cart-drawer-gift", CartDrawerGift);
function initToggleUpsells()
{
  const id_0833=document["querySelector"]("cart-drawer");
  if(window["Shopify"]&&window["Shopify"]["designMode"])return;
  id_0833&&id_0833["querySelectorAll"]("cart-drawer-upsell[data-toggle=\"true\"], cart-drawer-gift")["forEach"](id_0834=>{
    id_0834["addRemoveFromCart"]&&id_0834["addRemoveFromCart"]()
  })
}
initToggleUpsells();
class MainBundleOffer extends HTMLElement
{
  constructor()
  {
    super(),
    this.offerItems=this["querySelectorAll"](".main-offer-item"),
    this.updatePrices=this.dataset["updatePrices"]==="true",
    this["updatePrices"]&&(this["priceSpan"]=this["querySelector"](".bundle-deals__total-price-js"), this.comparePriceSpan=this["querySelector"](".bundle-deals__total-compare-price-js"), this["percentageLeft"]=parseFloat(this.dataset["percentageLeft"]), this.fixedDiscount=parseFloat(this["dataset"]["fixedDiscount"]), this.moneyFormat=this["dataset"]["moneyFormat"])
  }
  ["updateTotalPrices"]()
  {
    if(this.updatePrices)
    {
      var id_0835=0,
      id_0836=0;
      for(let id_0837=0; id_0837<this.offerItems["length"]; id_0837++)id_0835+=parseInt(this["offerItems"][id_0837]["price"]),
      id_0836+=parseInt(this["offerItems"][id_0837].comparePrice);
      id_0835=id_0835*this.percentageLeft-this["fixedDiscount"],
      displayPrices(id_0835, id_0836, this.priceSpan, this.comparePriceSpan, this["moneyFormat"])
    }
  }
}
customElements["define"]("main-bundle-offer", MainBundleOffer);
class CustomProductField extends HTMLElement
{
  constructor()
  {
    super()
  }
  connectedCallback()
  {
    this["fieldName"]=this["dataset"]["name"],
    this.input=this["querySelector"]('[type="text"], [type="number"], textarea'),
    this["inputRadios"]=this.querySelectorAll("[type=\"radio\"]"),
    this["select"]=this["querySelector"](".select__select"),
    this["productForm"]=document["getElementById"]("product-form-"+this["dataset"]["section"]),
    this["prevValue"]=this["dataset"]["defaultValue"],
    this["isRequired"]=this["dataset"]["required"]==="true",
    this["isText"]=!0,
    this["notMain"]=this["dataset"]["notMain"]==="true",
    (this.dataset.type==="select"||this.dataset["type"]==="pills")&&(this["isText"]=!1),
    this["createInputs"](),
    this["isRequired"]&&this["isText"]&&(this.isValid=!0, this["atcButtons"]=this["notMain"]?document.querySelectorAll(".featured-product-atc-"+this["dataset"]["section"]):document["querySelectorAll"](".main-product-atc"), this["mainAtcButton"]=this["productForm"]["querySelector"]("#ProductSubmitButton-"+this["dataset"]["section"]), this.mainAtcBtnLabel=this.mainAtcButton["querySelector"](".main-atc__label"), this["mainAtcBtnError"]=this.mainAtcButton["querySelector"](".main-atc__error"), this.atcErrorMsg=this.dataset["atcErrorMsg"], this["mainAtcButton"].dataset["requiredFields"]=parseInt(this["mainAtcButton"].dataset["requiredFields"])+1, console.log(this["mainAtcBtnError"]), this["mainAtcBtnError"]&&(this["mainAtcBtnError"]["innerHTML"]=this.atcErrorMsg), this["applyStickyAtcError"]=this["dataset"]["applyStickyAtcError"]==="true", this["stickyAtcButton"]=document["querySelector"]("#sticky-atc-"+this["dataset"]["section"]), this["applyStickyAtcError"]&&this.stickyAtcButton&&(this.stickyAtcBtnLabel=this.stickyAtcButton.querySelector(".sticky-atc__label"), this["stickyAtcBtnError"]=this["stickyAtcButton"].querySelector(".sticky-atc__error"), console.log(this["stickyAtcBtnError"]), this["stickyAtcBtnError"]&&(this.stickyAtcBtnError["innerHTML"]=this["atcErrorMsg"])), this["validateValue"](this.prevValue, null)),
    this["input"]&&this["input"].addEventListener("input", this["handleChange"]["bind"](this)),
    this["inputRadios"]["forEach"](id_0838=>{
      id_0838["addEventListener"]("input", this["handleChange"]["bind"](this))
    }),
    this["select"]&&this["select"]["addEventListener"]("change", this["handleChange"].bind(this))
  }
  handleChange(id_0839)
  {
    const id_0840=id_0839["target"]["value"]["trim"]();
    if(id_0839["target"].checkValidity())this["prevValue"]=id_0840;
    else{
      id_0839.target.value=this["prevValue"];
      return
    }
    this["dataset"]["value"]=id_0840,
    this.productFormInput["value"]=id_0840,
    this["isRequired"]&&this["isText"]&&this["validateValue"](id_0840, id_0839["target"])
  }
  validateValue(id_0841, id_0842)
  {
    const id_0843=id_0841["length"]>0;
    if(id_0843===this["isValid"])return;
    this["isValid"]=id_0843,
    id_0842&&(this["isValid"]?(id_0842["classList"]["remove"]("input--error"), this["mainAtcButton"]["dataset"].validFields=parseInt(this["mainAtcButton"]["dataset"]["validFields"])+1):(id_0842["classList"].add("input--error"), this["mainAtcButton"].dataset.validFields=parseInt(this["mainAtcButton"].dataset["validFields"])-1));
    const id_0844=this["mainAtcButton"]["dataset"]["validFields"]===this["mainAtcButton"].dataset["requiredFields"],
    id_0845=this["mainAtcButton"]["dataset"].unavailable==="true";
    this["atcButtons"].forEach(id_0846=>{
      id_0844&&!id_0845?id_0846["removeAttribute"]("disabled"):id_0846.setAttribute("disabled", "")
    }),
    this.atcErrorMsg.length!==0&&(id_0844?(this["mainAtcBtnLabel"]["style"]["display"]="", this["mainAtcBtnError"]["style"].display="none", this.applyStickyAtcError&&this["stickyAtcButton"]&&(this["stickyAtcBtnLabel"].style["display"]="", this["stickyAtcBtnError"]["style"]["display"]="none")):(this["mainAtcBtnLabel"].style.display="none", this["mainAtcBtnError"]["style"]["display"]="", this["applyStickyAtcError"]&&this["stickyAtcButton"]&&(this["stickyAtcBtnLabel"].style["display"]="none", this["stickyAtcBtnError"]["style"]["display"]="")))
  }
  ["createInputs"]()
  {
    this["productFormInput"]=document["createElement"]("input"),
    this["productFormInput"]["setAttribute"]("type", "hidden"),
    this["productFormInput"]["setAttribute"]("name", "properties["+this["fieldName"]+"]"),
    this.productFormInput["value"]=this.dataset["defaultValue"],
    this.productFormInput["classList"]["add"]("exa-property-input"),
    this["productForm"]["appendChild"](this.productFormInput)
  }
}
customElements["define"]("custom-product-field", CustomProductField);
function playMedia()
{
  return
}
class VariantSelects extends HTMLElement
{
  constructor()
  {
    super(),
    this.secondarySelectSelector="StickyAtcVariantPicker-",
    this["secondarySelect"]=document["getElementById"](""+this.secondarySelectSelector+this["dataset"]["section"]),
    this["isSecondary"]=!1,
    this.QuantityBreaks=document["getElementById"]("quantity-breaks-"+this["dataset"]["section"]),
    this["hasQuantityBreaksPicker"]=this["dataset"]["hasQuantityBreaksPicker"]==="true",
    this["prependMedia"]=this["dataset"].disablePrepend!="true",
    this["filtering"]=this["dataset"]["hasFiltering"]==="true",
    this["skipNonExistent"]=this["dataset"]["skipNonExistent"]==="true",
    this["skipUnavailable"]=this["dataset"]["skipUnavailable"]==="true",
    this["hasQuantityBreaksPicker"]&&(this["quantityBreaksPickerStyle"]=this.dataset["quantityBreaksPickerStyle"], this["quantityBreaksPickerDisplayedImages"]=this["dataset"]["quantityBreaksPickerDisplayedImages"]),
    this["addEventListener"]("change", this.onVariantChange)
  }
  onVariantChange()
  {
    if(this["updateOptions"](), this["updateMasterId"](), this.toggleAddButton(!0, "", !1), this["updatePickupAvailability"](), this["removeErrorMessage"](), this["updateVariantStatuses"](), this["currentVariant"])
    {
      if(this["skipUnavailable"]&&!this["currentVariant"]["available"]&&findAvailableVariant(this, this["options"], !0, !0, !0))return;
      this["updateMedia"](),
      this.updateURL(),
      this.updateVariantInput(),
      this["renderProductInfo"](),
      this["updateShareUrl"]()
    }
    else{
      if(this["toggleAddButton"](!0, "", !0), this["skipNonExistent"]&&findAvailableVariant(this, this.options, !0, !0, this["skipUnavailable"]))return;
      this["setUnavailable"]()
    }
  }
  ["updateOptions"]()
  {
    const id_0847=[];
    this.querySelectorAll(".product-form__input")["forEach"](id_0848=>{
      let id_0849; const id_0850=id_0848["querySelector"](".product-form__input__type")["dataset"].type; id_0850=="dropdown"||id_0850=="dropdwon"?id_0849=id_0848["querySelector"](".select__select").value:id_0849=id_0848["querySelector"]("input[type=\"radio\"]:checked")["value"], id_0847["push"](id_0849)
    }),
    this["options"]=id_0847
  }
  ["updateMasterId"]()
  {
    this["currentVariant"]=this["getVariantData"]()["find"](id_0851=>{
      return!id_0851["options"]["map"]((id_0852, id_0853)=>this["options"][id_0853]===id_0852)["includes"](!1)
    })
  }
  updateMedia()
  {
    if(!this["currentVariant"]||!this.currentVariant["featured_media"])return;
    document["querySelectorAll"]("[id^=\"MediaGallery-"+this["dataset"].section+'"]').forEach(id_0854=>id_0854["setActiveMedia"](this["dataset"]["section"]+"-"+this["currentVariant"]["featured_media"].id, this.prependMedia, this["filtering"], this["currentVariant"]));
    const id_0855=document["querySelector"]("#ProductModal-"+this["dataset"]["section"]+" .product-media-modal__content");
    if(!id_0855)return;
    const id_0856=id_0855["querySelector"]("[data-media-id=\""+this["currentVariant"]["featured_media"].id+'"]');
    id_0855["prepend"](id_0856)
  }
  ["updateURL"]()
  {
    !this["currentVariant"]||this["dataset"]["updateUrl"]==="false"||window.history["replaceState"]({
    }, "", this.dataset["url"]+"?variant="+this["currentVariant"].id)
  }
  ["updateShareUrl"]()
  {
    const id_0857=document.getElementById("Share-"+this.dataset.section);
    !id_0857||!id_0857["updateUrl"]||id_0857["updateUrl"](""+window["shopUrl"]+this["dataset"]["url"]+"?variant="+this["currentVariant"].id)
  }
  ["updateVariantInput"]()
  {
    document["querySelectorAll"]("#product-form-"+this.dataset["section"]+", #product-form-installment-"+this["dataset"]["section"])["forEach"](id_0858=>{
      const id_0859=id_0858["querySelector"]('input[name="id"]'); id_0859.value=this.currentVariant.id, id_0859["dispatchEvent"](new Event("change", {
        bubbles:!0
      }))
    })
  }
  ["updateVariantStatuses"]()
  {
    const id_0860=this["variantData"]["filter"](id_0861=>this["querySelector"](":checked")["value"]===id_0861["option1"]);
    let id_0862;
    this.isSecondary&&this.secondarySelect?id_0862=[...this.secondarySelect.querySelectorAll(".product-form__input")]:id_0862=[...this["querySelectorAll"](".product-form__input")],
    id_0862["forEach"]((id_0863, id_0864)=>{
      if(id_0864===0)return; const id_0865=[...id_0863["querySelectorAll"]("input[type=\"radio\"], .select__select option")], id_0866=id_0862[id_0864-1]["querySelector"](":checked")["value"], id_0867=id_0860.filter(id_0868=>id_0868["available"]&&id_0868["option"+id_0864]===id_0866)["map"](id_0869=>id_0869["option"+(id_0864+1)]), id_0870=id_0860["filter"](id_0871=>id_0871["option"+id_0864]===id_0866)["map"](id_0872=>id_0872["option"+(id_0864+1)]); this["setInputAvailability"](id_0865, id_0867, id_0870, this["skipNonExistent"], this.skipUnavailable)
    })
  }
  ["setInputAvailability"](id_0873, id_0874, id_0875, id_0876=!1, id_0877=!1)
  {
    id_0873.forEach(id_0878=>{
      id_0878["classList"]["remove"]("unavailable", "non-existent", "disabled"), id_0878["removeAttribute"]("disabled"), id_0878.removeAttribute("hidden"); const id_0879=id_0878["getAttribute"]("value"); let id_0880=null; id_0878["dataset"]["swatch"]==="true"&&(id_0880=id_0878["closest"](".color-swatch")), id_0880&&id_0880["classList"].remove("unavailable", "non-existent", "disabled"), id_0875.includes(id_0879)?id_0874["includes"](id_0879)?id_0878["nodeName"]==="OPTION"?id_0878["innerText"]=id_0879:(id_0878["classList"].remove("disabled"), id_0880&&id_0880["classList"]["remove"]("disabled")):id_0878["nodeName"]==="OPTION"?(id_0878["innerText"]=window["variantStrings"]["unavailable_with_option"].replace("[value]", id_0879), id_0878["classList"].add("unavailable"), id_0877&&(id_0878["setAttribute"]("disabled", ""), id_0878["setAttribute"]("hidden", ""))):(id_0878["classList"].add("disabled", "unavailable"), id_0880&&id_0880["classList"]["add"]("disabled", "unavailable")):id_0878["nodeName"]==="OPTION"?(id_0878["innerText"]=window["variantStrings"]["unavailable_with_option"]["replace"]("[value]", id_0879), id_0878["classList"]["add"]("non-existent"), id_0876&&(id_0878["setAttribute"]("disabled", ""), id_0878["setAttribute"]("hidden", ""))):(id_0878["classList"]["add"]("disabled", "non-existent"), id_0880&&id_0880.classList["add"]("disabled", "non-existent"))
    })
  }
  updatePickupAvailability()
  {
    const id_0881=document.querySelector("pickup-availability");
    id_0881&&(this["currentVariant"]&&this["currentVariant"]["available"]?id_0881["fetchAvailability"]&&id_0881["fetchAvailability"](this.currentVariant.id):(id_0881.removeAttribute("available"), id_0881["innerHTML"]=""))
  }
  ["removeErrorMessage"]()
  {
    const id_0882=this["closest"]("section");
    if(!id_0882)return;
    const id_0883=id_0882.querySelector("product-form");
    id_0883&&id_0883.handleErrorMessage()
  }
  renderProductInfo()
  {
    const id_0884=this["currentVariant"].id,
    id_0885=this["dataset"]["originalSection"]?this["dataset"]["originalSection"]:this["dataset"]["section"];
    fetch(this["dataset"]["url"]+"?variant="+id_0884+"&section_id="+(this["dataset"]["originalSection"]?this["dataset"].originalSection:this.dataset["section"]))["then"](id_0886=>id_0886.text())["then"](id_0887=>{
      if(this.currentVariant.id!==id_0884)return; const id_0888=new DOMParser().parseFromString(id_0887, "text/html"), id_0889=document.getElementById("price-"+this["dataset"]["section"]), id_0890=id_0888.getElementById("price-"+(this["dataset"].originalSection?this.dataset["originalSection"]:this.dataset["section"])), id_0891=document["getElementById"]("sticky-atc-separate-price-"+this["dataset"].section), id_0892=id_0888["getElementById"]("sticky-atc-separate-price-"+(this["dataset"]["originalSection"]?this["dataset"].originalSection:this["dataset"]["section"])), id_0893=document["getElementById"]("sticky-atc-price-"+this.dataset["section"]), id_0894=id_0888["getElementById"]("sticky-atc-price-"+(this.dataset["originalSection"]?this["dataset"].originalSection:this["dataset"]["section"])), id_0895=document["getElementById"]("sticky-atc-image-"+this.dataset["section"]); id_0895&&this.currentVariant["featured_image"]&&(id_0895["src"]=this.currentVariant["featured_image"].src); const id_0896=document["getElementById"]("main-atc-price-"+this["dataset"]["section"]), id_0897=id_0888["getElementById"]("main-atc-price-"+(this["dataset"]["originalSection"]?this["dataset"]["originalSection"]:this.dataset["section"])), id_0898=document["querySelectorAll"]('[id^="custom-label-'+this["dataset"].section+'"]'), id_0899=id_0888.querySelectorAll("[id^=\"custom-label-"+(this["dataset"]["originalSection"]?this["dataset"].originalSection:this["dataset"]["section"])+'"]'), id_0900=id_0888["getElementById"]("Sku-"+(this.dataset.originalSection?this.dataset["originalSection"]:this["dataset"].section)), id_0901=document["getElementById"]("Sku-"+this["dataset"]["section"]), id_0902=id_0888["getElementById"]("Inventory-"+(this["dataset"]["originalSection"]?this["dataset"].originalSection:this["dataset"].section)), id_0903=document["getElementById"]("Inventory-"+this["dataset"]["section"]); if(id_0889&&id_0890&&(id_0889.innerHTML=id_0890.innerHTML), id_0891&&id_0892&&(id_0891.innerHTML=id_0892["innerHTML"]), id_0893&&id_0894&&(id_0893["innerHTML"]=id_0894["innerHTML"]), id_0897&&id_0896&&(id_0896["innerHTML"]=id_0897["innerHTML"]), id_0898&&id_0899)for(var id_0904=0; id_0904<id_0898["length"]; id_0904++)id_0898[id_0904]["innerHTML"]=id_0899[id_0904]["innerHTML"]; if(id_0902&&id_0903&&(id_0903["innerHTML"]=id_0902["innerHTML"]), id_0900&&id_0901&&(id_0901["innerHTML"]=id_0900["innerHTML"], id_0901["classList"].toggle("visibility-hidden", id_0900["classList"]["contains"]("visibility-hidden"))), this["QuantityBreaks"])
      {
        const id_0905=id_0888.getElementById("quantity-breaks-"+(this["dataset"].originalSection?this["dataset"]["originalSection"]:this["dataset"]["section"])), id_0906=this.QuantityBreaks.querySelectorAll(".dynamic-price"), id_0907=id_0905["querySelectorAll"](".dynamic-price"); for(let id_0908=0; id_0908<id_0906["length"]; id_0908++)id_0906[id_0908]["innerHTML"]=id_0907[id_0908]["innerHTML"]; if(this["QuantityBreaks"]["hasVariants"])
        {
          if(typeof this["QuantityBreaks"]["syncCurrentVariant"]==="function")this["QuantityBreaks"]["syncCurrentVariant"](this["currentVariant"]); else{
            this["QuantityBreaks"].variantSelects.forEach(id_0909=>{
              id_0909["dataset"]["selectedId"]=this["currentVariant"].id
            }); const id_0910=this["QuantityBreaks"]["querySelectorAll"](".quantity-break__variant-select"), id_0911=id_0905["querySelectorAll"](".quantity-break__variant-select"); for(let id_0912=0; id_0912<id_0910["length"]; id_0912++)id_0911[id_0912]&&(id_0910[id_0912]["innerHTML"]=id_0911[id_0912]["innerHTML"]); this["QuantityBreaks"]["initVariants"]()
          }
        }
        this.QuantityBreaks["querySelectorAll"](".quantity-break__selector-item__image img")["forEach"](id_0913=>{
          id_0913["src"]=this.currentVariant.featured_image.src
        }), this["QuantityBreaks"]["updatePdpPrices"]&&(this["QuantityBreaks"]["dataset"]["basePrice"]=id_0905["dataset"].basePrice, this["QuantityBreaks"]["labels"]["forEach"](id_0914=>{
          id_0914["dataset"]["baseComparePrice"]=id_0905["querySelector"]("label[data-input=\""+id_0914.dataset["input"]+'"]')["dataset"]["baseComparePrice"]
        }), this.QuantityBreaks["initOfferPrices"](!0), this["QuantityBreaks"].updateInnerPrices())
      }
      const id_0915=document["getElementById"]("SubscriptionWidget-"+this.dataset["section"]), id_0916=id_0888["getElementById"]("SubscriptionWidget-"+(this.dataset["originalSection"]?this.dataset["originalSection"]:this["dataset"]["section"])); if(id_0915)if(!id_0916)id_0915["classList"].add("hidden"); else if(id_0915["dataset"]["basePrice"]=id_0916.dataset["basePrice"]||"", id_0915["dataset"]["baseComparePrice"]=id_0916.dataset["baseComparePrice"]||"", (id_0915["dataset"]["firstSellingPlanId"]||"")!==(id_0916["dataset"]["firstSellingPlanId"]||"")&&typeof id_0915["hydrateFromExternalHTML"]==="function")id_0915["hydrateFromExternalHTML"](id_0916); else{
        const id_0917=id_0915.querySelectorAll(".one-time-price-update"), id_0918=id_0916["querySelectorAll"](".one-time-price-update"); for(let id_0919=0; id_0919<id_0917["length"]; id_0919++)id_0917[id_0919]["innerHTML"]=id_0918[id_0919].innerHTML; id_0915.updatePrices&&id_0915.updateAllPrices()
      }
      if(this.hasQuantityBreaksPicker)
      {
        const id_0920=id_0888["getElementById"]("variant-selects-"+(this["dataset"]["originalSection"]?this["dataset"]["originalSection"]:this["dataset"]["section"])), id_0921=this["querySelectorAll"](".dynamic-price"), id_0922=id_0920.querySelectorAll(".dynamic-price"); for(let id_0923=0; id_0923<id_0921["length"]; id_0923++)id_0921[id_0923]["innerHTML"]=id_0922[id_0923].innerHTML; if(this["quantityBreaksPickerDisplayedImages"]==="variant_images")
        {
          const id_0924=this["querySelectorAll"](".quantity-break__image img"), id_0925=id_0920["querySelectorAll"](".quantity-break__image img"); for(let id_0926=0; id_0926<id_0924["length"]; id_0926++)id_0925[id_0926]&&(id_0924[id_0926]["src"]=id_0925[id_0926]["src"])
        }
      }
      if(this["secondarySelect"])
      {
        const id_0927=id_0888["getElementById"](""+this.secondarySelectSelector+(this.dataset["originalSection"]?this.dataset.originalSection:this["dataset"]["section"])); id_0927&&(this["secondarySelect"].innerHTML=id_0927.innerHTML)
      }
      const id_0928=document["getElementById"]("price-"+this["dataset"]["section"]); id_0928&&id_0928["classList"].remove("visibility-hidden"), this["dataset"]["options"]=this.currentVariant.options["join"](","), id_0903&&id_0903["classList"]["toggle"]("visibility-hidden", id_0902["innerText"]===""); const id_0929=id_0888.getElementById("ProductSubmitButton-"+id_0885); this["toggleAddButton"](id_0929?id_0929["hasAttribute"]("disabled"):!0, window["variantStrings"]["soldOut"]), publish(PUB_SUB_EVENTS["variantChange"], {
        data:{
          sectionId:id_0885, html:id_0888, variant:this["currentVariant"]
        }
      })
    })
  }
  ["toggleAddButton"](id_0930=!0, id_0931, id_0932=!0)
  {
    const id_0933=document["getElementById"]("product-form-"+this["dataset"]["section"]);
    if(!id_0933)return;
    const id_0934=id_0933.querySelector("[name=\"add\"]");
    if(!id_0934)return;
    const id_0935=document.querySelectorAll(".main-product-atc");
    var id_0936=id_0933["querySelector"](".main-atc__label__text");
    id_0936||(id_0936=id_0933["querySelector"](".main-atc__label"));
    const id_0937=id_0933["querySelector"](".main-atc-price"),
    id_0938=document["querySelector"](".sticky-atc__add_button");
    let id_0939=null,
    id_0940=null;
    if(id_0938&&(id_0939=id_0938["querySelector"](".sticky-atc__label__text"), id_0940=id_0938.querySelector(".sticky-atc__button-price")), id_0930)id_0934["setAttribute"]("disabled", "disabled"),
    id_0934["setAttribute"]("data-unavailable", "true"),
    id_0931&&id_0936&&(id_0936["textContent"]=id_0931),
    id_0939&&id_0938.dataset.label&&(id_0939["textContent"]=id_0931),
    id_0935.forEach(id_0941=>{
      id_0941.setAttribute("disabled", "disabled")
    }),
    id_0937&&id_0937["classList"].add("hidden"),
    id_0940&&id_0940["classList"]["add"]("hidden");
    else{
      id_0934.setAttribute("data-unavailable", "false");
      const id_0942=id_0934["dataset"]["customLabel"]??window["variantStrings"]["addToCart"];
      id_0936&&(id_0936.textContent=id_0942),
      id_0937&&id_0937["classList"]["remove"]("hidden"),
      id_0939&&id_0938["dataset"]["label"]&&(id_0939["textContent"]=id_0938["dataset"]["label"]),
      id_0940&&id_0940["classList"]["remove"]("hidden"),
      id_0934.dataset["requiredFields"]===id_0934.dataset["validFields"]&&(id_0934["removeAttribute"]("disabled"), id_0935["forEach"](id_0943=>{
        id_0943.removeAttribute("disabled")
      }))
    }
  }
  ["setUnavailable"]()
  {
    const id_0944=document["getElementById"]("product-form-"+this["dataset"]["section"]);
    if(!id_0944)return;
    const id_0945=id_0944.querySelector("[name=\"add\"]");
    var id_0946=id_0944["querySelector"](".main-atc__label__text");
    id_0946||(id_0946=id_0944["querySelector"](".main-atc__label"));
    const id_0947=document.getElementById("price-"+this["dataset"]["section"]),
    id_0948=document["getElementById"]("Inventory-"+this["dataset"]["section"]),
    id_0949=document["getElementById"]("Sku-"+this.dataset.section);
    id_0945&&(id_0946&&(id_0946["textContent"]=window.variantStrings.unavailable), id_0947&&id_0947["classList"]["add"]("visibility-hidden"), id_0948&&id_0948["classList"].add("visibility-hidden"), id_0949&&id_0949["classList"]["add"]("visibility-hidden"))
  }
  ["getVariantData"]()
  {
    return this["variantData"]=this.variantData||JSON["parse"](this["querySelector"]("[type=\"application/json\"]")["textContent"]),
    this["variantData"]
  }
}
customElements["define"]("variant-selects", VariantSelects);
class SecondaryVariantSelect extends VariantSelects
{
  constructor()
  {
    super(),
    this["secondarySelectSelector"]="variant-selects-",
    this["secondarySelect"]=document["getElementById"](""+this["secondarySelectSelector"]+this["dataset"]["section"]),
    this["isSecondary"]=!0
  }
  ["updateOptions"]()
  {
    this["options"]=this.querySelector("select")["value"]["split"](",")
  }
}
customElements["define"]("secondary-variant-select", SecondaryVariantSelect);
class SecondaryVariantSelectSeparate extends VariantSelects
{
  constructor()
  {
    super(),
    this["secondarySelectSelector"]="variant-selects-",
    this["secondarySelect"]=document["getElementById"](""+this["secondarySelectSelector"]+this["dataset"]["section"]),
    this["isSecondary"]=!0
  }
}
customElements["define"]("secondary-variant-select-separate", SecondaryVariantSelectSeparate);
class CardVariantSelects extends VariantSelects
{
  constructor()
  {
    super(),
    this["quickAddSubmit"]=document.getElementById(this["dataset"]["section"]+"-submit")
  }
  ["onVariantChange"]()
  {
    this.updateOptions(),
    this["updateMasterId"](),
    this["toggleAddButton"](!0, "", !1),
    this["updateVariantStatuses"](),
    this["currentVariant"]?(this["updateMedia"](), this["updateVariantInput"](), this["updateQuickAddUrl"](), this.toggleAddButton(!this["currentVariant"]["available"], window.variantStrings.soldOut)):(this["toggleAddButton"](!0, "", !0), this.setUnavailable())
  }
  ["updateMedia"]()
  {
    if(!this["currentVariant"]||!this["currentVariant"]["featured_media"])return;
    const id_0951=document.getElementById(this["dataset"]["section"]+"-image");
    if(!id_0951)return;
    const id_0952=this.currentVariant["featured_media"].preview_image,
    id_0953=id_0952["src"]["split"]("?")[0],
    id_0954=[165,
    360,
    533,
    720,
    940,
    1066],
    id_0955=id_0954["filter"](id_0956=>id_0952["width"]>=id_0956)["map"](id_0957=>id_0953+"?width="+id_0957+" "+id_0957+"w")["join"](", ");
    id_0951["srcset"]=id_0955+", "+id_0953+" "+id_0952["width"]+"w",
    id_0951["src"]=id_0953+"?width=533"
  }
  updateQuickAddUrl()
  {
    !this["currentVariant"]||!this["quickAddSubmit"]||(this["quickAddSubmit"]["dataset"]["productUrl"]=this.dataset["url"]+"?variant="+this["currentVariant"].id)
  }
}
customElements["define"]("card-variant-selects", CardVariantSelects);
